import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserShield, faUsers, faUserTie, faChalkboardTeacher, faUserCheck, faEye, faEyeSlash, faBuilding, faSitemap, faBriefcase, faUserPlus, faUserEdit, faExclamationTriangle, faListUl, faInfoCircle, faCheckCircle, faFileUpload, faRulerCombined, faTruck, faDownload, faCopy, faCheck, faCalendarCheck, faLock, faArrowLeft, faHandHoldingDollar, faGlobe, faAnchor, faGavel, faMoneyBillWave, faMountain, faTint, faIdBadge, faCircleMinus, faFileExcel, faPlaneUp, faAddressBook, faFileInvoiceDollar, faChartColumn, faUserPen, faFileSignature } from '@fortawesome/free-solid-svg-icons';
import { AuthContext } from '../context/AuthContext';
import Layout from './Layout';
import './HMSAdminDashboard.css';
import './ui/HMSTabs.css';
import withAuth from '../utils/withAuth';
import hmsAdminService from '../services/hmsAdminService';
import fetchEveryPage from '../utils/fetchEveryPage';
import { ROLE_ASSIGNMENT_FORM_DEFAULTS } from '../constants/roleAssignmentFields';
import axiosInstance from '../utils/axiosConfig';
import { useFlashMessage } from './FlashMessage';
import { processEmployeeAvatars, getAbsoluteMediaUrl } from '../utils/mediaUtils';
import { getErrorMessage } from '../utils/errorMessages';
import { formatBirthday } from '../utils/dateUtils';
import AdminEmployeeLeaveModal from './AdminEmployeeLeaveModal';
import LeaveRequestViewModal from './LeaveRequestViewModal';
import HMSNotificationModal from './ui/HMSNotificationModal';
import SystemDataManagement from './SystemDataManagement';
import EmployeeCrmPartnerBlock from './hr/EmployeeCrmPartnerBlock';
import EmployeePowersOfAttorney from './hr/EmployeePowersOfAttorney';
import PowerOfAttorneyManagement from './hr/PowerOfAttorneyManagement';
import PowerOfAttorneyRolesPanel from './hr/PowerOfAttorneyRolesPanel';
import logo from '../assets/images/IMT_logo.png';
import Footer from './ui/Footer';
import HamsterFloatingButton from './HamsterFloatingButton';
import BackToTopButton from './BackToTopButton';

// Staff bulk upload: the office column's header, printed in the template and
// in the upload help list. Must be exactly the server's
// STAFF_UPLOAD_OFFICE_HEADER (backend/employees/admin_views.py), which also
// still accepts the older spellings so an old template keeps importing.
const STAFF_UPLOAD_OFFICE_HEADER = 'IMT Office (where you work)';

// Exportable employee fields — keys/order must match backend EXPORTABLE_EMPLOYEE_FIELDS
const EXPORTABLE_EMPLOYEE_FIELDS = [
  { key: 'hms_employee_id', label: 'Employee ID' },
  { key: 'first_name', label: 'First Name' },
  { key: 'last_name', label: 'Last Name' },
  { key: 'preferred_name', label: 'Preferred Name' },
  { key: 'title', label: 'Title' },
  { key: 'job_function', label: 'Function' },
  { key: 'email', label: 'Login Email' },
  { key: 'work_email', label: 'Work Email' },
  { key: 'work_phone', label: 'Work Phone' },
  { key: 'personal_phone', label: 'Personal Phone' },
  { key: 'office', label: 'Office' },
  { key: 'department', label: 'Department' },
  { key: 'position', label: 'Position' },
  { key: 'location', label: 'Location' },
  { key: 'languages_spoken', label: 'Languages' },
  { key: 'date_of_birth', label: 'Date of Birth' },
  { key: 'date_joined_hms', label: 'Date Joined' },
  { key: 'is_supervisor', label: 'Supervisor' },
  { key: 'is_head_of_department', label: 'Head of Department' },
  { key: 'is_active', label: 'Active' },
];

const DEFAULT_EXPORT_FIELDS = [
  'first_name', 'last_name', 'title', 'job_function', 'work_email',
  'work_phone', 'office', 'department', 'position'
];

/*
 * Role Assignment — the form's shape now lives in
 * constants/roleAssignmentFields.js, re-exported here so every existing import
 * of ROLE_ASSIGNMENT_FORM_DEFAULTS keeps working.
 *
 * It moved out of this file because the backend now REFUSES an assign-role body
 * carrying a key it does not recognise, so drift between the two lists no
 * longer loses one setting quietly — it rejects every save from this screen.
 * The list has to sit somewhere the backend's drift test can read it (the
 * frontend/src/constants read-only mount), which is the whole reason for the
 * indirection. See the long note at the top of that file.
 */
export { ROLE_ASSIGNMENT_FORM_DEFAULTS };

/*
 * Fill the form from the employee being edited, for DISPLAY. Every key in the
 * defaults is read off the employee record by the same name, so a new setting
 * is seeded automatically. supervised_department_id is the one exception: it
 * arrives nested, as supervised_department.id.
 *
 * A setting the employee record does not carry still has to render as
 * something, so it renders un-ticked. What must NOT happen is that guess being
 * written back to the server — see buildRoleAssignmentPayload.
 */
export const roleAssignmentFormFromEmployee = (employee = {}) => {
  const form = {};
  Object.keys(ROLE_ASSIGNMENT_FORM_DEFAULTS).forEach((key) => {
    form[key] = employee[key] ?? ROLE_ASSIGNMENT_FORM_DEFAULTS[key];
  });
  form.supervised_department_id = employee.supervised_department?.id ?? null;
  return form;
};

/*
 * Build the assign-role request body. The form's own contents go out whole —
 * there is no field list here to keep in step, which is the entire fix.
 *
 * ONE thing is held back: a setting the server never sent us that the
 * administrator also never touched. The browser bundle and the server deploy as
 * two separate images, so there is a window in which the newer screen knows
 * about a setting the older employee list does not return. The form shows that
 * setting un-ticked because it has to show something — and it used to send that
 * guess, which wrote the permission OFF. An admin opening Assign Role on
 * somebody who HOLDS Template approval, changing something unrelated and
 * saving, silently revoked it and got a green "saved".
 *
 * An absent key means "leave it alone" to the server, which is the only honest
 * thing to say about a setting this screen never had a real value for. The
 * moment the administrator actually clicks the box it stops being a guess and
 * goes out like everything else.
 */
export const buildRoleAssignmentPayload = (employeeId, newRole, form, employee = {}) => {
  const payload = { employee_id: employeeId, new_role: newRole };
  Object.keys(form || {}).forEach((key) => {
    const isKnownSetting = key in ROLE_ASSIGNMENT_FORM_DEFAULTS;
    const serverSentIt = employee?.[key] !== undefined && employee?.[key] !== null;
    const stillTheSeededGuess = form[key] === ROLE_ASSIGNMENT_FORM_DEFAULTS[key];
    if (isKnownSetting && !serverSentIt && stillTheSeededGuess) return;
    payload[key] = form[key];
  });
  return payload;
};

/*
 * Settings that need a SECOND setting to be worth anything.
 *
 * The Hedging Trader tick is a visibility grant and a mailing list, not a
 * module key. On its own it produces a person who is emailed a link to a screen
 * they are refused at — and nothing anywhere would say so, because from the
 * server's side both halves are working exactly as written. Ticking it beside
 * Customer Trader is the other way to build a role that does nothing: a
 * customer trader is never shown purchase pricing, so their hedging list is
 * empty by construction.
 *
 * Exported as a plain function on purpose. A warning rendered inline in a
 * 6,000-line component can only be tested by copying its condition into a test
 * harness, and a copied condition keeps passing while the screen drifts away
 * from it. This one is testable as itself.
 */
export const hedgingTraderWarnings = (form = {}) => {
  if (!form.is_hedging_trader) return [];
  const warnings = [];
  if (!form.has_bunkers_access && !form.has_full_access) {
    warnings.push('needs-a-module');
  }
  if (form.is_customer_trader) {
    warnings.push('customer-trader-conflict');
  }
  return warnings;
};

function HMSAdminDashboard() {
  const { user } = useContext(AuthContext);
  const history = useHistory();
  const { showSuccess, showError, showInfo } = useFlashMessage();
  
  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Current user avatar state
  const [currentUserAvatar, setCurrentUserAvatar] = useState(null);
  
  // Search
  const [searchQuery, setSearchQuery] = useState('');
  
  // Statistics
  const [stats, setStats] = useState({});
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAuditLogs, setShowAuditLogs] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [filteredAuditLogs, setFilteredAuditLogs] = useState([]);
  const [activeAuditFilter, setActiveAuditFilter] = useState('all');
  const [showEmployeeDetails, setShowEmployeeDetails] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [passwordResetEmployee, setPasswordResetEmployee] = useState(null);
  
  // Role assignment modal states
  const [showRoleAssignment, setShowRoleAssignment] = useState(false);
  const [roleAssignmentEmployee, setRoleAssignmentEmployee] = useState(null);
  const [selectedRole, setSelectedRole] = useState('');
  
  // Every setting the Role Assignment form holds — see
  // ROLE_ASSIGNMENT_FORM_DEFAULTS above. Nothing in this file lists the fields
  // again; the save sends whatever this object contains.
  const [roleAssignmentData, setRoleAssignmentData] = useState({ ...ROLE_ASSIGNMENT_FORM_DEFAULTS });
  
  // NEW: Reportee counts and unassignment warning modal states
  const [reporteeCounts, setReporteeCounts] = useState({
    supervisor_count: 0,
    hod_count: 0
  });
  const [showUnassignWarning, setShowUnassignWarning] = useState(false);
  const [unassignWarningData, setUnassignWarningData] = useState(null);
  const [pendingRoleSubmission, setPendingRoleSubmission] = useState(null);
  
  // NEW: Assign Users modal states (shared for both Supervisor and HOD)
  const [showAssignUsers, setShowAssignUsers] = useState(false);
  const [assignSupervisor, setAssignSupervisor] = useState(null);
  const [assignHOD, setAssignHOD] = useState(null);
  const [assignLeaveSupervisor, setAssignLeaveSupervisor] = useState(null);
  const [assignType, setAssignType] = useState(null); // 'supervisor', 'hod', or 'leave_supervisor'
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [assignUsersSearch, setAssignUsersSearch] = useState('');
  const [assignFilterDept, setAssignFilterDept] = useState(''); // '' = all departments
  const [assignFilterOffice, setAssignFilterOffice] = useState(''); // '' = all offices
  
  // Deactivate confirmation modal states
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [deactivateEmployee, setDeactivateEmployee] = useState(null);

  // Position verification states
  const [positionRequests, setPositionRequests] = useState([]);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [activeRequestsTab, setActiveRequestsTab] = useState('audit'); // 'audit', 'leave_actions', or 'admin_requests'
  const [activeAdminRequestsSubTab, setActiveAdminRequestsSubTab] = useState('position_requests'); // 'position_requests' or 'leave_requests'
  
  // Leave management states
  const [showViewLeaveModal, setShowViewLeaveModal] = useState(false);
  const [showLeaveRequestViewModal, setShowLeaveRequestViewModal] = useState(false);
  const [selectedLeaveRequestId, setSelectedLeaveRequestId] = useState(null);
  const [pendingLeaveRequestsCount, setPendingLeaveRequestsCount] = useState(0);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveRequestsLoading, setLeaveRequestsLoading] = useState(false);
  
  // Leave request notification modal states
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  const [selectedLeaveForModal, setSelectedLeaveForModal] = useState(null);
  
  // Leave audit logs states
  const [leaveAuditLogs, setLeaveAuditLogs] = useState([]);
  const [filteredLeaveAuditLogs, setFilteredLeaveAuditLogs] = useState([]);
  const [activeLeaveAuditFilter, setActiveLeaveAuditFilter] = useState('all');
  const [leaveAuditLogsLoading, setLeaveAuditLogsLoading] = useState(false);
  
  // System Data audit logs states
  const [systemDataLogs, setSystemDataLogs] = useState([]);
  const [filteredSystemDataLogs, setFilteredSystemDataLogs] = useState([]);
  const [activeSystemDataFilter, setActiveSystemDataFilter] = useState('all');
  
  // Action dropdown states
  const [activeDropdown, setActiveDropdown] = useState(null);
  
  // Separate superusers from regular employees
  const [superusers, setSuperusers] = useState([]);
  const [regularEmployees, setRegularEmployees] = useState([]);
  
  // NEW: Separate supervisors, HODs, CRM Approvers, Financial Approvers, and Delivery Reviewers
  const [supervisors, setSupervisors] = useState([]);
  const [headsOfDepartment, setHeadsOfDepartment] = useState([]);
  const [crmApprovers, setCrmApprovers] = useState([]);
  const [kycApprovers, setKycApprovers] = useState([]);
  const [creditApprovers, setCreditApprovers] = useState([]);
  const [financialApprovers, setFinancialApprovers] = useState([]);
  const [deliveryReviewers, setDeliveryReviewers] = useState([]);
  const [legalApprovers, setLegalApprovers] = useState([]);
  const [legalApproversDisplayLimit, setLegalApproversDisplayLimit] = useState(20);
  const [leaveSupervisors, setLeaveSupervisors] = useState([]);

  // Deactivated employees states
  const [deactivatedEmployees, setDeactivatedEmployees] = useState([]);
  const [deactivatedCount, setDeactivatedCount] = useState(0);
  // Row totals, so a truncated list can never again look like a complete one.
  const [auditTotal, setAuditTotal] = useState(0);
  const [leaveAuditTotal, setLeaveAuditTotal] = useState(0);
  const [showDeactivatedTab, setShowDeactivatedTab] = useState(false);

  // Search results state
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  // The text the last finished search ran for, so a search that finds nobody
  // says so instead of leaving no pop-up at all.
  const [searchedQuery, setSearchedQuery] = useState('');
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);

  // Extended search fields
  const [searchFilters, setSearchFilters] = useState({
    position: '',
    department: '',
    office: '',
    status: ''
  });

  // Debounced search
  const searchDebounceRef = useRef(null);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  // Admin Mode Toggle - Employee Management vs System Data
  const [adminMode, setAdminMode] = useState('employees'); // 'employees' | 'system-data' | 'power-of-attorney'
  const [systemDataTab, setSystemDataTab] = useState('offices'); // 'offices' | 'departments' | 'positions' | 'units' | 'branches' | 'ports' | 'power_of_attorney_roles'
  
  // Tab state for Admins/Users (only for Employee Management mode)
  const [activeTab, setActiveTab] = useState('users'); // 'admins' or 'users'
  
  // Pagination display states
  const [superusersDisplayLimit, setSuperusersDisplayLimit] = useState(20);
  const [regularEmployeesDisplayLimit, setRegularEmployeesDisplayLimit] = useState(20);
  const [supervisorsDisplayLimit, setSupervisorsDisplayLimit] = useState(20);  // NEW
  const [hodsDisplayLimit, setHodsDisplayLimit] = useState(20);  // NEW
  const [crmApproversDisplayLimit, setCrmApproversDisplayLimit] = useState(20);  // NEW
  const [kycApproversDisplayLimit, setKycApproversDisplayLimit] = useState(20);  // NEW
  const [creditApproversDisplayLimit, setCreditApproversDisplayLimit] = useState(20);  // NEW
  const [financialApproversDisplayLimit, setFinancialApproversDisplayLimit] = useState(20);  // NEW
  const [deliveryReviewersDisplayLimit, setDeliveryReviewersDisplayLimit] = useState(20);  // NEW
  const [leaveSupervisorsDisplayLimit, setLeaveSupervisorsDisplayLimit] = useState(20);  // NEW

  // Form data for creating employee
  const [newEmployee, setNewEmployee] = useState({
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    confirmPassword: '',
    is_superuser: false,
    is_supervisor: false,              // NEW
    is_head_of_department: false,      // NEW
    is_crm_approver: false,            // NEW
    is_kyc_approver: false,            // NEW
    is_credit_approver: false,         // NEW
    is_financial_approver: false,      // NEW
    is_delivery_reviewer: false,       // NEW
    is_leave_supervisor: false,        // NEW
    leave_supervisor_view_only: true,  // NEW
    supervised_department_id: null,    // NEW
    send_invite_email: true            // NEW: email H40 link + login details to new employee
  });

  // Whether the bulk-upload flow should email each new employee their credentials
  const [bulkSendInviteEmail, setBulkSendInviteEmail] = useState(true);
  
  // Departments and offices lists for dropdowns
  const [departments, setDepartments] = useState([]);
  const [offices, setOffices] = useState([]);

  // Bulk upload states
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showBulkUploadResults, setShowBulkUploadResults] = useState(false);
  const [bulkUploadResults, setBulkUploadResults] = useState(null);

  // Export to Excel states
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFields, setExportFields] = useState(DEFAULT_EXPORT_FIELDS);
  const [exportIncludeInactive, setExportIncludeInactive] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Password validation states
  const [createPasswordStrength, setCreatePasswordStrength] = useState({
    valid: false,
    length: false,
    hasNumber: false,
    hasSpecial: false,
    hasUpper: false,
    hasLower: false
  });

  const [resetPasswordData, setResetPasswordData] = useState({
    password: '',
    confirmPassword: ''
  });

  const [resetPasswordStrength, setResetPasswordStrength] = useState({
    valid: false,
    length: false,
    hasNumber: false,
    hasSpecial: false,
    hasUpper: false,
    hasLower: false
  });

  // Password visibility toggle states
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [showCreateConfirmPassword, setShowCreateConfirmPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showResetConfirmPassword, setShowResetConfirmPassword] = useState(false);

  // Check if user is superuser
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);


  // Audit filter definitions - Employee Actions Only
  const auditFilterOptions = [
    { value: 'all', label: 'All Actions', icon: '•' },
    { value: 'create', label: 'Created', icon: '+' },
    { value: 'update', label: 'Updated', icon: '~' },
    { value: 'deactivate', label: 'Deactivated', icon: '×' },
    { value: 'activate', label: 'Activated', icon: '✓' },
    { value: 'password_reset', label: 'Password Reset', icon: '*' },
    { value: 'role_change', label: 'Admin Role Change', icon: '◐' },
    { value: 'supervisor_hod_crm', label: 'Supervisor/HOD/CRM Approver Change', icon: '★' }
  ];

  // System Data filter definitions
  const systemDataFilterOptions = [
    { value: 'all', label: 'All Actions', icon: '•' },
    { value: 'system_data_create', label: 'System Data Created', icon: '+' },
    { value: 'system_data_update', label: 'System Data Updated', icon: '~' },
    { value: 'system_data_delete', label: 'System Data Deleted', icon: '×' }
  ];

  // Leave audit subtab definitions - Admin actions only
  const leaveAuditSubTabs = [
    { value: 'all', label: 'All Admin Actions', icon: '•' },
    { value: 'request_approved', label: 'Approved', icon: '✓' },
    { value: 'request_rejected', label: 'Rejected', icon: '✗' }
  ];

  // Filter audit logs based on active filter
  const filterAuditLogs = (logs, filter) => {
    if (filter === 'all') {
      return logs;
    }
    
    // Handle both old format (create, role_change) and new format (CREATED EMPLOYEE, ROLE_CHANGE)
    const filterMappings = {
      'create': ['create', 'CREATED EMPLOYEE'],
      'update': ['update', 'UPDATED EMPLOYEE'],
      'deactivate': ['deactivate', 'DEACTIVATED EMPLOYEE'],
      'activate': ['activate', 'ACTIVATED EMPLOYEE'],
      'password_reset': ['password_reset', 'PASSWORD_RESET'],
      'role_change': ['role_change', 'ROLE_CHANGE'],
      'supervisor_hod_crm': ['supervisor_hod', 'SUPERVISOR_HOD', 'supervisor_hod_crm', 'SUPERVISOR_HOD_CRM']
    };
    
    const validActions = filterMappings[filter] || [filter];
    return logs.filter(log => validActions.includes(log.action));
  };

  // Handle audit filter change - Employee actions only
  const handleAuditFilterChange = (filterValue) => {
    setActiveAuditFilter(filterValue);
    const filtered = filterAuditLogs(auditLogs, filterValue);
    setFilteredAuditLogs(filtered);
  };

  // Filter leave audit logs based on active filter
  const filterLeaveAuditLogs = (logs, filter) => {
    if (filter === 'all') {
      return logs;
    }
    return logs.filter(log => log.action === filter);
  };

  // Handle leave audit filter change
  const handleLeaveAuditFilterChange = (filterValue) => {
    setActiveLeaveAuditFilter(filterValue);
    const filtered = filterLeaveAuditLogs(leaveAuditLogs, filterValue);
    setFilteredLeaveAuditLogs(filtered);
  };

  // Filter system data logs based on active filter
  const filterSystemDataLogs = (logs, filter) => {
    if (filter === 'all') {
      return logs;
    }
    return logs.filter(log => log.action === filter);
  };

  // Handle system data filter change
  const handleSystemDataFilterChange = (filterValue) => {
    setActiveSystemDataFilter(filterValue);
    const filtered = filterSystemDataLogs(systemDataLogs, filterValue);
    setFilteredSystemDataLogs(filtered);
  };

  // Format action labels consistently (remove underscores, proper case)
  const formatActionLabel = (action, actionDisplay) => {
    // Define clean display names for all actions
    const actionLabels = {
      'create': 'Created Employee',
      'update': 'Updated Employee', 
      'deactivate': 'Deactivated Employee',
      'activate': 'Activated Employee',
      'password_reset': 'Password Reset',
      'role_change': 'Admin Role Change',
      'supervisor_hod': 'Supervisor/HOD/CRM Approver Change',
      'system_data_create': 'System Data Created',
      'system_data_update': 'System Data Updated',
      'system_data_delete': 'System Data Deleted',
      'CREATED EMPLOYEE': 'Created Employee',
      'ROLE_CHANGE': 'Admin Role Change',
      'SUPERVISOR_HOD': 'Supervisor/HOD/CRM Approver Change',
      'PASSWORD_RESET': 'Password Reset'
    };
    
    // Use predefined label if available
    if (actionLabels[action]) {
      return actionLabels[action];
    }
    
    // Use backend display if available
    if (actionDisplay && actionDisplay !== action) {
      return actionDisplay;
    }
    
    // Fallback: format action by removing underscores and capitalizing
    return action
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  // Format leave action details for display
  const formatLeaveActionDetails = (log) => {
    const details = log.details || {};
    const parts = [];

    // Add leave type and days
    if (log.leave_request) {
      parts.push(`${log.leave_request.leave_type_name}: ${log.leave_request.total_days} days`);
      
      // Add dates
      if (log.leave_request.start_date && log.leave_request.end_date) {
        const startDate = new Date(log.leave_request.start_date).toLocaleDateString();
        const endDate = new Date(log.leave_request.end_date).toLocaleDateString();
        parts.push(`${startDate} - ${endDate}`);
      }
      
      // Add sick note info if applicable
      if (log.leave_request.leave_type_name === 'Sick Leave' && log.leave_request.total_days >= 3) {
        if (log.leave_request.sick_note_id) {
          parts.push('✓ Sick note uploaded');
        } else {
          parts.push('⚠ Sick note required');
        }
      }
    } else if (details.leave_type) {
      // Fallback to details object
      parts.push(`${details.leave_type}: ${details.total_days || 0} days`);
      
      // Add sick note info from details if available
      if (details.leave_type === 'Sick Leave' && details.total_days >= 3) {
        if (details.sick_note_id) {
          parts.push('✓ Sick note uploaded');
        } else {
          parts.push('⚠ Sick note required');
        }
      }
    }

    // Add action-specific info
    if (log.action === 'request_cancelled' && details.original_status) {
      parts.push(`(was ${details.original_status})`);
    }

    if (log.action === 'balance_adjusted') {
      const type = details.adjustment_type || 'adjustment';
      const amount = details.adjustment_amount || 0;
      parts.push(`${type} ${amount} days`);
    }

    // Add admin notes if available
    if (log.notes) {
      parts.push(`Note: ${log.notes}`);
    }

    return parts.join(' | ') || 'No details available';
  };

  // Clean up body style on component unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  useEffect(() => {
    checkSuperuserStatus();
  }, []);

  // Manage body scroll when modals are open
  useEffect(() => {
    const isAnyModalOpen = showCreateModal || showAuditLogs || showEmployeeDetails || showPasswordReset || showRoleAssignment || showDeactivateConfirm || showViewLeaveModal || showLeaveRequestViewModal || isLeaveModalOpen || showBulkUploadModal || showBulkUploadResults || showExportModal;
    
    if (isAnyModalOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [showCreateModal, showAuditLogs, showEmployeeDetails, showPasswordReset, showRoleAssignment, showDeactivateConfirm, showViewLeaveModal, showLeaveRequestViewModal, isLeaveModalOpen, showBulkUploadModal, showBulkUploadResults, showExportModal]);

  // Fetch current user profile data for avatar
  useEffect(() => {
    const fetchCurrentUserProfile = async () => {
      try {
        const response = await axiosInstance.get('/api/profile/me/');
        if (response.data?.avatar_photo) {
          const absoluteAvatarUrl = getAbsoluteMediaUrl(response.data.avatar_photo);
          console.log('✅ Current user avatar loaded:', absoluteAvatarUrl);
          setCurrentUserAvatar(absoluteAvatarUrl);
        }
      } catch (error) {
        console.error('❌ Error fetching current user profile:', error);
      }
    };
    
    if (user?.id) {
      fetchCurrentUserProfile();
    }
  }, [user]);

  useEffect(() => {
    if (isSuperuser) {
      fetchEmployees();
      fetchStats();
      fetchDeactivatedEmployees(); // Load deactivated employees for tab display
      fetchPositionRequests(); // Load position requests to show notification badge immediately
      fetchLeaveRequests(); // Load leave requests to show notification badge immediately
      fetchDepartments(); // NEW: Load departments for HOD dropdown
    }
  }, [isSuperuser]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  // Password validation function
  const validatePassword = (password) => {
    const strength = {
      valid: false,
      length: password.length >= 8,
      hasNumber: /\d/.test(password),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      hasUpper: /[A-Z]/.test(password),
      hasLower: /[a-z]/.test(password)
    };
    
    // Password is valid if it meets all criteria
    strength.valid = strength.length && strength.hasNumber && 
                     strength.hasSpecial && strength.hasUpper && strength.hasLower;
    
    return strength;
  };

  // Validate create employee password
  useEffect(() => {
    if (newEmployee.password) {
      setCreatePasswordStrength(validatePassword(newEmployee.password));
    }
  }, [newEmployee.password]);

  // Generate a strong random password that satisfies the HMS password policy.
  // Uses window.crypto.getRandomValues so it is cryptographically random.
  const generateStrongPassword = (length = 12) => {
    const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const lower = 'abcdefghijkmnopqrstuvwxyz';
    const digits = '23456789';
    const specials = '!@#$%^&*()-_=+';
    const all = upper + lower + digits + specials;
    const pickFrom = (set) => {
      const idx = window.crypto.getRandomValues(new Uint32Array(1))[0] % set.length;
      return set[idx];
    };
    const chars = [pickFrom(upper), pickFrom(lower), pickFrom(digits), pickFrom(specials)];
    for (let i = chars.length; i < length; i += 1) {
      chars.push(pickFrom(all));
    }
    for (let i = chars.length - 1; i > 0; i -= 1) {
      const j = window.crypto.getRandomValues(new Uint32Array(1))[0] % (i + 1);
      [chars[i], chars[j]] = [chars[j], chars[i]];
    }
    return chars.join('');
  };

  const handleGenerateCreatePassword = () => {
    const generated = generateStrongPassword();
    setNewEmployee((prev) => ({
      ...prev,
      password: generated,
      confirmPassword: generated,
    }));
    setShowCreatePassword(true);
    setShowCreateConfirmPassword(true);
  };

  // Validate reset password
  useEffect(() => {
    if (resetPasswordData.password) {
      setResetPasswordStrength(validatePassword(resetPasswordData.password));
    }
  }, [resetPasswordData.password]);

  const checkSuperuserStatus = async () => {
    try {
      setCheckingAccess(true);
      const response = await hmsAdminService.checkSuperuserStatus();
      setIsSuperuser(response.is_superuser);
      if (!response.is_superuser) {
        showError('Access denied: Superuser privileges required');
      }
    } catch (err) {
      console.error('Error checking superuser status:', err);
      showError('Failed to verify admin privileges');
    } finally {
      setCheckingAccess(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      // The admin API caps a page at 100 rows. This screen has no page navigator —
      // every list discloses itself with its own "Show More" — so pull every page
      // and work off the full set, else employees past row 100 are invisible here.
      const firstPage = await hmsAdminService.getEmployees({
        page: 1,
        search: '',
        is_active: true  // Only fetch active employees
      });

      let allResults = firstPage.results;
      for (let page = 2; page <= (firstPage.total_pages || 1); page++) {
        const nextPage = await hmsAdminService.getEmployees({
          page,
          search: '',
          is_active: true
        });
        allResults = allResults.concat(nextPage.results);
      }
      const response = { ...firstPage, results: allResults };

      setEmployees(response.results);

      // DEBUG: Log first employee to check supervisor/HOD fields
      if (response.results.length > 0) {
        console.log('🔍 DEBUG - First employee data:', {
          name: response.results[0].display_name,
          is_supervisor: response.results[0].is_supervisor,
          is_head_of_department: response.results[0].is_head_of_department,
          supervised_department: response.results[0].supervised_department
        });
      }

      // Separate superusers from regular employees (only active)
      const supers = response.results.filter(emp => emp.is_superuser && emp.is_active);
      const regulars = response.results.filter(emp => !emp.is_superuser && emp.is_active);
      
      // NEW: Filter supervisors, HODs, CRM Approvers, Credit Approvers, Financial Approvers, and Delivery Reviewers (only active)
      const supervs = response.results.filter(emp => emp.is_supervisor && emp.is_active);
      const hods = response.results.filter(emp => emp.is_head_of_department && emp.is_active);
      const crmApprovs = response.results.filter(emp => emp.is_crm_approver && emp.is_active);
      const kycApprovs = response.results.filter(emp => emp.is_kyc_approver && emp.is_active);
      const creditApprovs = response.results.filter(emp => emp.is_credit_approver && emp.is_active);
      const finApprovs = response.results.filter(emp => emp.is_financial_approver && emp.is_active);
      const delReviewers = response.results.filter(emp => emp.is_delivery_reviewer && emp.is_active);
      const legalApprovs = response.results.filter(emp => emp.is_legal_approver && emp.is_active);
      const leaveSups = response.results.filter(emp => emp.is_leave_supervisor && emp.is_active);

      console.log('🔍 DEBUG - Filtered counts:', {
        total: response.results.length,
        supers: supers.length,
        regulars: regulars.length,
        supervisors: supervs.length,
        hods: hods.length,
        crm_approvers: crmApprovs.length,
        credit_approvers: creditApprovs.length,
        financial_approvers: finApprovs.length
      });

      setSuperusers(supers);
      setRegularEmployees(regulars);
      setSupervisors(supervs);  // NEW
      setHeadsOfDepartment(hods);  // NEW
      setCrmApprovers(crmApprovs);  // NEW
      setKycApprovers(kycApprovs);  // NEW
      setCreditApprovers(creditApprovs);  // NEW
      setFinancialApprovers(finApprovs);  // NEW
      setDeliveryReviewers(delReviewers);  // NEW
      setLegalApprovers(legalApprovs);  // NEW (legal contracts)
      setLeaveSupervisors(leaveSups);  // NEW
      
      // Set current user avatar from employee data
      if (user?.id) {
        const currentEmployee = response.results.find(emp => emp.id === user.id);
        if (currentEmployee?.avatar_photo) {
          const absoluteAvatarUrl = getAbsoluteMediaUrl(currentEmployee.avatar_photo);
          setCurrentUserAvatar(absoluteAvatarUrl);
        }
      }
    } catch (err) {
      console.error('Error fetching employees:', err);
      showError('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await hmsAdminService.getDashboardStats();
      setStats(response);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchDeactivatedEmployees = async () => {
    try {
      const deactivated = await fetchEveryPage((page) =>
        hmsAdminService.getDeactivatedEmployees({ page }));
      const response = { results: deactivated.rows, total_count: deactivated.total };
      setDeactivatedEmployees(response.results);
      // The tab label already showed the TRUE total while the list held one page
      // of it -- the badge-says-69, table-shows-25 mismatch all over again.
      setDeactivatedCount(response.total_count);
      setShowDeactivatedTab(response.total_count > 0);
    } catch (err) {
      console.error('Error fetching deactivated employees:', err);
    }
  };

  // NEW: Fetch departments for HOD dropdown
  const fetchDepartments = async () => {
    try {
      const response = await axiosInstance.get('/api/position/choices/');
      setDepartments(response.data.departments || []);
      setOffices(response.data.offices || []);
    } catch (err) {
      console.error('Error fetching departments:', err);
      showError('Failed to load departments. Please refresh the page.');
    }
  };

  const handleCreateEmployee = async (e) => {
    e.preventDefault();
    
    // Validate password fields
    if (!createPasswordStrength.valid) {
      showError('Password does not meet security requirements');
      return;
    }
    
    if (newEmployee.password !== newEmployee.confirmPassword) {
      showError('Passwords do not match');
      return;
    }
    
    // NEW: Validate HOD must have department
    if (newEmployee.is_head_of_department && !newEmployee.supervised_department_id) {
      showError('Head of Department must have a supervised department assigned');
      return;
    }
    
    try {
      // Create employee data including supervisor/HOD/CRM Approver fields
      const employeeData = {
        email: newEmployee.email,
        first_name: newEmployee.first_name,
        last_name: newEmployee.last_name,
        password: newEmployee.password,
        is_superuser: newEmployee.is_superuser,
        is_supervisor: newEmployee.is_supervisor,                    // NEW
        is_head_of_department: newEmployee.is_head_of_department,    // NEW
        is_crm_approver: newEmployee.is_crm_approver,               // NEW
        is_kyc_approver: newEmployee.is_kyc_approver,               // NEW
        is_credit_approver: newEmployee.is_credit_approver,         // NEW
        is_financial_approver: newEmployee.is_financial_approver,   // NEW
        supervised_department_id: newEmployee.supervised_department_id, // NEW
        send_invite_email: newEmployee.send_invite_email                // NEW
      };

      const response = await hmsAdminService.createEmployee(employeeData);
      const userTypeText = newEmployee.is_superuser ? 'Administrator' : 'Regular User';
      const fullName = `${newEmployee.first_name} ${newEmployee.last_name}`;
      if (newEmployee.send_invite_email) {
        if (response && response.invite_email_sent) {
          showSuccess(`${userTypeText} ${fullName} created. Invite email sent to ${newEmployee.email}.`);
        } else {
          showError(
            `${userTypeText} ${fullName} was created, but the invite email could not be sent. ` +
            `Please share the login details manually.`
          );
        }
      } else {
        showSuccess(`${userTypeText} ${fullName} created successfully`);
      }
      setNewEmployee({ 
        email: '', 
        first_name: '', 
        last_name: '', 
        password: '', 
        confirmPassword: '', 
        is_superuser: false,
        is_supervisor: false,              // NEW
        is_head_of_department: false,      // NEW
        is_crm_approver: false,            // NEW
        is_kyc_approver: false,            // NEW
        is_credit_approver: false,         // NEW
        is_financial_approver: false,      // NEW
        supervised_department_id: null,    // NEW
        send_invite_email: true            // NEW
      });
      setCreatePasswordStrength({
        valid: false,
        length: false,
        hasNumber: false,
        hasSpecial: false,
        hasUpper: false,
        hasLower: false
      });
      setShowCreateModal(false);
      fetchEmployees();
      fetchStats();
      fetchDeactivatedEmployees();
    } catch (err) {
      console.error('Error creating employee:', err);
      showError(getErrorMessage(err, 'Failed to create employee. Please try again.'));
    }
  };

  const handleBulkUpload = async (e) => {
    e.preventDefault();
    
    if (!bulkUploadFile) {
      showError('Please select a CSV file to upload');
      return;
    }
    
    setIsUploading(true);
    
    try {
      const result = await hmsAdminService.bulkUploadEmployees(bulkUploadFile, {
        sendInviteEmail: bulkSendInviteEmail,
      });
      
      // Store results for display
      setBulkUploadResults(result);
      
      // Close upload modal and show results
      setShowBulkUploadModal(false);
      setBulkUploadFile(null);
      setShowBulkUploadResults(true);
      
      // Refresh employee lists
      fetchEmployees();
      fetchStats();
      fetchDeactivatedEmployees();
      
    } catch (err) {
      console.error('Error bulk uploading employees:', err);
      showError(getErrorMessage(err, 'Bulk upload failed. Please try again.'));
      setShowBulkUploadModal(false);
      setBulkUploadFile(null);
    } finally {
      setIsUploading(false);
    }
  };

  const toggleExportField = (key) => {
    setExportFields(prev =>
      prev.includes(key)
        ? prev.filter(k => k !== key)
        : EXPORTABLE_EMPLOYEE_FIELDS.filter(f => prev.includes(f.key) || f.key === key).map(f => f.key)
    );
  };

  const handleExportEmployees = async (e) => {
    e.preventDefault();

    if (exportFields.length === 0) {
      showError('Please select at least one field to export');
      return;
    }

    setIsExporting(true);

    try {
      const response = await hmsAdminService.exportEmployeesXlsx({
        fields: exportFields,
        includeInactive: exportIncludeInactive,
      });

      // Parse filename from Content-Disposition, falling back to a default
      let filename = 'employees_export.xlsx';
      const disposition = response.headers['content-disposition'];
      if (disposition) {
        const match = disposition.match(/filename="?([^";]+)"?/);
        if (match) {
          filename = match[1];
        }
      }

      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);

      setShowExportModal(false);
    } catch (err) {
      console.error('Error exporting employees:', err);
      showError(getErrorMessage(err, 'Export failed. Please try again.'));
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownloadCsvTemplate = () => {
    const headers = [
      'Name', 'Surname', 'Short Name', 'Position', STAFF_UPLOAD_OFFICE_HEADER,
      'Legal Entity (contract location)', 'First day of work', 'Mobile phone number',
      'Email-Address', 'Division', 'D.O.B. yyyy', 'D.O.B. mm', 'D.O.B dd'
    ];
    const dummyRows = [
      ['John', 'Smith', 'jsm', 'Trader', 'IMT Bergbau AG', 'Berlin', '01/01/2024', '+49 (172) 1234567', 'jsm@example.com', 'Trade', '1990', 'January', '15'],
      ['Jane', 'Doe', 'jdo', 'Operator', 'IMT Bergbau AG', 'Berlin', '15/03/2023', '+49 (173) 7654321', 'jdo@example.com', 'Operations', '1985', 'March', '22'],
      ['Mike', 'Johnson', 'mjo', 'Accountant', 'IMT Bergbau Africa (Pty.) Ltd.', 'Johannesburg', '01/06/2022', '+27 (82) 1234567', 'mjo@example.com', 'Accounting', '1988', 'July', '8'],
      ['Sarah', 'Williams', 'swi', 'Administrator', 'IMT Bergbau Singapore Pte. Ltd.', 'Singapore', '10/09/2025', '+65 (90) 1234567', 'swi@example.com', 'Management/Admin', '1992', 'November', '30'],
      ['Alex', 'Brown', 'abr', 'CEO', 'IMT Bergbau Switzerland SA', 'Switzerland', '01/01/2020', '+41 (79) 1234567', 'abr@example.com', 'Management/Admin', '1975', 'May', '3'],
    ];
    const csvContent = [headers.join(';'), ...dummyRows.map(r => r.join(';'))].join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'IMT_Bulk_Upload_Template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const [passwordsCopied, setPasswordsCopied] = React.useState(false);

  const handleCopyNewUserPasswords = (newUsers) => {
    const lines = newUsers.map(u => `${u.name} | ${u.email} | ${u.password}`).join('\n');
    navigator.clipboard.writeText(lines).then(() => {
      setPasswordsCopied(true);
      setTimeout(() => setPasswordsCopied(false), 2500);
    });
  };

  const handleDeactivateEmployee = (employeeId, employeeName) => {
    setDeactivateEmployee({ id: employeeId, name: employeeName });
    setShowDeactivateConfirm(true);
    setActiveDropdown(null); // Close any open dropdowns
  };

  const confirmDeactivateEmployee = async () => {
    if (!deactivateEmployee) return;

    try {
      await hmsAdminService.deactivateEmployee(deactivateEmployee.id);
      showSuccess(`Employee ${deactivateEmployee.name} deactivated successfully`);
      setShowDeactivateConfirm(false);
      setDeactivateEmployee(null);
      fetchEmployees();
      fetchStats();
      fetchDeactivatedEmployees();
    } catch (err) {
      console.error('Error deactivating employee:', err);
      showError('Failed to deactivate employee');
      setShowDeactivateConfirm(false);
      setDeactivateEmployee(null);
    }
  };

  const cancelDeactivateEmployee = () => {
    setShowDeactivateConfirm(false);
    setDeactivateEmployee(null);
  };

  const handleActivateEmployee = async (employeeId, employeeName) => {
    try {
      await hmsAdminService.activateEmployee(employeeId);
      showSuccess(`Employee ${employeeName} activated successfully`);
      setActiveDropdown(null); // Close any open dropdowns
      fetchEmployees();
      fetchStats();
      fetchDeactivatedEmployees();
    } catch (err) {
      console.error('Error activating employee:', err);
      showError('Failed to activate employee');
    }
  };

  const fetchPositionRequests = async () => {
    try {
      const response = await hmsAdminService.getPositionVerificationRequests('all');
      setPositionRequests(response.requests || []);
      
      // Count pending requests for notification badge
      const pendingCount = response.status_counts?.pending || 0;
      setPendingRequestsCount(pendingCount);
      
      console.log('📋 ADMIN: Position requests loaded:', {
        total: response.requests?.length || 0,
        pending: pendingCount,
        approved: response.status_counts?.approved || 0,
        denied: response.status_counts?.denied || 0
      });
    } catch (err) {
      console.error('Error fetching position requests:', err);
      // Don't show error for this - it's not critical
      setPositionRequests([]);
      setPendingRequestsCount(0);
    }
  };

  const fetchLeaveRequests = async () => {
    try {
      setLeaveRequestsLoading(true);
      const response = await axiosInstance.get('/api/leave/admin/requests/');
      
      const data = response.data;
      setLeaveRequests(data.results || data);
      
      // Count pending leave requests that require approval (exclude auto-approved types)
      const pending = (data.results || data).filter(req => {
        const requiresApproval = req.leave_type_details?.requires_approval !== false;
        return req.status === 'pending' && requiresApproval;
      });
      setPendingLeaveRequestsCount(pending.length);
      
      console.log('📋 ADMIN: Leave requests loaded:', {
        total: (data.results || data).length,
        pending: pending.length,
        autoApproved: (data.results || data).filter(req => req.leave_type_details?.requires_approval === false).length
      });
    } catch (err) {
      console.error('Error fetching leave requests:', err);
      setLeaveRequests([]);
      setPendingLeaveRequestsCount(0);
    } finally {
      setLeaveRequestsLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      // Load audit logs, leave audit logs, and position requests in parallel
      const [audit] = await Promise.all([
        fetchEveryPage((page) => hmsAdminService.getAuditLogs({ page })),
        fetchPositionRequests().catch(() => {}), // Don't fail if position requests fail
        fetchLeaveAuditLogs().catch(() => {}) // Load leave audit logs as well
      ]);
      const auditResponse = { results: audit.rows };
      setAuditTotal(audit.total);
      
      // Separate employee logs from system data logs
      const systemDataActions = ['system_data_create', 'system_data_update', 'system_data_delete'];
      const employeeLogs = auditResponse.results.filter(log => !systemDataActions.includes(log.action));
      const systemLogs = auditResponse.results.filter(log => systemDataActions.includes(log.action));
      
      setAuditLogs(employeeLogs);
      setSystemDataLogs(systemLogs);
      
      console.log('📋 DEBUG - Audit logs fetched:', {
        total: auditResponse.results.length,
        employeeCount: employeeLogs.length,
        systemDataCount: systemLogs.length,
        actions: auditResponse.results.map(log => log.action)
      });
      
      // Initialize filtered logs for both employee and system data
      const filteredEmployee = filterAuditLogs(employeeLogs, activeAuditFilter);
      const filteredSystemData = filterSystemDataLogs(systemLogs, activeSystemDataFilter);
      setFilteredAuditLogs(filteredEmployee);
      setFilteredSystemDataLogs(filteredSystemData);
      
      console.log('📋 DEBUG - Filtered audit logs:', {
        activeFilter: activeAuditFilter,
        filteredEmployeeCount: filteredEmployee.length,
        filteredSystemDataCount: filteredSystemData.length
      });
      
      setShowAuditLogs(true);
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      showError('Failed to load audit logs');
    }
  };

  const fetchLeaveAuditLogs = async () => {
    try {
      setLeaveAuditLogsLoading(true);
      const leave = await fetchEveryPage((page) =>
        hmsAdminService.getLeaveAuditLogs({ page, action: 'all' }));
      const response = { results: leave.rows };
      setLeaveAuditTotal(leave.total);

      setLeaveAuditLogs(response.results);
      // Initialize filtered logs - default is "all" so show all leave logs
      setFilteredLeaveAuditLogs(response.results);
      
      console.log('📋 ADMIN: Leave audit logs loaded:', {
        total: response.results.length
      });
    } catch (err) {
      console.error('Error fetching leave audit logs:', err);
      showError('Failed to load leave audit logs');
    } finally {
      setLeaveAuditLogsLoading(false);
    }
  };

  // Helper function to format position display name
  const formatPositionDisplay = (positionKey) => {
    if (!positionKey) return 'No Position';
    
    // Convert position key to display name
    const positionMap = {
      'ceo': 'CEO',
      'cfo': 'CFO',
      'managing_director': 'Managing Director',
      'head_of_operations': 'Head of Operations',
      'head_of_accounting_controlling': 'Head of Accounting/Controlling',
      'branch_manager': 'Branch Manager',
      'market_manager': 'Market Manager',
      'deputy_manager': 'Deputy Manager',
      'team_leader': 'Team Leader',
      'project_manager': 'Project Manager',
      'lubricants_manager': 'Lubricants Manager',
      'senior_trader': 'Senior Trader',
      'senior_operator': 'Senior Operator',
      'trader': 'Trader',
      'supply_trader': 'Supply Trader',
      'customer_trader': 'Customer Trader',
      'bunker_trader': 'Bunker Trader',
      'lubricants_trader': 'Lubricants Trader',
      'operator': 'Operator',
      'operator_assistant': 'Operator Assistant',
      'administrator': 'Administrator',
      'administrator_assistant': 'Administrator Assistant',
      'accountant': 'Accountant',
      'accountant_controlling': 'Accountant Controlling',
      'driver': 'Driver',
      'it_support': 'IT Support',
      'hr_specialist': 'HR Specialist',
      'legal_counsel': 'Legal Counsel',
      'marketing_specialist': 'Marketing Specialist',
      'sales_representative': 'Sales Representative',
      'management_admin': 'Management/Admin'
    };
    
    return positionMap[positionKey] || positionKey;
  };

  // Handle position request review (approve/deny)
  const handlePositionRequestReview = async (requestId, action) => {
    const request = positionRequests.find(req => req.id === requestId);
    if (!request) return;

    try {
      const result = await hmsAdminService.reviewPositionRequest(requestId, action, '');
      
      showSuccess(result.message);
      
      // Refresh position requests
      await fetchPositionRequests();
      
    } catch (err) {
      console.error(`Error ${action}ing position request:`, err);
      showError(`Failed to ${action} position request`);
    }
  };

  const handleLeaveRequestAction = async (requestId, action) => {
    try {
      const endpoint = action === 'approve' ? 'approve' : 'reject';
      const response = await axiosInstance.post(`/api/leave/admin/requests/${requestId}/${endpoint}/`, {
        notes: '' // Could add admin notes in the future
      });
      
      const result = response.data;
      showSuccess(result.message);
      
      // Refresh leave requests
      await fetchLeaveRequests();
    } catch (err) {
      console.error(`Error ${action}ing leave request:`, err);
      const errorMessage = err.response?.data?.error || `Failed to ${action} leave request`;
      showError(errorMessage);
    }
  };

  const viewLeaveRequestDetails = (requestId) => {
    setSelectedLeaveRequestId(requestId);
    setShowLeaveRequestViewModal(true);
  };

  /**
   * Convert leave request data to notification format for HMSNotificationModal
   */
  const convertLeaveRequestToNotification = (leaveRequest) => {
    const requiresApproval = leaveRequest.leave_type_details?.requires_approval !== false;
    const isAutoApproved = !requiresApproval && leaveRequest.status === 'approved';
    const isCancelled = leaveRequest.status === 'cancelled';
    
    // Calculate consecutive sick days for sick leave
    const isSickLeave = leaveRequest.leave_type_details?.name === 'Sick Leave';
    const consecutiveDays = isSickLeave ? leaveRequest.total_days : 0;
    
    return {
      id: `leave-${leaveRequest.id}`, // Synthetic ID for modal
      title: isAutoApproved 
        ? `Leave Notification from ${leaveRequest.employee_details?.name || 'Employee'}`
        : `Leave Request from ${leaveRequest.employee_details?.name || 'Employee'}`,
      description: isAutoApproved
        ? `${leaveRequest.employee_details?.name || 'An employee'} has submitted ${leaveRequest.total_days} day(s) of ${leaveRequest.leave_type_details?.name || 'leave'}. This leave type is auto-approved.`
        : `${leaveRequest.employee_details?.name || 'An employee'} has requested ${leaveRequest.total_days} day(s) of ${leaveRequest.leave_type_details?.name || 'leave'}.`,
      notification_type: 'leave_request',
      notification_type_display: isAutoApproved ? 'Leave Notification' : 'Leave Request',
      created_at: leaveRequest.created_at,
      status: 'unread',
      action_required: isCancelled ? false : requiresApproval, // No action required for cancelled requests
      is_synthetic: true, // Flag to indicate this is not a real notification from the notifications table
      metadata: {
        leave_request_id: leaveRequest.id,
        employee_id: leaveRequest.employee_details?.id || leaveRequest.employee_id,
        employee_name: leaveRequest.employee_details?.name,
        leave_type: leaveRequest.leave_type_details?.name,
        start_date: leaveRequest.start_date,
        end_date: leaveRequest.end_date,
        total_days: leaveRequest.total_days,
        reason: leaveRequest.reason,
        cancelled: isCancelled, // Add cancellation flag
        original_status: isCancelled ? 'pending' : leaveRequest.status, // Track original status for cancelled requests
        requires_approval: requiresApproval,
        auto_approved: isAutoApproved,
        // Sick note fields
        consecutive_sick_days: consecutiveDays,
        requires_sick_note: consecutiveDays >= 3,
        sick_note_id: leaveRequest.sick_note_id,
        sick_note_docuware_document_id: leaveRequest.sick_note_docuware_document_id,
        sick_note_name: leaveRequest.sick_note_name,
        sick_note_uploaded_at: leaveRequest.sick_note_uploaded_at
      }
    };
  };

  /**
   * Handle opening leave request modal
   */
  const handleViewLeaveRequest = (leaveRequest) => {
    const notificationFormat = convertLeaveRequestToNotification(leaveRequest);
    setSelectedLeaveForModal(notificationFormat);
    setIsLeaveModalOpen(true);
  };

  /**
   * Handle closing leave request modal
   */
  const handleCloseLeaveModal = () => {
    setIsLeaveModalOpen(false);
    setSelectedLeaveForModal(null);
  };

  /**
   * Handle action complete from leave request modal
   */
  const handleLeaveModalActionComplete = () => {
    // Refresh leave requests after approve/deny
    fetchLeaveRequests();
    fetchStats(); // Update stats if needed
    
    // Dispatch custom event to update navbar notification badge immediately
    window.dispatchEvent(new CustomEvent('notificationStatsChanged'));
  };

  // Debounced search function
  const debouncedSearch = useCallback(async (query, filters = {}) => {
    try {
      setIsSearching(true);
      const params = {
        page: 1,
        search: query.trim(),
        active: undefined
      };

      // Add filter parameters if provided
      if (filters.position) params.position = filters.position;
      if (filters.department) params.department = filters.department;
      if (filters.office) params.office = filters.office;
      if (filters.status) params.is_active = filters.status;

      const response = await hmsAdminService.getEmployees(params);

      setSearchResults(response.results);
      setSearchedQuery(query.trim());
    } catch (err) {
      console.error('Error searching employees:', err);
      showError('Failed to search employees');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Apply filters function (independent of search)
  const applyFilters = useCallback(async () => {
    try {
      setIsSearching(true);
      setHasAppliedFilters(true); // Mark that filters have been applied
      const params = {
        page: 1,
        search: '', // No search query for filters-only
        active: undefined
      };

      // Add filter parameters
      if (searchFilters.position) params.position = searchFilters.position;
      if (searchFilters.department) params.department = searchFilters.department;
      if (searchFilters.office) params.office = searchFilters.office;
      if (searchFilters.status) params.is_active = searchFilters.status;

      const response = await hmsAdminService.getEmployees(params);
      setSearchResults(response.results);
      showError('');
    } catch (err) {
      console.error('Error applying filters:', err);
      showError('Failed to apply filters');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [searchFilters]);

  // Handle search input change with debouncing
  const handleSearchInputChange = useCallback((e) => {
    const value = e.target.value;
    setSearchQuery(value);

    // Clear previous debounce
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    // Set new debounce
    searchDebounceRef.current = setTimeout(() => {
      if (value.trim()) {
        debouncedSearch(value, {}); // Search only, no filters applied automatically
      } else {
        setSearchResults([]);
      }
    }, 500); // 500ms debounce
  }, [debouncedSearch]);

  // Handle manual search (button click)
  const handleSearch = async (e) => {
    e.preventDefault();

    // Clear debounce
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    await debouncedSearch(searchQuery, {}); // Manual search - no filters applied
  };

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    const newFilters = { ...searchFilters, [field]: value };
    setSearchFilters(newFilters);
    // Reset applied filters flag when user changes filters
    setHasAppliedFilters(false);
    // Filters are independent - only apply when "Apply Filters" button is pressed
  };

  const clearSearchResults = () => {
    setSearchResults([]);
    setSearchQuery('');
  };

  // Handle gear cog dropdown toggle with positioning logic
  const toggleDropdown = (employeeId) => {
    const newActiveDropdown = activeDropdown === employeeId ? null : employeeId;
    setActiveDropdown(newActiveDropdown);
    
  // FIRST: Always remove dropdown-open from ALL dropdowns and clear padding
  document.querySelectorAll('.action-dropdown.dropdown-open').forEach(el => {
    el.classList.remove('dropdown-open');
    const menu = el.querySelector('.dropdown-menu');
    if (menu) {
      menu.classList.remove('dropdown-upward');
    }
  });
  
  // Remove padding from search dropdown list
  const searchList = document.querySelector('.search-dropdown-list');
  if (searchList) {
    searchList.style.paddingBottom = '';
  }
    
    // If opening a dropdown, calculate position after render
    if (newActiveDropdown) {
      setTimeout(() => {
        const dropdownElement = document.querySelector(`[data-dropdown-id="${employeeId}"]`);
        const dropdownMenu = dropdownElement?.querySelector('.dropdown-menu');
        const actionDropdown = dropdownElement?.closest('.action-dropdown');
        
        if (dropdownMenu && actionDropdown) {
          // Add dropdown-open class to parent for z-index elevation
          actionDropdown.classList.add('dropdown-open');
          
          // Check if this is a search result dropdown
          const isSearchResult = dropdownElement?.closest('.search-result-item');
          
          if (isSearchResult) {
            // Add padding to search list to create space for dropdown
            const searchList = document.querySelector('.search-dropdown-list');
            if (searchList) {
              const menuHeight = dropdownMenu.offsetHeight;
              searchList.style.paddingBottom = `${menuHeight}px`;
            }
          }
          
          // Calculate if dropdown would go off-screen
          const rect = dropdownMenu.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const spaceBelow = viewportHeight - rect.bottom;
          
          // If less than 50px space below or dropdown is cut off, display upward
          if (spaceBelow < 50 || rect.bottom > viewportHeight) {
            dropdownMenu.classList.add('dropdown-upward');
          } else {
            dropdownMenu.classList.remove('dropdown-upward');
          }
        }
      }, 10);
    }
    // Cleanup is now handled at the top of the function
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    // Only add listener if there's an active dropdown
    if (!activeDropdown) return;
    
    const handleClickOutside = (event) => {
      // Ignore clicks on gear buttons (they handle their own toggle)
      if (event.target.closest('.gear-button')) {
        return;
      }
      
      // Check if click is on dropdown menu or action dropdown
      const clickedDropdown = event.target.closest('.action-dropdown');
      const clickedMenu = event.target.closest('.dropdown-menu');
      
      // If clicked outside both, close all dropdowns
      if (!clickedDropdown && !clickedMenu) {
        setActiveDropdown(null);
        // Remove dropdown-open class from all action-dropdowns
        document.querySelectorAll('.action-dropdown.dropdown-open').forEach(el => {
          el.classList.remove('dropdown-open');
          const menu = el.querySelector('.dropdown-menu');
          if (menu) {
            menu.classList.remove('dropdown-upward');
          }
        });
        // Remove padding from search dropdown list
        const searchList = document.querySelector('.search-dropdown-list');
        if (searchList) {
          searchList.style.paddingBottom = '';
        }
      }
    };
    
    // Add listener after a small delay to avoid race condition with toggleDropdown
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 50);
    
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeDropdown]); // Re-run when activeDropdown changes


  // Helper function to format languages display
  const formatLanguages = (languages) => {
    if (!languages || languages.length === 0) return 'Not set';
    
    return languages.map(lang => {
      // Convert underscore to space and capitalize each word
      return lang.split('_').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).join(' ');
    }).join(', ');
  };

  // Helper function to format working characteristics
  const formatCharacteristics = (characteristics) => {
    if (!characteristics || characteristics.length === 0) return 'Not set';
    
    return characteristics.map(char => {
      // Convert underscore to space and capitalize each word
      return char.split('_').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).join(' ');
    }).join(', ');
  };

  // Handle view employee details - show modal with full employee info
  const handleViewEmployee = (employee) => {
    setActiveDropdown(null);
    setSelectedEmployee(employee);
    setShowEmployeeDetails(true);
  };

  // Handle employee selection from search dropdown
  const handleEmployeeSelect = (employee) => {
    handleViewEmployee(employee);
    setSearchQuery('');
    setSearchResults([]);
    setHasAppliedFilters(false);
  };

  // Handle view leave - show modal
  const handleViewLeave = (employee) => {
    setActiveDropdown(null);
    setSelectedEmployee(employee);
    setShowViewLeaveModal(true);
  };

  // Handle reset password - show modal
  const handleResetPassword = (employee) => {
    setActiveDropdown(null);
    setPasswordResetEmployee(employee);
    setResetPasswordData({
      password: '',
      confirmPassword: ''
    });
    setResetPasswordStrength({
      valid: false,
      length: false,
      hasNumber: false,
      hasSpecial: false,
      hasUpper: false,
      hasLower: false
    });
    setShowPasswordReset(true);
  };

  // Handle password reset form submission
  const handlePasswordResetSubmit = async (e) => {
    e.preventDefault();
    
    // Validate password
    if (!resetPasswordStrength.valid) {
      showError('Password does not meet security requirements');
      return;
    }
    
    if (resetPasswordData.password !== resetPasswordData.confirmPassword) {
      showError('Passwords do not match');
      return;
    }

    try {
      const response = await hmsAdminService.resetEmployeePassword({
        employee_id: passwordResetEmployee.id,
        new_password: resetPasswordData.password
      });
      
      showSuccess(`Password reset successfully for ${passwordResetEmployee.display_name}`);
      setShowPasswordReset(false);
      setPasswordResetEmployee(null);
      
    } catch (err) {
      console.error('Error resetting password:', err);
      showError(getErrorMessage(err, 'Failed to reset password. Please try again.'));
    }
  };

  // Handle role assignment - show modal
  const handleAssignRole = async (employee) => {
    setActiveDropdown(null);
    setRoleAssignmentEmployee(employee);
    setSelectedRole(employee.is_superuser ? 'administrator' : 'user'); // Show current role
    
    // Seed every setting the form holds straight off the employee record.
    setRoleAssignmentData(roleAssignmentFormFromEmployee(employee));
    
    // Fetch reportee counts for warning modal
    try {
      const countsResponse = await hmsAdminService.getEmployeeReporteeCounts(employee.id);
      setReporteeCounts({
        supervisor_count: countsResponse.supervisor_count || 0,
        hod_count: countsResponse.hod_count || 0
      });
    } catch (err) {
      console.error('Error fetching reportee counts:', err);
      // Set to 0 if error - we'll still allow the operation
      setReporteeCounts({ supervisor_count: 0, hod_count: 0 });
    }
    
    setShowRoleAssignment(true);
  };

  // Handle role assignment form submission
  const handleRoleAssignmentSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedRole) {
      showError('Please select a role');
      return;
    }
    
    // NEW: Validate HOD must have department
    if (roleAssignmentData.is_head_of_department && !roleAssignmentData.supervised_department_id) {
      showError('Head of Department must have a supervised department assigned');
      return;
    }

    // Check if we're unchecking Supervisor or HOD with active reportees
    const wasOriginalEmployee = roleAssignmentEmployee;
    const isUncheckingSupervisor = wasOriginalEmployee.is_supervisor && !roleAssignmentData.is_supervisor;
    const isUncheckingHOD = wasOriginalEmployee.is_head_of_department && !roleAssignmentData.is_head_of_department;
    
    // ONE place builds the save, for both paths below. The form's own contents
    // go out whole — no field list, so a setting added to the form cannot fail
    // to leave the browser.
    const payload = buildRoleAssignmentPayload(
      roleAssignmentEmployee.id, selectedRole, roleAssignmentData,
      // The employee record as the server actually sent it. Passed in so the
      // save can tell a value the server gave us from one this screen only
      // guessed at, and never write back a guess.
      roleAssignmentEmployee,
    );

    const needsWarning = (
      (isUncheckingSupervisor && reporteeCounts.supervisor_count > 0) ||
      (isUncheckingHOD && reporteeCounts.hod_count > 0)
    );
    
    if (needsWarning) {
      // Show warning modal instead of submitting — the payload is already built.
      setPendingRoleSubmission(payload);
      setUnassignWarningData({
        employee: roleAssignmentEmployee,
        isUncheckingSupervisor,
        isUncheckingHOD,
        supervisorCount: reporteeCounts.supervisor_count,
        hodCount: reporteeCounts.hod_count
      });
      setShowUnassignWarning(true);
      return;
    }

    try {
      console.log('Assigning role with payload:', payload);
      
      const response = await hmsAdminService.assignEmployeeRole(payload);
      
      console.log('✅ DEBUG - Role assignment response:', response);
      
      // A save that asked for a state the account is already in is not a
      // failure, but it is not a save either — say so plainly rather than
      // flashing green "saved" for something that was never written.
      if (response.no_changes) {
        showInfo(response.message);
      } else {
        showSuccess(response.message);
      }
      setShowRoleAssignment(false);
      setRoleAssignmentEmployee(null);
      setSelectedRole('');
      setRoleAssignmentData({ ...ROLE_ASSIGNMENT_FORM_DEFAULTS });
      
      // Refresh employee list AND audit logs to show changes immediately
      await fetchEmployees();
      
      // Refresh audit logs if modal is open
      if (showAuditLogs) {
        await fetchAuditLogs();
      }
      
    } catch (err) {
      console.error('Error assigning role:', err);
      showError(getErrorMessage(err, 'Failed to assign role. Please try again.'));
    }
  };

  // Handle confirmed unassignment after warning
  const handleConfirmUnassignment = async () => {
    if (!pendingRoleSubmission) return;
    
    try {
      console.log('🚀 DEBUG - Confirming role unassignment with payload:', pendingRoleSubmission);
      
      const response = await hmsAdminService.assignEmployeeRole(pendingRoleSubmission);
      
      console.log('✅ DEBUG - Role unassignment response:', response);
      
      // Same calm answer on the unassign path: nothing moved is not a save.
      if (response.no_changes) {
        showInfo(response.message);
      } else {
        showSuccess(response.message);
      }
      
      // Close both modals
      setShowUnassignWarning(false);
      setShowRoleAssignment(false);
      
      // Reset states
      setRoleAssignmentEmployee(null);
      setSelectedRole('');
      setRoleAssignmentData({ ...ROLE_ASSIGNMENT_FORM_DEFAULTS });
      setPendingRoleSubmission(null);
      setUnassignWarningData(null);
      
      // Refresh employee list AND audit logs to show changes immediately
      await fetchEmployees();
      
      // Refresh audit logs if modal is open
      if (showAuditLogs) {
        await fetchAuditLogs();
      }
      
    } catch (err) {
      console.error('Error confirming unassignment:', err);
      showError(getErrorMessage(err, 'Failed to assign role. Please try again.'));
    }
  };

  // Handle canceling unassignment warning
  const handleCancelUnassignment = () => {
    setShowUnassignWarning(false);
    setPendingRoleSubmission(null);
    setUnassignWarningData(null);
    // Keep the role assignment modal open so user can adjust
  };

  // Handle opening assign users modal
  const handleAssignUsers = async (supervisor) => {
    setActiveDropdown(null);
    setAssignSupervisor(supervisor);
    setAssignHOD(null);
    setAssignType('supervisor');
    setAssignUsersSearch('');
    setAssignFilterDept('');
    setAssignFilterOffice('');
    
    // Fetch users already assigned to this supervisor and pre-select them
    try {
      const response = await hmsAdminService.getSupervisorAssignments(supervisor.id);
      const assignedUserIds = response.assigned_users
        .map(user => user.id)
        .filter(id => Number(id) !== Number(supervisor.id));
      setSelectedUserIds(assignedUserIds);
    } catch (err) {
      console.error('Error fetching supervisor assignments:', err);
      setSelectedUserIds([]);
    }
    
    setShowAssignUsers(true);
  };

  // Handle opening assign users modal for HOD
  const handleAssignUsersToHOD = async (hod) => {
    setActiveDropdown(null);
    setAssignHOD(hod);
    setAssignSupervisor(null);
    setAssignType('hod');
    setAssignUsersSearch('');
    setAssignFilterDept('');
    setAssignFilterOffice('');
    
    // Fetch users already assigned to this HOD and pre-select them
    try {
      const response = await hmsAdminService.getHODAssignments(hod.id);
      const assignedUserIds = response.assigned_users
        .map(user => user.id)
        .filter(id => Number(id) !== Number(hod.id));
      setSelectedUserIds(assignedUserIds);
    } catch (err) {
      console.error('Error fetching HOD assignments:', err);
      setSelectedUserIds([]);
    }
    
    setShowAssignUsers(true);
  };

  // Handle opening assign users modal for Leave Supervisor
  const handleAssignUsersToLeaveSupervisor = async (leaveSup) => {
    setActiveDropdown(null);
    setAssignLeaveSupervisor(leaveSup);
    setAssignSupervisor(null);
    setAssignHOD(null);
    setAssignType('leave_supervisor');
    setAssignUsersSearch('');
    setAssignFilterDept('');
    setAssignFilterOffice('');

    try {
      const response = await hmsAdminService.getLeaveSupervisorAssignments(leaveSup.id);
      const assignedUserIds = response.assigned_users
        .map(user => user.id)
        .filter(id => Number(id) !== Number(leaveSup.id));
      setSelectedUserIds(assignedUserIds);
    } catch (err) {
      console.error('Error fetching leave supervisor assignments:', err);
      setSelectedUserIds([]);
    }

    setShowAssignUsers(true);
  };

  // Handle assigning/unassigning users to supervisor, HOD, or leave supervisor
  const handleAssignUsersSubmit = async (unassign = false) => {
    if (!assignSupervisor && !assignHOD && !assignLeaveSupervisor) return;
    
    if (selectedUserIds.length === 0) {
      showError('Please select at least one user');
      return;
    }

    try {
      let response;
      
      if (assignType === 'supervisor') {
        const safeUserIds = selectedUserIds.filter(id => Number(id) !== Number(assignSupervisor.id));
        response = await hmsAdminService.assignUsersToSupervisor({
          supervisor_id: assignSupervisor.id,
          user_ids: safeUserIds,
          unassign
        });
      } else if (assignType === 'hod') {
        const safeUserIds = selectedUserIds.filter(id => Number(id) !== Number(assignHOD.id));
        response = await hmsAdminService.assignUsersToHOD({
          hod_id: assignHOD.id,
          user_ids: safeUserIds,
          unassign
        });
      } else if (assignType === 'leave_supervisor') {
        const safeUserIds = selectedUserIds.filter(id => Number(id) !== Number(assignLeaveSupervisor.id));
        response = await hmsAdminService.assignUsersToLeaveSupervisor({
          leave_supervisor_id: assignLeaveSupervisor.id,
          user_ids: safeUserIds,
          unassign
        });
      }
      
      showSuccess(response.message);
      setShowAssignUsers(false);
      setAssignSupervisor(null);
      setAssignHOD(null);
      setAssignLeaveSupervisor(null);
      setAssignType(null);
      setSelectedUserIds([]);
      setAssignFilterDept('');
      setAssignFilterOffice('');
      setAssignUsersSearch('');
      
      // Refresh employee list
      await fetchEmployees();
      
      // Refresh audit logs if modal is open
      if (showAuditLogs) {
        await fetchAuditLogs();
      }
      
    } catch (err) {
      console.error('Error assigning users:', err);
      showError(getErrorMessage(err, 'Failed to assign role. Please try again.'));
    }
  };

  // Toggle user selection
  const toggleUserSelection = (userId) => {
    setSelectedUserIds(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  // Select all users
  // Shared filter for the Assign Users modal: applies self-exclusion, active,
  // search, and the Department/Office dropdown filters. Used by both the rendered
  // list and Select All so they always agree.
  const getFilteredAssignUsers = () => {
    const selfId = assignType === 'supervisor' ? assignSupervisor?.id : assignType === 'hod' ? assignHOD?.id : assignLeaveSupervisor?.id;
    const searchLower = assignUsersSearch.trim().toLowerCase();
    return employees.filter(emp => {
      if (!emp.is_active) return false;
      if (Number(emp.id) === Number(selfId)) return false;
      if (assignFilterDept && emp.department?.id !== parseInt(assignFilterDept)) return false;
      if (assignFilterOffice && emp.office?.id !== parseInt(assignFilterOffice)) return false;
      if (searchLower) {
        // Every typed word must appear somewhere, any order: "simpkins gareth" works.
        const haystack = `${emp.display_name || ''} ${emp.email || ''}`.toLowerCase();
        return searchLower.split(/\s+/).every(word => haystack.includes(word));
      }
      return true;
    });
  };

  const selectAllUsers = () => {
    // Only select users currently visible under the active filters
    setSelectedUserIds(prev => {
      const visibleIds = getFilteredAssignUsers().map(emp => emp.id);
      return Array.from(new Set([...prev, ...visibleIds]));
    });
  };

  // Deselect all users
  const deselectAllUsers = () => {
    setSelectedUserIds([]);
  };

  // Get available departments for HOD assignment
  const getAvailableDepartments = (currentEmployeeId = null) => {
    // Get all departments that are already assigned to a HOD
    const assignedDepartmentIds = headsOfDepartment
      .filter(hod => hod.supervised_department?.id) // Filter out HODs without departments
      .map(hod => ({
        departmentId: hod.supervised_department.id,
        employeeId: hod.id
      }));

    // Filter out departments that are assigned to other HODs
    return departments.filter(dept => {
      const assignment = assignedDepartmentIds.find(a => a.departmentId === dept.id);
      // Include department if:
      // 1. It's not assigned to anyone, OR
      // 2. It's assigned to the current employee being edited
      return !assignment || assignment.employeeId === currentEmployeeId;
    });
  };

  // Format last login display
  const formatLastLogin = (lastLogin) => {
    if (!lastLogin) return 'Never';
    const now = new Date();
    const loginDate = new Date(lastLogin);
    const diffInHours = Math.floor((now - loginDate) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return loginDate.toLocaleDateString();
  };

  // Get permission badge
  const getPermissionBadge = (employee) => {
    if (employee.is_superuser) {
      return <span className="permission-badge superuser">Superuser</span>;
    }
    if (employee.is_staff) {
      return <span className="permission-badge staff">Staff</span>;
    }
    return <span className="permission-badge user">User</span>;
  };

  // Show loading screen while checking access
  if (checkingAccess) {
    return (
      <Layout showLeftNavSidebar={true} showRightSidebar={false}>
        <div className="hms-admin-dashboard">
          <div className="admin-container">
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Verifying admin access...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!isSuperuser) {
    return (
      <Layout showLeftNavSidebar={true} showRightSidebar={false}>
        <div className="hms-admin-dashboard">
          <div className="admin-container">
            <div className="access-denied">
              <div className="access-denied-icon-wrapper">
                <FontAwesomeIcon icon={faLock} className="access-denied-icon" />
              </div>
              <h2>Access Denied</h2>
              <p>You do not have the required permissions to view the Admin Dashboard. Please contact your system administrator if you believe this is an error.</p>
              <button onClick={() => history.push('/settings')} className="access-denied-btn">
                <FontAwesomeIcon icon={faArrowLeft} />
                Return to Hamster HR
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout showLeftNavSidebar={true} showRightSidebar={false}>
      <div className="hms-admin-dashboard">
        <div className="admin-container">
          <div className="admin-header">
            <div className="header-left">
              <img src={logo} alt="IMT Bergbau AG" className="header-logo-img" />
              <div>
                <h1 className="hms-screen-title">IMT Admin Dashboard</h1>
                <p className="admin-subtitle">Manage users, permissions, and organizational structure</p>
              </div>
            </div>
            <div className="header-right">
              {/* Compact Search in Header */}
              <div className="header-search-wrapper">
                <div className="header-search-container">
                  <div className="search-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Search employees..."
                    value={searchQuery}
                    onChange={handleSearchInputChange}
                    className="header-search-input"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="clear-search"
                      onClick={clearSearchResults}
                      aria-label="Clear search"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  )}
                </div>
                
                {/* Search Results Dropdown */}
                {(isSearching || searchResults.length > 0 || (hasAppliedFilters && searchQuery) || (searchedQuery && searchedQuery === searchQuery.trim())) && (
                  <div className="header-search-dropdown">
                    {isSearching && (
                      <div className="search-dropdown-loading">
                        <div className="spinner"></div>
                        <span>Searching employees...</span>
                      </div>
                    )}
                    
                    {!isSearching && searchResults.length === 0 && (hasAppliedFilters || searchedQuery === searchQuery.trim()) && (
                      <div className="search-dropdown-empty">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10 10L14 14M14 10L10 14M21 21L16.514 16.506M19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <p>No employees found</p>
                      </div>
                    )}
                    
                    {!isSearching && searchResults.length > 0 && (
                      <div className="search-dropdown-results">
                        <div className="search-dropdown-header">
                          <span>Found {searchResults.length} employee{searchResults.length !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="search-dropdown-list">
                          {searchResults.map(employee => (
                            <div 
                              key={employee.id}
                              className="search-result-item"
                            >
                              <div className="search-result-avatar">
                                {employee.avatar_photo ? (
                                  <img src={employee.avatar_photo} alt={`${employee.first_name} ${employee.last_name}`} />
                                ) : (
                                  <div className="avatar-placeholder">
                                    {employee.first_name?.[0]}{employee.last_name?.[0]}
                                  </div>
                                )}
                              </div>
                              <div className="search-result-info" onClick={() => handleEmployeeSelect(employee)}>
                                <div className="search-result-name">
                                  {employee.first_name} {employee.last_name}
                                </div>
                                <div className="search-result-details">
                                  {employee.hms_employee_id || 'N/A'}
                                </div>
                              </div>
                              <div className="action-dropdown" onClick={(e) => e.stopPropagation()} data-dropdown-id={`search-dropdown-${employee.id}`}>
                                <button
                                  className="gear-button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleDropdown(`search-dropdown-${employee.id}`);
                                  }}
                                >
                                  Actions
                                </button>
                                {activeDropdown === `search-dropdown-${employee.id}` && (
                                  <div className="dropdown-menu">
                                    {employee.is_active && (
                                      <>
                                        <button onClick={() => handleViewEmployee(employee)}>View Details</button>
                                        <button onClick={() => handleResetPassword(employee)}>Reset Password</button>
                                        <button onClick={() => handleViewLeave(employee)}>View Leave</button>
                                        <button onClick={() => handleAssignRole(employee)}>Assign Role</button>
                                        <hr />
                                      </>
                                    )}
                                    {employee.is_active ? (
                                      <button
                                        className="danger"
                                        onClick={() => handleDeactivateEmployee(employee.id, employee.display_name)}
                                      >
                                        Deactivate
                                      </button>
                                    ) : (
                                      <button
                                        className="success"
                                        onClick={() => handleActivateEmployee(employee.id, employee.display_name)}
                                      >
                                        Activate
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* User Avatar - Display Only */}
              <div className="header-filter-wrapper">
                <div 
                  className="header-user-avatar"
                  title="Your Profile"
                >
                  {currentUserAvatar ? (
                    <img 
                      src={currentUserAvatar} 
                      alt="Your profile" 
                      className="user-avatar-image"
                    />
                  ) : (
                    <div className="user-avatar-initials">
                      {user?.first_name?.[0] || ''}{user?.last_name?.[0] || ''}
                    </div>
                  )}
                </div>
                
                {/* Advanced Filters Dropdown */}
                {showAdvancedFilters && (
                  <div className="header-filters-dropdown">
                    <div className="filters-dropdown-header">
                      <span>Advanced Filters</span>
                      <button 
                        className="close-filters-btn"
                        onClick={() => setShowAdvancedFilters(false)}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </div>
                    <form className="hms-stacked-form" onSubmit={handleSearch}>
                      <div className="filters-dropdown-body">
                        <div className="filter-group-inline">
                          <label>Position</label>
                          <select
                            value={searchFilters.position}
                            onChange={(e) => handleFilterChange('position', e.target.value)}
                          >
                            <option value="">All Positions</option>
                            <option value="Operator">Operator</option>
                            <option value="Trader">Trader</option>
                            <option value="Senior Trader">Senior Trader</option>
                            <option value="Managing Director">Managing Director</option>
                            <option value="CEO">CEO</option>
                          </select>
                        </div>
                        <div className="filter-group-inline">
                          <label>Department</label>
                          <select
                            value={searchFilters.department}
                            onChange={(e) => handleFilterChange('department', e.target.value)}
                          >
                            <option value="">All Departments</option>
                            <option value="Accounting">Accounting</option>
                            <option value="Management/Admin">Management/Admin</option>
                            <option value="Legal">Legal</option>
                            <option value="Operations">Operations</option>
                            <option value="Trade">Trade</option>
                          </select>
                        </div>
                        <div className="filter-group-inline">
                          <label>Office</label>
                          <select
                            value={searchFilters.office}
                            onChange={(e) => handleFilterChange('office', e.target.value)}
                          >
                            <option value="">All Offices</option>
                            <option value="Hamburg">Hamburg</option>
                            <option value="Geneva">Geneva</option>
                            <option value="Cape Town">Cape Town</option>
                          </select>
                        </div>
                        <div className="filter-group-inline">
                          <label>Status</label>
                          <select
                            value={searchFilters.status}
                            onChange={(e) => handleFilterChange('status', e.target.value)}
                          >
                            <option value="">All Status</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        </div>
                      </div>
                      <div className="filters-dropdown-footer">
                        <button type="submit" className="btn-apply-filters">
                          Apply Filters
                        </button>
                        <button 
                          type="button"
                          className="btn-clear-filters"
                          onClick={() => {
                            const clearedFilters = {
                              position: '',
                              department: '',
                              office: '',
                              status: ''
                            };
                            setSearchFilters(clearedFilters);
                            setSearchResults([]);
                            setHasAppliedFilters(false);
                          }}
                        >
                          Clear
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
              <div className="admin-actions">
                <button 
                  className="btn btn-primary"
                  onClick={() => setShowCreateModal(true)}
                >
                  <FontAwesomeIcon icon={faUserPlus} /> Create Employee
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={() => setShowBulkUploadModal(true)}
                >
                  <FontAwesomeIcon icon={faFileUpload} /> Bulk Upload Users
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={() => setShowExportModal(true)}
                >
                  <FontAwesomeIcon icon={faFileExcel} /> Export XL
                </button>
                <button 
                  className="btn btn-secondary position-relative"
                  onClick={fetchAuditLogs}
                >
                  Admin Center
                  {(pendingRequestsCount + pendingLeaveRequestsCount) > 0 && (
                    <span className="notification-badge">
                      {pendingRequestsCount + pendingLeaveRequestsCount}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>


          {/* Advanced Filters and Search Results - Moved to header dropdown */}
          
          {/* Full search results table (when needed for detailed view) */}
          {!isSearching && searchResults.length > 5 && showAdvancedFilters && (
            <div className="search-section">
              <div className="search-results-container">
                <h4>Filter Results ({searchResults.length})</h4>
                <div className="search-results-table-container">
                  <table className="search-results-table">
                    <thead>
                      <tr>
                        <th>IMT ID</th>
                        <th>Avatar</th>
                        <th>Name</th>
                        <th>Position</th>
                        <th>Department</th>
                        <th>Office</th>
                        <th>Last Login</th>
                        <th>Permissions</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {searchResults.map(employee => (
                        <tr 
                          key={`search-${employee.id}`} 
                          className={!employee.is_active ? 'inactive' : ''}
                          onClick={() => handleViewEmployee(employee)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td className="result-main">
                            <div className="result-title">
                              {employee.display_name}
                            </div>
                            <div className="result-meta">
                              <div className="meta-item">
                                <span>IMT ID: {employee.hms_employee_id || 'Not assigned'}</span>
                              </div>
                              <div className="meta-item">
                                <span>Email: {employee.email}</span>
                              </div>
                              <div className="meta-item">
                                <span>Position: {employee.position?.name || 'Not set'}</span>
                              </div>
                              <div className="meta-item">
                                <span>Department: {employee.department?.name || 'Not set'}</span>
                              </div>
                              <div className="meta-item">
                                <span>Office: {employee.office?.name || 'Not set'}</span>
                              </div>
                              <div className="meta-item">
                                <span>Last Login: {formatLastLogin(employee.last_login)}</span>
                              </div>
                            </div>
                          </td>
                          <td className="result-variant">
                            {getPermissionBadge(employee)}
                            <span className={`status-badge ${employee.is_active ? 'active' : 'inactive'}`}>
                              {employee.is_active ? 'Active' : 'Inactive'}
                            </span>
                            <div className="action-dropdown" onClick={(e) => e.stopPropagation()} data-dropdown-id={`search-${employee.id}`}>
                              <button
                                className="gear-button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleDropdown(`search-${employee.id}`);
                                }}
                              >
                                Actions
                              </button>
                              {activeDropdown === `search-${employee.id}` && (
                                <div className="dropdown-menu">
                                  {employee.is_active && (
                                    <>
                                      <button onClick={() => handleViewEmployee(employee)}>View Details</button>
                                      <button onClick={() => handleResetPassword(employee)}>Reset Password</button>
                                      <button onClick={() => handleViewLeave(employee)}>View Leave</button>
                                      <button onClick={() => handleAssignRole(employee)}>Assign Role</button>
                                      <hr />
                                    </>
                                  )}
                                  {employee.is_active ? (
                                    <button
                                      className="danger"
                                      onClick={() => handleDeactivateEmployee(employee.id, employee.display_name)}
                                    >
                                      Deactivate
                                    </button>
                                  ) : (
                                    <button
                                      className="success"
                                      onClick={() => handleActivateEmployee(employee.id, employee.display_name)}
                                    >
                                      Activate
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Admin Mode Toggle - Employee Management vs System Data */}
          <div className="admin-mode-toggle-container">
            <div className="admin-mode-toggle">
              <button 
                type="button"
                className={`toggle-option ${adminMode === 'employees' ? 'active' : ''}`}
                onClick={() => setAdminMode('employees')}
              >
                Employee Management
              </button>
              <button 
                type="button"
                className={`toggle-option ${adminMode === 'system-data' ? 'active' : ''}`}
                onClick={() => setAdminMode('system-data')}
              >
                System Data
              </button>
              <button
                type="button"
                className={`toggle-option ${adminMode === 'power-of-attorney' ? 'active' : ''}`}
                onClick={() => setAdminMode('power-of-attorney')}
              >
                Power of Attorney
              </button>
            </div>
          </div>

          {/* HMS Tabs Navigation - Only show employee tabs in Employee Management mode */}
          {adminMode === 'employees' && (
            <div className="hms-tabs">
              <div className="tabs-container">
                <button 
                  className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
                  onClick={() => setActiveTab('users')}
                >
                <div className="tab-icon">
                  <FontAwesomeIcon icon={faUsers} />
                </div>
                <span>Users ({regularEmployees.length})</span>
              </button>
              <button 
                className={`tab-button ${activeTab === 'admins' ? 'active' : ''}`}
                onClick={() => setActiveTab('admins')}
              >
                <div className="tab-icon">
                  <FontAwesomeIcon icon={faUserShield} />
                </div>
                <span>Admins ({superusers.length})</span>
              </button>
              
              {/* NEW: Supervisors Tab */}
              <button 
                className={`tab-button ${activeTab === 'supervisors' ? 'active' : ''}`}
                onClick={() => setActiveTab('supervisors')}
              >
                <div className="tab-icon">
                  <FontAwesomeIcon icon={faUserTie} />
                </div>
                <span>Supervisors ({supervisors.length})</span>
              </button>
              
              {/* Leave Supervisors Tab */}
              <button 
                className={`tab-button ${activeTab === 'leave_supervisors' ? 'active' : ''}`}
                onClick={() => setActiveTab('leave_supervisors')}
              >
                <div className="tab-icon">
                  <FontAwesomeIcon icon={faCalendarCheck} />
                </div>
                <span>Leave Supervisors ({leaveSupervisors.length})</span>
              </button>
              
              {/* NEW: Heads of Department Tab */}
              <button 
                className={`tab-button ${activeTab === 'hods' ? 'active' : ''}`}
                onClick={() => setActiveTab('hods')}
              >
                <div className="tab-icon">
                  <FontAwesomeIcon icon={faChalkboardTeacher} />
                </div>
                <span>Heads of Department ({headsOfDepartment.length})</span>
              </button>
              
              {/* NEW: CRM Approvers Tab */}
              <button 
                className={`tab-button ${activeTab === 'crm_approvers' ? 'active' : ''}`}
                onClick={() => setActiveTab('crm_approvers')}
              >
                <div className="tab-icon">
                  <FontAwesomeIcon icon={faUserCheck} />
                </div>
                <span>CRM Approvers ({crmApprovers.length})</span>
              </button>

              {/* NEW: KYC Approvers Tab */}
              <button
                className={`tab-button ${activeTab === 'kyc_approvers' ? 'active' : ''}`}
                onClick={() => setActiveTab('kyc_approvers')}
              >
                <div className="tab-icon">
                  <FontAwesomeIcon icon={faIdBadge} />
                </div>
                <span>KYC Approvers ({kycApprovers.length})</span>
              </button>

              {/* NEW: Credit Approvers Tab */}
              <button 
                className={`tab-button ${activeTab === 'credit_approvers' ? 'active' : ''}`}
                onClick={() => setActiveTab('credit_approvers')}
              >
                <div className="tab-icon">
                  <FontAwesomeIcon icon={faHandHoldingDollar} />
                </div>
                <span>Credit Approvers ({creditApprovers.length})</span>
              </button>
              
              {/* NEW: Financial Approvers Tab */}
              <button 
                className={`tab-button ${activeTab === 'financial_approvers' ? 'active' : ''}`}
                onClick={() => setActiveTab('financial_approvers')}
              >
                <div className="tab-icon">
                  <FontAwesomeIcon icon={faUserShield} />
                </div>
                <span>Financial Approvers ({financialApprovers.length})</span>
              </button>
              
              {/* NEW: Delivery Reviewers Tab */}
              <button 
                className={`tab-button ${activeTab === 'delivery_reviewers' ? 'active' : ''}`}
                onClick={() => setActiveTab('delivery_reviewers')}
              >
                <div className="tab-icon">
                  <FontAwesomeIcon icon={faTruck} />
                </div>
                <span>Delivery Reviewers ({deliveryReviewers.length})</span>
              </button>

              {/* NEW: Legal Approvers Tab (bunker contracts module) */}
              <button 
                className={`tab-button ${activeTab === 'legal_approvers' ? 'active' : ''}`}
                onClick={() => setActiveTab('legal_approvers')}
              >
                <div className="tab-icon">
                  <FontAwesomeIcon icon={faGavel} />
                </div>
                <span>Legal Approvers ({legalApprovers.length})</span>
              </button>
              
              {/* Deactivated Users tab */}
              <button 
                className={`tab-button deactivated-tab ${activeTab === 'deactivated' ? 'active' : ''}`}
                  onClick={() => setActiveTab('deactivated')}
                >
                  <div className="tab-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
                      <path d="M7 12h10v-2H7z"/>
                    </svg>
                  </div>
                  <span>Deactivated Users ({deactivatedCount})</span>
                </button>
            </div>
          </div>
          )}

          {/* System Data Tabs - Only show in System Data mode */}
          {adminMode === 'system-data' && (
            <div className="hms-tabs">
              <div className="tabs-container">
                <button 
                  className={`tab-button ${systemDataTab === 'offices' ? 'active' : ''}`}
                  onClick={() => setSystemDataTab('offices')}
                >
                  <div className="tab-icon">
                    <FontAwesomeIcon icon={faBuilding} />
                  </div>
                  <span>Offices</span>
                </button>
                <button 
                  className={`tab-button ${systemDataTab === 'departments' ? 'active' : ''}`}
                  onClick={() => setSystemDataTab('departments')}
                >
                  <div className="tab-icon">
                    <FontAwesomeIcon icon={faSitemap} />
                  </div>
                  <span>Departments</span>
                </button>
                <button 
                  className={`tab-button ${systemDataTab === 'positions' ? 'active' : ''}`}
                  onClick={() => setSystemDataTab('positions')}
                >
                  <div className="tab-icon">
                    <FontAwesomeIcon icon={faBriefcase} />
                  </div>
                  <span>Positions</span>
                </button>
                <button 
                  className={`tab-button ${systemDataTab === 'units' ? 'active' : ''}`}
                  onClick={() => setSystemDataTab('units')}
                >
                  <div className="tab-icon">
                    <FontAwesomeIcon icon={faRulerCombined} />
                  </div>
                  <span>Units</span>
                </button>
                <button 
                  className={`tab-button ${systemDataTab === 'branches' ? 'active' : ''}`}
                  onClick={() => setSystemDataTab('branches')}
                >
                  <div className="tab-icon">
                    <FontAwesomeIcon icon={faGlobe} />
                  </div>
                  <span>IMT Branches</span>
                </button>
                <button
                  className={`tab-button ${systemDataTab === 'ports' ? 'active' : ''}`}
                  onClick={() => setSystemDataTab('ports')}
                >
                  <div className="tab-icon">
                    <FontAwesomeIcon icon={faAnchor} />
                  </div>
                  <span>Ports</span>
                </button>
                <button
                  className={`tab-button ${systemDataTab === 'power_of_attorney_roles' ? 'active' : ''}`}
                  onClick={() => setSystemDataTab('power_of_attorney_roles')}
                >
                  <div className="tab-icon">
                    <FontAwesomeIcon icon={faFileSignature} />
                  </div>
                  <span>Power of Attorney Roles</span>
                </button>
              </div>
            </div>
          )}

          {/* Superuser Section - Only show in Employee Management mode */}
          {adminMode === 'employees' && activeTab === 'admins' && superusers.length > 0 && (
            <>
            <div className="superuser-section">
              <h3>System Administrators ({superusers.length})</h3>
              <table className="superuser-table">
                <thead>
                  <tr>
                    <th>IMT ID</th>
                    <th>Avatar</th>
                    <th>Name</th>
                    <th>Position</th>
                    <th>Department</th>
                    <th>Office</th>
                    <th>Last Login</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {superusers.slice(0, superusersDisplayLimit).map(admin => (
                    <tr 
                      key={admin.id} 
                      className={`superuser-row ${!admin.is_active ? 'inactive' : ''}`}
                      onClick={() => handleViewEmployee(admin)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{admin.hms_employee_id || 'Not assigned'}</td>
                      <td className="avatar-cell">
                        {admin.avatar_photo ? (
                          <img 
                            src={admin.avatar_photo} 
                            alt={admin.display_name}
                            className="table-avatar"
                          />
                        ) : (
                          <div className="table-avatar-initials">
                            {admin.first_name?.[0] || admin.email?.[0] || '?'}{admin.last_name?.[0] || ''}
                          </div>
                        )}
                      </td>
                      <td><strong>{admin.display_name}</strong></td>
                      <td>{admin.position?.name || 'Administrator'}</td>
                      <td>{admin.department?.name || 'Not set'}</td>
                      <td>{admin.office?.name || 'Not set'}</td>
                      <td>{formatLastLogin(admin.last_login)}</td>
                      <td>
                        <span className={`status-badge ${admin.is_active ? 'active' : 'inactive'}`}>
                          {admin.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="actions-cell">
                        <div className="action-dropdown" onClick={(e) => e.stopPropagation()} data-dropdown-id={`super-${admin.id}`}>
                          <button 
                            className="gear-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDropdown(`super-${admin.id}`);
                            }}
                          >
                            Actions
                          </button>
                          {activeDropdown === `super-${admin.id}` && (
            <div className="dropdown-menu">
              {admin.is_active && (
                <>
                  <button onClick={() => handleViewEmployee(admin)}>View Details</button>
                  <button onClick={() => handleResetPassword(admin)}>Reset Password</button>
                  <button onClick={() => handleViewLeave(admin)}>View Leave</button>
                  <button onClick={() => handleAssignRole(admin)}>Assign Role</button>
                  <hr />
                </>
              )}
              {admin.is_active ? (
                <button 
                  className="danger"
                  onClick={() => handleDeactivateEmployee(admin.id, admin.display_name)}
                >
                  Deactivate
                </button>
              ) : (
                <button 
                  className="success"
                  onClick={() => handleActivateEmployee(admin.id, admin.display_name)}
                >
                  Activate
                </button>
              )}
            </div>
          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {superusers.length > superusersDisplayLimit && (
              <div className="show-more-container">
                <button 
                  className="btn btn-show-more"
                  onClick={() => setSuperusersDisplayLimit(prev => prev + 20)}
                >
                  Show More ({superusers.length - superusersDisplayLimit} remaining)
                </button>
              </div>
            )}
            </>
          )}
          
          {/* NEW: Supervisors Section - Only show in Employee Management mode */}
          {adminMode === 'employees' && activeTab === 'supervisors' && (
            <>
            <div className="superuser-section">
              <h3>Supervisors ({supervisors.length})</h3>
              {supervisors && supervisors.length > 0 ? (
                <>
                <table className="superuser-table">
                  <thead>
                    <tr>
                      <th>IMT ID</th>
                      <th>Avatar</th>
                      <th>Name</th>
                      <th>Position</th>
                      <th>Department</th>
                      <th>Office</th>
                      <th>Users Assigned</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {supervisors.slice(0, supervisorsDisplayLimit).map((supervisor) => (
                    <tr 
                      key={supervisor.id}
                      className={`superuser-row ${!supervisor.is_active ? 'inactive' : ''}`}
                      onClick={() => handleViewEmployee(supervisor)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{supervisor.hms_employee_id || 'Not assigned'}</td>
                      <td className="avatar-cell">
                        {supervisor.avatar_photo ? (
                          <img 
                            src={supervisor.avatar_photo} 
                            alt={supervisor.display_name}
                            className="table-avatar"
                          />
                        ) : (
                          <div className="table-avatar-initials">
                            {supervisor.first_name?.[0] || supervisor.email?.[0] || '?'}{supervisor.last_name?.[0] || ''}
                          </div>
                        )}
                      </td>
                      <td>{supervisor.display_name}</td>
                      <td>{supervisor.position?.name || 'Not set'}</td>
                      <td>{supervisor.department?.name || 'Not set'}</td>
                      <td>{supervisor.office?.name || 'Not set'}</td>
                      <td>
                        <span className="users-count-badge">
                          {supervisor.supervised_users_count || 0}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${supervisor.is_active ? 'active' : 'inactive'}`}>
                          {supervisor.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="actions-cell">
                        <div className="action-dropdown" onClick={(e) => e.stopPropagation()} data-dropdown-id={`supervisor-${supervisor.id}`}>
                          <button 
                            className="gear-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDropdown(`supervisor-${supervisor.id}`);
                            }}
                          >
                            Actions
                          </button>
                          {activeDropdown === `supervisor-${supervisor.id}` && (
                            <div className="dropdown-menu">
                              {supervisor.is_active && (
                                <>
                                  <button onClick={() => handleViewEmployee(supervisor)}>View Details</button>
                                  <button onClick={() => handleResetPassword(supervisor)}>Reset Password</button>
                                  <button onClick={() => handleViewLeave(supervisor)}>View Leave</button>
                                  <button onClick={() => handleAssignRole(supervisor)}>Assign Role</button>
                                  <button onClick={() => handleAssignUsers(supervisor)} className="highlight">Assign Users</button>
                                  <hr />
                                </>
                              )}
                              {supervisor.is_active ? (
                                <button 
                                  className="danger"
                                  onClick={() => handleDeactivateEmployee(supervisor.id, supervisor.display_name)}
                                >
                                  Deactivate
                                </button>
                              ) : (
                                <button 
                                  className="success"
                                  onClick={() => handleActivateEmployee(supervisor.id, supervisor.display_name)}
                                >
                                  Activate
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                    ))}
                  </tbody>
                </table>
                {supervisors.length > supervisorsDisplayLimit && (
                  <div className="show-more-container">
                    <button 
                      className="btn btn-show-more"
                      onClick={() => setSupervisorsDisplayLimit(prev => prev + 20)}
                    >
                      Show More ({supervisors.length - supervisorsDisplayLimit} remaining)
                    </button>
                  </div>
                )}
                </>
              ) : (
                <div className="no-deactivated-users">
                  <FontAwesomeIcon icon={faUserTie} style={{ fontSize: '64px', color: '#cbd5e0' }} />
                  <p>No supervisors found</p>
                </div>
              )}
            </div>
            </>
          )}
          
          {/* Leave Supervisors Section */}
          {adminMode === 'employees' && activeTab === 'leave_supervisors' && (
            <>
            <div className="superuser-section">
              <h3>Leave Supervisors ({leaveSupervisors.length})</h3>
              {leaveSupervisors && leaveSupervisors.length > 0 ? (
                <>
                <table className="superuser-table">
                  <thead>
                    <tr>
                      <th>IMT ID</th>
                      <th>Avatar</th>
                      <th>Name</th>
                      <th>Position</th>
                      <th>Department</th>
                      <th>Office</th>
                      <th>Access</th>
                      <th>Users Assigned</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaveSupervisors.slice(0, leaveSupervisorsDisplayLimit).map((ls) => (
                    <tr 
                      key={ls.id}
                      className={`superuser-row ${!ls.is_active ? 'inactive' : ''}`}
                      onClick={() => handleViewEmployee(ls)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{ls.hms_employee_id || 'Not assigned'}</td>
                      <td className="avatar-cell">
                        {ls.avatar_photo ? (
                          <img src={ls.avatar_photo} alt={ls.display_name} className="table-avatar" />
                        ) : (
                          <div className="table-avatar-initials">
                            {ls.first_name?.[0] || ls.email?.[0] || '?'}{ls.last_name?.[0] || ''}
                          </div>
                        )}
                      </td>
                      <td>{ls.display_name}</td>
                      <td>{ls.position?.name || 'Not set'}</td>
                      <td>{ls.department?.name || 'Not set'}</td>
                      <td>{ls.office?.name || 'Not set'}</td>
                      <td>
                        <span className={`status-badge ${ls.leave_supervisor_view_only ? 'inactive' : 'active'}`}>
                          {ls.leave_supervisor_view_only ? 'View Only' : 'Full Access'}
                        </span>
                      </td>
                      <td>
                        <span className="users-count-badge">
                          {ls.leave_supervised_users_count || 0}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${ls.is_active ? 'active' : 'inactive'}`}>
                          {ls.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="actions-cell">
                        <div className="action-dropdown" onClick={(e) => e.stopPropagation()} data-dropdown-id={`leave-sup-${ls.id}`}>
                          <button 
                            className="gear-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDropdown(`leave-sup-${ls.id}`);
                            }}
                          >
                            Actions
                          </button>
                          {activeDropdown === `leave-sup-${ls.id}` && (
                            <div className="dropdown-menu">
                              {ls.is_active && (
                                <>
                                  <button onClick={() => handleViewEmployee(ls)}>View Details</button>
                                  <button onClick={() => handleResetPassword(ls)}>Reset Password</button>
                                  <button onClick={() => handleViewLeave(ls)}>View Leave</button>
                                  <button onClick={() => handleAssignRole(ls)}>Assign Role</button>
                                  <button onClick={() => handleAssignUsersToLeaveSupervisor(ls)} className="highlight">Assign Users</button>
                                  <hr />
                                </>
                              )}
                              {ls.is_active ? (
                                <button className="danger" onClick={() => handleDeactivateEmployee(ls.id, ls.display_name)}>Deactivate</button>
                              ) : (
                                <button className="success" onClick={() => handleActivateEmployee(ls.id, ls.display_name)}>Activate</button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                    ))}
                  </tbody>
                </table>
                {leaveSupervisors.length > leaveSupervisorsDisplayLimit && (
                  <div className="show-more-container">
                    <button 
                      className="btn btn-show-more"
                      onClick={() => setLeaveSupervisorsDisplayLimit(prev => prev + 20)}
                    >
                      Show More ({leaveSupervisors.length - leaveSupervisorsDisplayLimit} remaining)
                    </button>
                  </div>
                )}
                </>
              ) : (
                <div className="no-deactivated-users">
                  <FontAwesomeIcon icon={faCalendarCheck} style={{ fontSize: '64px', color: '#cbd5e0' }} />
                  <p>No leave supervisors found</p>
                </div>
              )}
            </div>
            </>
          )}
          
          {/* NEW: Heads of Department Section - Only show in Employee Management mode */}
          {adminMode === 'employees' && activeTab === 'hods' && (
            <>
            <div className="superuser-section">
              <h3>Heads of Department ({headsOfDepartment.length})</h3>
              {headsOfDepartment && headsOfDepartment.length > 0 ? (
                <>
                <table className="superuser-table">
                  <thead>
                    <tr>
                      <th>IMT ID</th>
                      <th>Avatar</th>
                      <th>Name</th>
                      <th>Position</th>
                      <th>Head of Department</th>
                      <th>Office</th>
                      <th>Users Assigned</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {headsOfDepartment.slice(0, hodsDisplayLimit).map((hod) => (
                    <tr 
                      key={hod.id}
                      className={`superuser-row ${!hod.is_active ? 'inactive' : ''}`}
                      onClick={() => handleViewEmployee(hod)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{hod.hms_employee_id || 'Not assigned'}</td>
                      <td className="avatar-cell">
                        {hod.avatar_photo ? (
                          <img 
                            src={hod.avatar_photo} 
                            alt={hod.display_name}
                            className="table-avatar"
                          />
                        ) : (
                          <div className="table-avatar-initials">
                            {hod.first_name?.[0] || hod.email?.[0] || '?'}{hod.last_name?.[0] || ''}
                          </div>
                        )}
                      </td>
                      <td>{hod.display_name}</td>
                      <td>{hod.position?.name || 'Not set'}</td>
                      <td>{hod.supervised_department?.name || 'Not set'}</td>
                      <td>{hod.office?.name || 'Not set'}</td>
                      <td>
                        <span className="users-count-badge">
                          {hod.hod_supervised_users_count || 0}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${hod.is_active ? 'active' : 'inactive'}`}>
                          {hod.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="actions-cell">
                        <div className="action-dropdown" onClick={(e) => e.stopPropagation()} data-dropdown-id={`hod-${hod.id}`}>
                          <button 
                            className="gear-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDropdown(`hod-${hod.id}`);
                            }}
                          >
                            Actions
                          </button>
                          {activeDropdown === `hod-${hod.id}` && (
                            <div className="dropdown-menu">
                              {hod.is_active && (
                                <>
                                  <button onClick={() => handleViewEmployee(hod)}>View Details</button>
                                  <button onClick={() => handleResetPassword(hod)}>Reset Password</button>
                                  <button onClick={() => handleViewLeave(hod)}>View Leave</button>
                                  <button onClick={() => handleAssignRole(hod)}>Assign Role</button>
                                  <button onClick={() => handleAssignUsersToHOD(hod)} className="highlight">Assign Users</button>
                                  <hr />
                                </>
                              )}
                              {hod.is_active ? (
                                <button 
                                  className="danger"
                                  onClick={() => handleDeactivateEmployee(hod.id, hod.display_name)}
                                >
                                  Deactivate
                                </button>
                              ) : (
                                <button 
                                  className="success"
                                  onClick={() => handleActivateEmployee(hod.id, hod.display_name)}
                                >
                                  Activate
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                    ))}
                  </tbody>
                </table>
                {headsOfDepartment.length > hodsDisplayLimit && (
                  <div className="show-more-container">
                    <button 
                      className="btn btn-show-more"
                      onClick={() => setHodsDisplayLimit(prev => prev + 20)}
                    >
                      Show More ({headsOfDepartment.length - hodsDisplayLimit} remaining)
                    </button>
                  </div>
                )}
                </>
              ) : (
                <div className="no-deactivated-users">
                  <FontAwesomeIcon icon={faChalkboardTeacher} style={{ fontSize: '64px', color: '#cbd5e0' }} />
                  <p>No heads of department found</p>
                </div>
              )}
            </div>
            </>
          )}

          {/* CRM Approvers Tab Content */}
          {adminMode === 'employees' && activeTab === 'crm_approvers' && (
            <>
            <div className="superuser-section">
              <h3>CRM Approvers ({crmApprovers.length})</h3>
              {crmApprovers && crmApprovers.length > 0 ? (
                <>
                <table className="superuser-table">
                  <thead>
                    <tr>
                      <th>IMT ID</th>
                      <th>Avatar</th>
                      <th>Name</th>
                      <th>Position</th>
                      <th>Department</th>
                      <th>Office</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {crmApprovers.slice(0, crmApproversDisplayLimit).map((approver) => (
                    <tr 
                      key={approver.id}
                      className={`superuser-row ${!approver.is_active ? 'inactive' : ''}`}
                      onClick={() => handleViewEmployee(approver)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{approver.hms_employee_id || 'Not assigned'}</td>
                      <td className="avatar-cell">
                        {approver.avatar_photo ? (
                          <img 
                            src={approver.avatar_photo} 
                            alt={approver.display_name}
                            className="table-avatar"
                          />
                        ) : (
                          <div className="table-avatar-initials">
                            {approver.first_name?.[0] || approver.email?.[0] || '?'}{approver.last_name?.[0] || ''}
                          </div>
                        )}
                      </td>
                      <td>{approver.display_name}</td>
                      <td>{approver.position?.name || 'Not set'}</td>
                      <td>{approver.department?.name || 'Not set'}</td>
                      <td>{approver.office?.name || 'Not set'}</td>
                      <td>
                        <span className={`status-badge ${approver.is_active ? 'active' : 'inactive'}`}>
                          {approver.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="actions-cell">
                        <div className="action-dropdown" onClick={(e) => e.stopPropagation()} data-dropdown-id={`crm-${approver.id}`}>
                          <button 
                            className="gear-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDropdown(`crm-${approver.id}`);
                            }}
                          >
                            Actions
                          </button>
                          {activeDropdown === `crm-${approver.id}` && (
                            <div className="dropdown-menu">
                              {approver.is_active && (
                                <>
                                  <button onClick={() => handleViewEmployee(approver)}>View Details</button>
                                  <button onClick={() => handleResetPassword(approver)}>Reset Password</button>
                                  <button onClick={() => handleViewLeave(approver)}>View Leave</button>
                                  <button onClick={() => handleAssignRole(approver)}>Assign Role</button>
                                  <hr />
                                </>
                              )}
                              {approver.is_active ? (
                                <button 
                                  className="danger"
                                  onClick={() => handleDeactivateEmployee(approver.id, approver.display_name)}
                                >
                                  Deactivate
                                </button>
                              ) : (
                                <button 
                                  className="success"
                                  onClick={() => handleActivateEmployee(approver.id, approver.display_name)}
                                >
                                  Activate
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                    ))}
                  </tbody>
                </table>
                {crmApprovers.length > crmApproversDisplayLimit && (
                  <div className="show-more-container">
                    <button 
                      className="btn btn-show-more"
                      onClick={() => setCrmApproversDisplayLimit(prev => prev + 20)}
                    >
                      Show More ({crmApprovers.length - crmApproversDisplayLimit} remaining)
                    </button>
                  </div>
                )}
                </>
              ) : (
                <div className="no-deactivated-users">
                  <FontAwesomeIcon icon={faUserCheck} style={{ fontSize: '64px', color: '#cbd5e0' }} />
                  <p>No CRM approvers found</p>
                </div>
              )}
            </div>
            </>
          )}

          {/* KYC Approvers Tab Content */}
          {adminMode === 'employees' && activeTab === 'kyc_approvers' && (
            <>
            <div className="superuser-section">
              <h3>KYC Approvers ({kycApprovers.length})</h3>
              {kycApprovers && kycApprovers.length > 0 ? (
                <>
                <table className="superuser-table">
                  <thead>
                    <tr>
                      <th>IMT ID</th>
                      <th>Avatar</th>
                      <th>Name</th>
                      <th>Position</th>
                      <th>Department</th>
                      <th>Office</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {kycApprovers.slice(0, kycApproversDisplayLimit).map((approver) => (
                    <tr
                      key={approver.id}
                      className={`superuser-row ${!approver.is_active ? 'inactive' : ''}`}
                      onClick={() => handleViewEmployee(approver)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{approver.hms_employee_id || 'Not assigned'}</td>
                      <td className="avatar-cell">
                        {approver.avatar_photo ? (
                          <img
                            src={approver.avatar_photo}
                            alt={approver.display_name}
                            className="table-avatar"
                          />
                        ) : (
                          <div className="table-avatar-initials">
                            {approver.first_name?.[0] || approver.email?.[0] || '?'}{approver.last_name?.[0] || ''}
                          </div>
                        )}
                      </td>
                      <td>{approver.display_name}</td>
                      <td>{approver.position?.name || 'Not set'}</td>
                      <td>{approver.department?.name || 'Not set'}</td>
                      <td>{approver.office?.name || 'Not set'}</td>
                      <td>
                        <span className={`status-badge ${approver.is_active ? 'active' : 'inactive'}`}>
                          {approver.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="actions-cell">
                        <div className="action-dropdown" onClick={(e) => e.stopPropagation()} data-dropdown-id={`kyc-${approver.id}`}>
                          <button
                            className="gear-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDropdown(`kyc-${approver.id}`);
                            }}
                          >
                            Actions
                          </button>
                          {activeDropdown === `kyc-${approver.id}` && (
                            <div className="dropdown-menu">
                              {approver.is_active && (
                                <>
                                  <button onClick={() => handleViewEmployee(approver)}>View Details</button>
                                  <button onClick={() => handleResetPassword(approver)}>Reset Password</button>
                                  <button onClick={() => handleViewLeave(approver)}>View Leave</button>
                                  <button onClick={() => handleAssignRole(approver)}>Assign Role</button>
                                  <hr />
                                </>
                              )}
                              {approver.is_active ? (
                                <button
                                  className="danger"
                                  onClick={() => handleDeactivateEmployee(approver.id, approver.display_name)}
                                >
                                  Deactivate
                                </button>
                              ) : (
                                <button
                                  className="success"
                                  onClick={() => handleActivateEmployee(approver.id, approver.display_name)}
                                >
                                  Activate
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                    ))}
                  </tbody>
                </table>
                {kycApprovers.length > kycApproversDisplayLimit && (
                  <div className="show-more-container">
                    <button
                      className="btn btn-show-more"
                      onClick={() => setKycApproversDisplayLimit(prev => prev + 20)}
                    >
                      Show More ({kycApprovers.length - kycApproversDisplayLimit} remaining)
                    </button>
                  </div>
                )}
                </>
              ) : (
                <div className="no-deactivated-users">
                  <FontAwesomeIcon icon={faIdBadge} style={{ fontSize: '64px', color: '#cbd5e0' }} />
                  <p>No KYC approvers found</p>
                </div>
              )}
            </div>
            </>
          )}

          {/* Credit Approvers Tab Content */}
          {adminMode === 'employees' && activeTab === 'credit_approvers' && (
            <>
            <div className="superuser-section">
              <h3>Credit Approvers ({creditApprovers.length})</h3>
              {creditApprovers && creditApprovers.length > 0 ? (
                <>
                <table className="superuser-table">
                  <thead>
                    <tr>
                      <th>IMT ID</th>
                      <th>Avatar</th>
                      <th>Name</th>
                      <th>Position</th>
                      <th>Department</th>
                      <th>Office</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {creditApprovers.slice(0, creditApproversDisplayLimit).map((approver) => (
                    <tr 
                      key={approver.id}
                      className={`superuser-row ${!approver.is_active ? 'inactive' : ''}`}
                      onClick={() => handleViewEmployee(approver)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{approver.hms_employee_id || 'Not assigned'}</td>
                      <td className="avatar-cell">
                        {approver.avatar_photo ? (
                          <img 
                            src={approver.avatar_photo} 
                            alt={approver.display_name}
                            className="table-avatar"
                          />
                        ) : (
                          <div className="table-avatar-initials">
                            {approver.first_name?.[0] || approver.email?.[0] || '?'}{approver.last_name?.[0] || ''}
                          </div>
                        )}
                      </td>
                      <td>{approver.display_name}</td>
                      <td>{approver.position?.name || 'Not set'}</td>
                      <td>{approver.department?.name || 'Not set'}</td>
                      <td>{approver.office?.name || 'Not set'}</td>
                      <td>
                        <span className={`status-badge ${approver.is_active ? 'active' : 'inactive'}`}>
                          {approver.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="actions-cell">
                        <div className="action-dropdown" onClick={(e) => e.stopPropagation()} data-dropdown-id={`credit-${approver.id}`}>
                          <button 
                            className="gear-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDropdown(`credit-${approver.id}`);
                            }}
                          >
                            Actions
                          </button>
                          {activeDropdown === `credit-${approver.id}` && (
                            <div className="dropdown-menu">
                              {approver.is_active && (
                                <>
                                  <button onClick={() => handleViewEmployee(approver)}>View Details</button>
                                  <button onClick={() => handleResetPassword(approver)}>Reset Password</button>
                                  <button onClick={() => handleViewLeave(approver)}>View Leave</button>
                                  <button onClick={() => handleAssignRole(approver)}>Assign Role</button>
                                  <hr />
                                </>
                              )}
                              {approver.is_active ? (
                                <button 
                                  className="danger"
                                  onClick={() => handleDeactivateEmployee(approver.id, approver.display_name)}
                                >
                                  Deactivate
                                </button>
                              ) : (
                                <button 
                                  className="success"
                                  onClick={() => handleActivateEmployee(approver.id, approver.display_name)}
                                >
                                  Activate
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                    ))}
                  </tbody>
                </table>
                {creditApprovers.length > creditApproversDisplayLimit && (
                  <div className="show-more-container">
                    <button 
                      className="btn btn-show-more"
                      onClick={() => setCreditApproversDisplayLimit(prev => prev + 20)}
                    >
                      Show More ({creditApprovers.length - creditApproversDisplayLimit} remaining)
                    </button>
                  </div>
                )}
                </>
              ) : (
                <div className="no-deactivated-users">
                  <FontAwesomeIcon icon={faHandHoldingDollar} style={{ fontSize: '64px', color: '#cbd5e0' }} />
                  <p>No credit approvers found</p>
                </div>
              )}
            </div>
            </>
          )}

          {/* Financial Approvers Tab Content */}
          {adminMode === 'employees' && activeTab === 'financial_approvers' && (
            <>
            <div className="superuser-section">
              <h3>Financial Approvers ({financialApprovers.length})</h3>
              {financialApprovers && financialApprovers.length > 0 ? (
                <>
                <table className="superuser-table">
                  <thead>
                    <tr>
                      <th>IMT ID</th>
                      <th>Avatar</th>
                      <th>Name</th>
                      <th>Position</th>
                      <th>Department</th>
                      <th>Office</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {financialApprovers.slice(0, financialApproversDisplayLimit).map((approver) => (
                    <tr 
                      key={approver.id}
                      className={`superuser-row ${!approver.is_active ? 'inactive' : ''}`}
                      onClick={() => handleViewEmployee(approver)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{approver.hms_employee_id || 'Not assigned'}</td>
                      <td className="avatar-cell">
                        {approver.avatar_photo ? (
                          <img 
                            src={approver.avatar_photo} 
                            alt={approver.display_name}
                            className="table-avatar"
                          />
                        ) : (
                          <div className="table-avatar-initials">
                            {approver.first_name?.[0] || approver.email?.[0] || '?'}{approver.last_name?.[0] || ''}
                          </div>
                        )}
                      </td>
                      <td>{approver.display_name}</td>
                      <td>{approver.position?.name || 'Not set'}</td>
                      <td>{approver.department?.name || 'Not set'}</td>
                      <td>{approver.office?.name || 'Not set'}</td>
                      <td>
                        <span className={`status-badge ${approver.is_active ? 'active' : 'inactive'}`}>
                          {approver.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="actions-cell">
                        <div className="action-dropdown" onClick={(e) => e.stopPropagation()} data-dropdown-id={`financial-${approver.id}`}>
                          <button 
                            className="gear-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDropdown(`financial-${approver.id}`);
                            }}
                          >
                            Actions
                          </button>
                          {activeDropdown === `financial-${approver.id}` && (
                            <div className="dropdown-menu">
                              {approver.is_active && (
                                <>
                                  <button onClick={() => handleViewEmployee(approver)}>View Details</button>
                                  <button onClick={() => handleResetPassword(approver)}>Reset Password</button>
                                  <button onClick={() => handleViewLeave(approver)}>View Leave</button>
                                  <button onClick={() => handleAssignRole(approver)}>Assign Role</button>
                                  <hr />
                                </>
                              )}
                              {approver.is_active ? (
                                <button 
                                  className="danger"
                                  onClick={() => handleDeactivateEmployee(approver.id, approver.display_name)}
                                >
                                  Deactivate
                                </button>
                              ) : (
                                <button 
                                  className="success"
                                  onClick={() => handleActivateEmployee(approver.id, approver.display_name)}
                                >
                                  Activate
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                    ))}
                  </tbody>
                </table>
                {financialApprovers.length > financialApproversDisplayLimit && (
                  <div className="show-more-container">
                    <button 
                      className="btn btn-show-more"
                      onClick={() => setFinancialApproversDisplayLimit(prev => prev + 20)}
                    >
                      Show More ({financialApprovers.length - financialApproversDisplayLimit} remaining)
                    </button>
                  </div>
                )}
                </>
              ) : (
                <div className="no-deactivated-users">
                  <FontAwesomeIcon icon={faUserShield} style={{ fontSize: '64px', color: '#cbd5e0' }} />
                  <p>No financial approvers found</p>
                </div>
              )}
            </div>
            </>
          )}

          {/* Delivery Reviewers Tab Content */}
          {adminMode === 'employees' && activeTab === 'delivery_reviewers' && (
            <>
            <div className="superuser-section">
              <h3>Delivery Reviewers ({deliveryReviewers.length})</h3>
              {deliveryReviewers && deliveryReviewers.length > 0 ? (
                <>
                <table className="superuser-table">
                  <thead>
                    <tr>
                      <th>IMT ID</th>
                      <th>Avatar</th>
                      <th>Name</th>
                      <th>Position</th>
                      <th>Department</th>
                      <th>Office</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deliveryReviewers.slice(0, deliveryReviewersDisplayLimit).map((approver) => (
                    <tr 
                      key={approver.id}
                      className={`superuser-row ${!approver.is_active ? 'inactive' : ''}`}
                      onClick={() => handleViewEmployee(approver)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{approver.hms_employee_id || 'Not assigned'}</td>
                      <td className="avatar-cell">
                        {approver.avatar_photo ? (
                          <img 
                            src={approver.avatar_photo} 
                            alt={approver.display_name}
                            className="table-avatar"
                          />
                        ) : (
                          <div className="table-avatar-initials">
                            {approver.first_name?.[0] || approver.email?.[0] || '?'}{approver.last_name?.[0] || ''}
                          </div>
                        )}
                      </td>
                      <td>{approver.display_name}</td>
                      <td>{approver.position?.name || 'Not set'}</td>
                      <td>{approver.department?.name || 'Not set'}</td>
                      <td>{approver.office?.name || 'Not set'}</td>
                      <td>
                        <span className={`status-badge ${approver.is_active ? 'active' : 'inactive'}`}>
                          {approver.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="actions-cell">
                        <div className="action-dropdown" onClick={(e) => e.stopPropagation()} data-dropdown-id={`delivery-${approver.id}`}>
                          <button 
                            className="gear-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDropdown(`delivery-${approver.id}`);
                            }}
                          >
                            Actions
                          </button>
                          {activeDropdown === `delivery-${approver.id}` && (
                            <div className="dropdown-menu">
                              {approver.is_active && (
                                <>
                                  <button onClick={() => handleViewEmployee(approver)}>View Details</button>
                                  <button onClick={() => handleResetPassword(approver)}>Reset Password</button>
                                  <button onClick={() => handleViewLeave(approver)}>View Leave</button>
                                  <button onClick={() => handleAssignRole(approver)}>Assign Role</button>
                                  <hr />
                                </>
                              )}
                              {approver.is_active ? (
                                <button 
                                  className="danger"
                                  onClick={() => handleDeactivateEmployee(approver.id, approver.display_name)}
                                >
                                  Deactivate
                                </button>
                              ) : (
                                <button 
                                  className="success"
                                  onClick={() => handleActivateEmployee(approver.id, approver.display_name)}
                                >
                                  Activate
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                    ))}
                  </tbody>
                </table>
                {deliveryReviewers.length > deliveryReviewersDisplayLimit && (
                  <div className="show-more-container">
                    <button 
                      className="btn btn-show-more"
                      onClick={() => setDeliveryReviewersDisplayLimit(prev => prev + 20)}
                    >
                      Show More ({deliveryReviewers.length - deliveryReviewersDisplayLimit} remaining)
                    </button>
                  </div>
                )}
                </>
              ) : (
                <div className="no-deactivated-users">
                  <FontAwesomeIcon icon={faTruck} style={{ fontSize: '64px', color: '#cbd5e0' }} />
                  <p>No delivery approvers found</p>
                </div>
              )}
            </div>
            </>
          )}

          {/* Legal Approvers Tab Content (bunker contracts module) */}
          {adminMode === 'employees' && activeTab === 'legal_approvers' && (
            <>
            <div className="superuser-section">
              <h3>Legal Approvers ({legalApprovers.length})</h3>
              {legalApprovers && legalApprovers.length > 0 ? (
                <>
                <table className="superuser-table">
                  <thead>
                    <tr>
                      <th>IMT ID</th>
                      <th>Avatar</th>
                      <th>Name</th>
                      <th>Position</th>
                      <th>Department</th>
                      <th>Office</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {legalApprovers.slice(0, legalApproversDisplayLimit).map((approver) => (
                    <tr 
                      key={approver.id}
                      className={`superuser-row ${!approver.is_active ? 'inactive' : ''}`}
                      onClick={() => handleViewEmployee(approver)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{approver.hms_employee_id || 'Not assigned'}</td>
                      <td className="avatar-cell">
                        {approver.avatar_photo ? (
                          <img 
                            src={approver.avatar_photo} 
                            alt={approver.display_name}
                            className="table-avatar"
                          />
                        ) : (
                          <div className="table-avatar-initials">
                            {approver.first_name?.[0] || approver.email?.[0] || '?'}{approver.last_name?.[0] || ''}
                          </div>
                        )}
                      </td>
                      <td>{approver.display_name}</td>
                      <td>{approver.position?.name || 'Not set'}</td>
                      <td>{approver.department?.name || 'Not set'}</td>
                      <td>{approver.office?.name || 'Not set'}</td>
                      <td>
                        <span className={`status-badge ${approver.is_active ? 'active' : 'inactive'}`}>
                          {approver.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="actions-cell">
                        <div className="action-dropdown" onClick={(e) => e.stopPropagation()} data-dropdown-id={`legal-${approver.id}`}>
                          <button 
                            className="gear-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDropdown(`legal-${approver.id}`);
                            }}
                          >
                            Actions
                          </button>
                          {activeDropdown === `legal-${approver.id}` && (
                            <div className="dropdown-menu">
                              {approver.is_active && (
                                <>
                                  <button onClick={() => handleViewEmployee(approver)}>View Details</button>
                                  <button onClick={() => handleResetPassword(approver)}>Reset Password</button>
                                  <button onClick={() => handleViewLeave(approver)}>View Leave</button>
                                  <button onClick={() => handleAssignRole(approver)}>Assign Role</button>
                                  <hr />
                                </>
                              )}
                              {approver.is_active ? (
                                <button 
                                  className="danger"
                                  onClick={() => handleDeactivateEmployee(approver.id, approver.display_name)}
                                >
                                  Deactivate
                                </button>
                              ) : (
                                <button 
                                  className="success"
                                  onClick={() => handleActivateEmployee(approver.id, approver.display_name)}
                                >
                                  Activate
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                    ))}
                  </tbody>
                </table>
                {legalApprovers.length > legalApproversDisplayLimit && (
                  <div className="show-more-container">
                    <button 
                      className="btn btn-show-more"
                      onClick={() => setLegalApproversDisplayLimit(prev => prev + 20)}
                    >
                      Show More ({legalApprovers.length - legalApproversDisplayLimit} remaining)
                    </button>
                  </div>
                )}
                </>
              ) : (
                <div className="no-deactivated-users">
                  <FontAwesomeIcon icon={faGavel} style={{ fontSize: '64px', color: '#cbd5e0' }} />
                  <p>No legal approvers found</p>
                </div>
              )}
            </div>
            </>
          )}

          {/* System Users Table - Only show in Employee Management mode */}
          {adminMode === 'employees' && activeTab === 'users' && (
          <>
          <div className="regular-employees-section">
            <h3>System Users ({regularEmployees.length})</h3>
            {loading ? (
              <div className="loading">Loading employees...</div>
            ) : (
              <>
                <table className="employees-table">
                <thead>
                  <tr>
                    <th>IMT ID</th>
                    <th>Avatar</th>
                    <th>Name</th>
                    <th>Position</th>
                    <th>Department</th>
                    <th>Supervisor</th>
                    <th>Head of Department</th>
                    <th>Office</th>
                    <th>Last Login</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {regularEmployees && regularEmployees.length > 0 ? (
                    regularEmployees.slice(0, regularEmployeesDisplayLimit).map(employee => (
                      <tr 
                        key={employee.id} 
                        className={!employee.is_active ? 'inactive' : ''}
                        onClick={() => handleViewEmployee(employee)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>{employee.hms_employee_id || 'Not assigned'}</td>
                        <td className="avatar-cell">
                          {employee.avatar_photo ? (
                            <img 
                              src={employee.avatar_photo} 
                              alt={employee.display_name}
                              className="table-avatar"
                            />
                          ) : (
                            <div className="table-avatar-initials">
                              {employee.first_name?.[0] || employee.email?.[0] || '?'}{employee.last_name?.[0] || ''}
                            </div>
                          )}
                        </td>
                        <td>{employee.display_name}</td>
                        <td>{employee.position?.name || 'Not set'}</td>
                        <td>{employee.department?.name || 'Not set'}</td>
                        <td>{employee.supervisor ? employee.supervisor.name : 'None'}</td>
                        <td>{employee.head_of_department ? employee.head_of_department.name : 'None'}</td>
                        <td>{employee.office?.name || 'Not set'}</td>
                        <td>{formatLastLogin(employee.last_login)}</td>
                        <td>
                          <span className={`status-badge ${employee.is_active ? 'active' : 'inactive'}`}>
                            {employee.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="actions-cell">
                          <div className="action-dropdown" onClick={(e) => e.stopPropagation()} data-dropdown-id={`emp-${employee.id}`}>
                            <button 
                              className="gear-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleDropdown(`emp-${employee.id}`);
                              }}
                            >
                              Actions
                            </button>
                            {activeDropdown === `emp-${employee.id}` && (
              <div className="dropdown-menu">
                {employee.is_active && (
                  <>
                    <button onClick={() => handleViewEmployee(employee)}>View Details</button>
                    <button onClick={() => handleResetPassword(employee)}>Reset Password</button>
                    <button onClick={() => handleViewLeave(employee)}>View Leave</button>
                    <button onClick={() => handleAssignRole(employee)}>Assign Role</button>
                    <hr />
                  </>
                )}
                {employee.is_active ? (
                  <button 
                    className="danger"
                    onClick={() => handleDeactivateEmployee(employee.id, employee.display_name)}
                  >
                    Deactivate
                  </button>
                ) : (
                  <button 
                    className="success"
                    onClick={() => handleActivateEmployee(employee.id, employee.display_name)}
                  >
                    Activate
                  </button>
                )}
              </div>
            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" style={{ textAlign: 'center', padding: '20px' }}>
                        {loading ? 'Loading employees...' : 'No employees found'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              </>
            )}
          </div>
          {regularEmployees && regularEmployees.length > regularEmployeesDisplayLimit && (
            <div className="show-more-container">
              <button 
                className="btn btn-show-more"
                onClick={() => setRegularEmployeesDisplayLimit(prev => prev + 20)}
              >
                Show More ({regularEmployees.length - regularEmployeesDisplayLimit} remaining)
              </button>
            </div>
          )}
          </>
          )}

          {/* Deactivated Users Section - Only show in Employee Management mode */}
          {adminMode === 'employees' && activeTab === 'deactivated' && (
            <div className="deactivated-employees-section">
              {deactivatedEmployees && deactivatedEmployees.length > 0 ? (
                <table className="employees-table deactivated-table">
                  <thead>
                    <tr>
                      <th>IMT ID</th>
                      <th>Avatar</th>
                      <th>Name</th>
                      <th>Position</th>
                      <th>Department</th>
                      <th>Office</th>
                      <th>Deactivated Date</th>
                      <th>Deactivated By</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deactivatedEmployees.map(employee => (
                      <tr key={employee.id} className="deactivated-row">
                        <td>{employee.hms_employee_id || 'Not assigned'}</td>
                        <td className="avatar-cell">
                          {employee.avatar_photo ? (
                            <img 
                              src={employee.avatar_photo} 
                              alt={employee.display_name}
                              className="table-avatar"
                            />
                          ) : (
                            <div className="table-avatar-initials">
                              {employee.first_name?.[0] || employee.email?.[0] || '?'}{employee.last_name?.[0] || ''}
                            </div>
                          )}
                        </td>
                        <td>{employee.display_name}</td>
                        <td>{employee.position?.name || 'Not set'}</td>
                        <td>{employee.department?.name || 'Not set'}</td>
                        <td>{employee.office?.name || 'Not set'}</td>
                        <td>{formatLastLogin(employee.deactivated_at)}</td>
                        <td>{employee.deactivated_by || 'Unknown'}</td>
                        <td className="actions-cell">
                          <div className="action-dropdown" onClick={(e) => e.stopPropagation()} data-dropdown-id={`deactivated-${employee.id}`}>
                            <button 
                              className="gear-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleDropdown(`deactivated-${employee.id}`);
                              }}
                            >
                              Actions
                            </button>
                            {activeDropdown === `deactivated-${employee.id}` && (
                              <div className="dropdown-menu">
                                <button 
                                  className="success"
                                  onClick={() => handleActivateEmployee(employee.id, employee.display_name)}
                                >
                                  Activate
                                </button>
                                <button onClick={() => handleViewEmployee(employee)}>View Details</button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="no-deactivated-users">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#cbd5e0' }}>
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
                    <path d="M7 12h10v-2H7z"/>
                  </svg>
                  <p>No deactivated employees found</p>
                </div>
              )}
            </div>
          )}

          {showCreateModal && (
            <div className="admin-modal-overlay">
              <div className="modal create-employee-modal">
                <div className="modal-header">
                  <h2>Create New Employee</h2>
                  <button 
                    className="close-btn"
                    onClick={() => setShowCreateModal(false)}
                  >
                    ×
                  </button>
                </div>
                <form onSubmit={handleCreateEmployee} className="modal-form">
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      required
                      value={newEmployee.email}
                      onChange={(e) => setNewEmployee({...newEmployee, email: e.target.value})}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      required
                      value={newEmployee.first_name}
                      onChange={(e) => setNewEmployee({...newEmployee, first_name: e.target.value})}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      required
                      value={newEmployee.last_name}
                      onChange={(e) => setNewEmployee({...newEmployee, last_name: e.target.value})}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>User Type *</label>
                    <div className="user-type-selection">
                      <div className="radio-group">
                        <label className="radio-label">
                          <input
                            type="radio"
                            name="userType"
                            value={false}
                            checked={!newEmployee.is_superuser}
                            onChange={(e) => setNewEmployee({...newEmployee, is_superuser: false})}
                          />
                          <div className="radio-content">
                            <strong>Regular User</strong>
                            <small>Standard employee access with limited permissions</small>
                          </div>
                        </label>
                        <label className="radio-label">
                          <input
                            type="radio"
                            name="userType"
                            value={true}
                            checked={newEmployee.is_superuser}
                            onChange={(e) => setNewEmployee({...newEmployee, is_superuser: true})}
                          />
                          <div className="radio-content">
                            <strong>Administrator</strong>
                            <small>Full system access including admin dashboard and user management</small>
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  {/* NEW: Additional Designations */}
                  <div className="form-group">
                    <label>Additional Designations (Optional)</label>
                    <div className="designation-selection">
                      <div className="toggle-group">
                        <label className={`toggle-label ${newEmployee.is_supervisor ? 'active' : ''}`}>
                          <input
                            type="checkbox"
                            checked={newEmployee.is_supervisor}
                            onChange={(e) => setNewEmployee({...newEmployee, is_supervisor: e.target.checked})}
                          />
                          <div className="toggle-content">
                            <strong>Supervisor</strong>
                            <small>Designated as a supervisor (admin-only designation)</small>
                          </div>
                        </label>
                        <label className={`toggle-label ${newEmployee.is_head_of_department ? 'active' : ''}`}>
                          <input
                            type="checkbox"
                            checked={newEmployee.is_head_of_department}
                            onChange={(e) => setNewEmployee({
                              ...newEmployee, 
                              is_head_of_department: e.target.checked,
                              supervised_department_id: e.target.checked ? newEmployee.supervised_department_id : null
                            })}
                          />
                          <div className="toggle-content">
                            <strong>Head of Department</strong>
                            <small>Designated as HOD - requires department assignment (admin-only)</small>
                          </div>
                        </label>
                        {newEmployee.is_head_of_department && (
                          <div className="indented-field" style={{ marginBottom: '8px' }}>
                            <select
                              required
                              value={newEmployee.supervised_department_id || ''}
                              onChange={(e) => setNewEmployee({
                                ...newEmployee, 
                                supervised_department_id: e.target.value ? parseInt(e.target.value) : null
                              })}
                              className="form-input"
                            >
                              <option value="">-- Select Department --</option>
                              {getAvailableDepartments(null).map(dept => (
                                <option key={dept.id} value={dept.id}>
                                  {dept.name}
                                </option>
                              ))}
                            </select>
                            {getAvailableDepartments(null).length === 0 && (
                              <small style={{ color: '#dc3545', marginTop: '4px', display: 'block' }}>
                                No available departments. All departments already have a Head of Department assigned.
                              </small>
                            )}
                          </div>
                        )}
                        <label className={`toggle-label ${newEmployee.is_crm_approver ? 'active' : ''}`}>
                          <input
                            type="checkbox"
                            checked={newEmployee.is_crm_approver}
                            onChange={(e) => setNewEmployee({...newEmployee, is_crm_approver: e.target.checked})}
                          />
                          <div className="toggle-content">
                            <strong>CRM Approver</strong>
                            <small>Designated as CRM Approver - auto-grants CRM access (admin-only)</small>
                          </div>
                        </label>
                        <label className={`toggle-label ${newEmployee.is_kyc_approver ? 'active' : ''}`}>
                          <input
                            type="checkbox"
                            checked={newEmployee.is_kyc_approver}
                            onChange={(e) => setNewEmployee({...newEmployee, is_kyc_approver: e.target.checked})}
                          />
                          <div className="toggle-content">
                            <strong>KYC Approver</strong>
                            <small>Approves partner KYC cases - auto-grants CRM access (admin-only)</small>
                          </div>
                        </label>
                        <label className={`toggle-label ${newEmployee.is_credit_approver ? 'active' : ''}`}>
                          <input
                            type="checkbox"
                            checked={newEmployee.is_credit_approver}
                            onChange={(e) => setNewEmployee({...newEmployee, is_credit_approver: e.target.checked})}
                          />
                          <div className="toggle-content">
                            <strong>Credit Approver</strong>
                            <small>Approves CRM partner credit limit increase requests (admin-only)</small>
                          </div>
                        </label>
                        <label className={`toggle-label ${newEmployee.is_financial_approver ? 'active' : ''}`}>
                          <input
                            type="checkbox"
                            checked={newEmployee.is_financial_approver}
                            onChange={(e) => setNewEmployee({...newEmployee, is_financial_approver: e.target.checked})}
                          />
                          <div className="toggle-content">
                            <strong>Financial Approver</strong>
                            <small>Designated as Financial Approver (admin-only designation, company-wide)</small>
                          </div>
                        </label>
                        <label className={`toggle-label ${newEmployee.is_delivery_reviewer ? 'active' : ''}`}>
                          <input
                            type="checkbox"
                            checked={newEmployee.is_delivery_reviewer}
                            onChange={(e) => setNewEmployee({...newEmployee, is_delivery_reviewer: e.target.checked})}
                          />
                          <div className="toggle-content">
                            <strong>Delivery Reviewer</strong>
                            <small>Designated as Delivery Reviewer (admin-only designation, company-wide)</small>
                          </div>
                        </label>
                        <label className={`toggle-label ${newEmployee.is_leave_supervisor ? 'active' : ''}`}>
                          <input
                            type="checkbox"
                            checked={newEmployee.is_leave_supervisor}
                            onChange={(e) => setNewEmployee({
                              ...newEmployee,
                              is_leave_supervisor: e.target.checked,
                              leave_supervisor_view_only: e.target.checked ? newEmployee.leave_supervisor_view_only : true
                            })}
                          />
                          <div className="toggle-content">
                            <strong>Leave Supervisor</strong>
                            <small>{newEmployee.is_leave_supervisor ? (newEmployee.leave_supervisor_view_only ? 'View Only' : 'Full Access') : 'Grants team leave calendar access'}</small>
                          </div>
                        </label>
                        {newEmployee.is_leave_supervisor && (
                          <div className="indented-field" style={{ marginBottom: '8px' }}>
                            <label className={`toggle-label ${newEmployee.leave_supervisor_view_only ? 'active' : ''}`}>
                              <div className="toggle-checkbox-container">
                                <input
                                  type="checkbox"
                                  className="toggle-checkbox"
                                  checked={newEmployee.leave_supervisor_view_only}
                                  onChange={(e) => setNewEmployee({...newEmployee, leave_supervisor_view_only: e.target.checked})}
                                />
                                <span className="toggle-checkbox-custom"></span>
                              </div>
                              <div className="toggle-content">
                                <strong>View Only</strong>
                                <small>When enabled, leave supervisor can only view the team leave calendar. No approvals, notifications, or work assignment.</small>
                              </div>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label>Password *</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showCreatePassword ? "text" : "password"}
                        required
                        value={newEmployee.password}
                        onChange={(e) => setNewEmployee({...newEmployee, password: e.target.value})}
                        className="form-input"
                        placeholder="Enter secure password or click Generate"
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => setShowCreatePassword(!showCreatePassword)}
                        aria-label={showCreatePassword ? "Hide password" : "Show password"}
                      >
                        <FontAwesomeIcon 
                          icon={showCreatePassword ? faEyeSlash : faEye} 
                          className="eye-icon"
                        />
                      </button>
                    </div>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      style={{ marginTop: 8, fontSize: '0.85em', padding: '6px 12px' }}
                      onClick={handleGenerateCreatePassword}
                    >
                      <i className="fas fa-key" style={{ marginRight: 6 }}></i>
                      Generate strong password
                    </button>
                    <small style={{ display: 'block', color: '#6b7280', marginTop: 4 }}>
                      The password will be included in the invite email if "Send invite email" is enabled below.
                    </small>
                    {newEmployee.password && (
                      <div className="password-requirements">
                        <h4>Password must have:</h4>
                        <ul>
                          <li className={createPasswordStrength.length ? "requirement-met" : "requirement-missing"}>
                            At least 8 characters
                          </li>
                          <li className={createPasswordStrength.hasUpper ? "requirement-met" : "requirement-missing"}>
                            At least one uppercase letter (A-Z)
                          </li>
                          <li className={createPasswordStrength.hasLower ? "requirement-met" : "requirement-missing"}>
                            At least one lowercase letter (a-z)
                          </li>
                          <li className={createPasswordStrength.hasNumber ? "requirement-met" : "requirement-missing"}>
                            At least one number (0-9)
                          </li>
                          <li className={createPasswordStrength.hasSpecial ? "requirement-met" : "requirement-missing"}>
                            At least one special character (!@#$%^&*(),.?":{}|&lt;&gt;)
                          </li>
                        </ul>
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Confirm Password *</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showCreateConfirmPassword ? "text" : "password"}
                        required
                        value={newEmployee.confirmPassword}
                        onChange={(e) => setNewEmployee({...newEmployee, confirmPassword: e.target.value})}
                        className="form-input"
                        placeholder="Re-enter password"
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => setShowCreateConfirmPassword(!showCreateConfirmPassword)}
                        aria-label={showCreateConfirmPassword ? "Hide password" : "Show password"}
                      >
                        <FontAwesomeIcon 
                          icon={showCreateConfirmPassword ? faEyeSlash : faEye} 
                          className="eye-icon"
                        />
                      </button>
                    </div>
                    {newEmployee.confirmPassword && newEmployee.password !== newEmployee.confirmPassword && (
                      <div className="password-error">
                        Passwords do not match
                      </div>
                    )}
                  </div>

                  <div className="form-group" style={{
                    background: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: 6,
                    padding: '12px 14px'
                  }}>
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 10,
                        cursor: 'pointer',
                        fontWeight: 600,
                        color: '#111827',
                        margin: 0,
                      }}
                    >
                      <input
                        type="checkbox"
                        className="invite-email-checkbox"
                        checked={newEmployee.send_invite_email}
                        onChange={(e) =>
                          setNewEmployee({ ...newEmployee, send_invite_email: e.target.checked })
                        }
                      />
                      <span>
                        Send invite email to employee
                        <small style={{
                          display: 'block',
                          fontWeight: 400,
                          color: '#6b7280',
                          marginTop: 2
                        }}>
                          Emails {newEmployee.email || 'the new employee'} the IMT login URL,
                          their username, and the temporary password above. The email instructs
                          them to change the password after their first login.
                        </small>
                      </span>
                    </label>
                  </div>

                  <div className="modal-actions">
                    <button 
                      type="submit" 
                      className="btn btn-primary"
                      disabled={!createPasswordStrength.valid || newEmployee.password !== newEmployee.confirmPassword}
                    >
                      Create Employee
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-secondary"
                      onClick={() => setShowCreateModal(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
                <div className="create-info">
                  <p><strong>Note:</strong> The employee can log in with the provided credentials and complete their profile via Account Information screen.</p>
                </div>
              </div>
            </div>
          )}

          {/* Bulk Upload Users Modal */}
          {showBulkUploadModal && (
            <div className="admin-modal-overlay">
              <div className="modal bulk-upload-modal">
                <div className="modal-header">
                  <h2>Bulk Upload Users</h2>
                  <button 
                    className="close-btn"
                    onClick={() => {
                      setShowBulkUploadModal(false);
                      setBulkUploadFile(null);
                    }}
                  >
                    ×
                  </button>
                </div>
                <form onSubmit={handleBulkUpload} className="modal-form">
                  <div className="bulk-upload-instructions">
                    <h3>CSV Format Requirements</h3>
                    <p>Your CSV file must include the following columns in this exact order:</p>
                    <ul style={{ fontSize: '0.9em', lineHeight: '1.6' }}>
                      <li><strong>Name</strong> - First name (required)</li>
                      <li><strong>Surname</strong> - Last name (required)</li>
                      <li><strong>Short name</strong> - Nickname or preferred short name</li>
                      <li><strong>Position</strong> - Job title (must match system position)</li>
                      <li><strong>{STAFF_UPLOAD_OFFICE_HEADER}</strong> - Office location (must match system office)</li>
                      <li><strong>Legal Entity (contract location)</strong> - Country name (must match valid country)</li>
                      <li><strong>First day of work</strong> - Join date (DD/MM/YYYY)</li>
                      <li><strong>Mobile phone number</strong> - Contact number</li>
                      <li><strong>Email-Address</strong> - Email address (required, unique)</li>
                      <li><strong>Division</strong> - Department name (must match system department)</li>
                      <li><strong>D.O.B. yyyy</strong> - Birth year</li>
                      <li><strong>D.O.B. mm</strong> - Birth month</li>
                      <li><strong>D.O.B dd</strong> - Birth day</li>
                    </ul>
                    <div style={{ marginTop: '15px', padding: '10px', background: '#f8f9fa', borderRadius: '4px', border: '1px solid #dee2e6' }}>
                      <p style={{ margin: '0', fontSize: '0.9em' }}>
                        <i className="fas fa-info-circle"></i> <strong>Note:</strong> Passwords will be auto-generated as <code>[Name]0123@</code> (e.g., "John0123@")
                      </p>
                      <p style={{ margin: '5px 0 0 0', fontSize: '0.9em' }}>
                        <i className="fas fa-sync-alt"></i> Existing users (by email) will be updated with new information
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleDownloadCsvTemplate}
                      className="btn btn-secondary"
                      style={{ marginTop: '12px', width: '100%' }}
                    >
                      <FontAwesomeIcon icon={faDownload} /> Download CSV Template
                    </button>
                  </div>
                  
                  <div className="form-group">
                    <label>Select CSV File *</label>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={(e) => setBulkUploadFile(e.target.files[0])}
                      className="form-input"
                      required
                    />
                    {bulkUploadFile && (
                      <div style={{ marginTop: '10px', padding: '8px', background: '#e7f3ff', borderRadius: '4px', border: '1px solid #b3d9ff' }}>
                        <p style={{ margin: '0', fontSize: '0.9em' }}>
                          <i className="fas fa-file-csv"></i> Selected: <strong>{bulkUploadFile.name}</strong> ({(bulkUploadFile.size / 1024).toFixed(2)} KB)
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="form-group" style={{
                    background: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: 6,
                    padding: '12px 14px'
                  }}>
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 10,
                        cursor: 'pointer',
                        fontWeight: 600,
                        color: '#111827',
                        margin: 0,
                      }}
                    >
                      <input
                        type="checkbox"
                        className="invite-email-checkbox"
                        checked={bulkSendInviteEmail}
                        onChange={(e) => setBulkSendInviteEmail(e.target.checked)}
                        disabled={isUploading}
                      />
                      <span>
                        Send invite email to each new employee
                        <small style={{
                          display: 'block',
                          fontWeight: 400,
                          color: '#6b7280',
                          marginTop: 2
                        }}>
                          Each newly-created employee will receive an email with the IMT login
                          URL, their username, and the auto-generated password. Existing
                          employees (matched by email) will be updated and will <strong>not</strong>
                          {' '}receive an email.
                        </small>
                      </span>
                    </label>
                  </div>

                  <div className="modal-actions">
                    <button 
                      type="submit" 
                      className="btn btn-primary"
                      disabled={!bulkUploadFile || isUploading}
                    >
                      {isUploading ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i> Uploading...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-upload"></i> Upload CSV
                        </>
                      )}
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowBulkUploadModal(false);
                        setBulkUploadFile(null);
                      }}
                      disabled={isUploading}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Export Employees to Excel Modal */}
          {showExportModal && (
            <div className="admin-modal-overlay">
              <div className="modal export-employees-modal">
                <div className="modal-header">
                  <h2>Export Employees to Excel</h2>
                  <button
                    className="close-btn"
                    onClick={() => setShowExportModal(false)}
                  >
                    ×
                  </button>
                </div>
                <form onSubmit={handleExportEmployees} className="modal-form">
                  <div className="form-group">
                    <label>Fields to Export *</label>
                    <div style={{ display: 'flex', gap: 14, margin: '4px 0 10px' }}>
                      <button
                        type="button"
                        onClick={() => setExportFields(EXPORTABLE_EMPLOYEE_FIELDS.map(f => f.key))}
                        disabled={isExporting}
                        style={{ background: 'none', border: 'none', padding: 0, color: '#2563eb', cursor: 'pointer', fontSize: '0.9em' }}
                      >
                        Select all
                      </button>
                      <button
                        type="button"
                        onClick={() => setExportFields([])}
                        disabled={isExporting}
                        style={{ background: 'none', border: 'none', padding: 0, color: '#2563eb', cursor: 'pointer', fontSize: '0.9em' }}
                      >
                        Clear
                      </button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '6px 16px' }}>
                      {/* Custom indicator instead of a native checkbox: the modal's
                          global input styling stretches checkboxes into full-width
                          pills and breaks toggling */}
                      {EXPORTABLE_EMPLOYEE_FIELDS.map((field) => {
                        const isChecked = exportFields.includes(field.key);
                        return (
                          <div
                            key={field.key}
                            role="checkbox"
                            aria-checked={isChecked}
                            tabIndex={isExporting ? -1 : 0}
                            onClick={() => !isExporting && toggleExportField(field.key)}
                            onKeyDown={(e) => {
                              if (!isExporting && (e.key === ' ' || e.key === 'Enter')) {
                                e.preventDefault();
                                toggleExportField(field.key);
                              }
                            }}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 8,
                              fontWeight: 400, cursor: isExporting ? 'default' : 'pointer',
                              userSelect: 'none', padding: '2px 0'
                            }}
                          >
                            <span aria-hidden="true" style={{
                              width: 16, height: 16, flex: '0 0 auto',
                              border: isChecked ? '1.5px solid #2563eb' : '1.5px solid #9ca3af',
                              borderRadius: 4,
                              background: isChecked ? '#2563eb' : '#fff',
                              color: '#fff', display: 'flex', alignItems: 'center',
                              justifyContent: 'center', fontSize: 11, fontWeight: 700,
                              lineHeight: 1
                            }}>{isChecked ? '✓' : ''}</span>
                            <span>{field.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="form-group" style={{
                    background: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: 6,
                    padding: '12px 14px'
                  }}>
                    <div
                      role="checkbox"
                      aria-checked={exportIncludeInactive}
                      tabIndex={isExporting ? -1 : 0}
                      onClick={() => !isExporting && setExportIncludeInactive(prev => !prev)}
                      onKeyDown={(e) => {
                        if (!isExporting && (e.key === ' ' || e.key === 'Enter')) {
                          e.preventDefault();
                          setExportIncludeInactive(prev => !prev);
                        }
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        cursor: isExporting ? 'default' : 'pointer',
                        fontWeight: 600,
                        color: '#111827',
                        margin: 0,
                        userSelect: 'none',
                      }}
                    >
                      <span aria-hidden="true" style={{
                        width: 16, height: 16, flex: '0 0 auto',
                        border: exportIncludeInactive ? '1.5px solid #2563eb' : '1.5px solid #9ca3af',
                        borderRadius: 4,
                        background: exportIncludeInactive ? '#2563eb' : '#fff',
                        color: '#fff', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', fontSize: 11, fontWeight: 700,
                        lineHeight: 1
                      }}>{exportIncludeInactive ? '✓' : ''}</span>
                      <span>Include deactivated employees</span>
                    </div>
                  </div>

                  <div className="modal-actions">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={exportFields.length === 0 || isExporting}
                    >
                      {isExporting ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i> Exporting...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faDownload} /> Download
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowExportModal(false)}
                      disabled={isExporting}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Bulk Upload Results Modal */}
          {showBulkUploadResults && bulkUploadResults && (
            <div className="admin-modal-overlay">
              <div className="modal bulk-upload-results-modal">
                <div className="modal-header">
                  <h2>Bulk Upload Results</h2>
                  <button 
                    className="close-btn"
                    onClick={() => {
                      setShowBulkUploadResults(false);
                      setBulkUploadResults(null);
                    }}
                  >
                    ×
                  </button>
                </div>
                <div className="bulk-upload-results-content">
                  <div className="bulk-upload-statistics-grid">
                    <div className="bulk-upload-stat-card bulk-upload-stat-card-success">
                      <div className="bulk-upload-stat-icon">
                        <FontAwesomeIcon icon={faUserPlus} />
                      </div>
                      <div className="bulk-upload-stat-info">
                        <h3>{bulkUploadResults.statistics.new_users}</h3>
                        <p>New Users Added</p>
                      </div>
                    </div>
                    
                    <div className="bulk-upload-stat-card bulk-upload-stat-card-update">
                      <div className="bulk-upload-stat-icon">
                        <FontAwesomeIcon icon={faUserEdit} />
                      </div>
                      <div className="bulk-upload-stat-info">
                        <h3>{bulkUploadResults.statistics.updated_users}</h3>
                        <p>Users Updated</p>
                      </div>
                    </div>
                    
                    <div className="bulk-upload-stat-card bulk-upload-stat-card-error">
                      <div className="bulk-upload-stat-icon">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                      </div>
                      <div className="bulk-upload-stat-info">
                        <h3>{bulkUploadResults.statistics.unaffected}</h3>
                        <p>Users Unaffected (Errors)</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Auto-created Positions & Departments */}
                  {((bulkUploadResults.auto_created_positions && bulkUploadResults.auto_created_positions.length > 0) ||
                    (bulkUploadResults.auto_created_departments && bulkUploadResults.auto_created_departments.length > 0)) && (
                    <div style={{ margin: '15px 0', padding: '12px 16px', background: '#fff8e1', borderRadius: '6px', border: '1px solid #ffe082' }}>
                      <h4 style={{ margin: '0 0 8px 0', fontSize: '0.95em', color: '#f57f17' }}>
                        <FontAwesomeIcon icon={faInfoCircle} /> Auto-Created Records
                      </h4>
                      {bulkUploadResults.auto_created_positions && bulkUploadResults.auto_created_positions.length > 0 && (
                        <p style={{ margin: '4px 0', fontSize: '0.9em' }}>
                          <strong>Positions:</strong> {bulkUploadResults.auto_created_positions.join(', ')}
                        </p>
                      )}
                      {bulkUploadResults.auto_created_departments && bulkUploadResults.auto_created_departments.length > 0 && (
                        <p style={{ margin: '4px 0', fontSize: '0.9em' }}>
                          <strong>Departments:</strong> {bulkUploadResults.auto_created_departments.join(', ')}
                        </p>
                      )}
                    </div>
                  )}

                  {/* New Users Details */}
                  {bulkUploadResults.new_users_details && bulkUploadResults.new_users_details.length > 0 && (
                    <div className="bulk-upload-details-section bulk-upload-success-section">
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                        <h3 className="bulk-upload-section-header bulk-upload-success-header" style={{ margin: 0 }}>
                          <FontAwesomeIcon icon={faUserPlus} /> New Users Added ({bulkUploadResults.new_users_details.length})
                        </h3>
                        <button
                          type="button"
                          className={`btn ${passwordsCopied ? 'btn-success' : 'btn-secondary'}`}
                          onClick={() => handleCopyNewUserPasswords(bulkUploadResults.new_users_details)}
                          style={{ fontSize: '0.85em', padding: '5px 12px' }}
                        >
                          <FontAwesomeIcon icon={passwordsCopied ? faCheck : faCopy} />
                          {' '}{passwordsCopied ? 'Copied!' : 'Copy Passwords'}
                        </button>
                      </div>
                      <div className="bulk-upload-table-wrapper">
                        <table className="bulk-upload-details-table">
                          <thead>
                            <tr>
                              <th>Row</th>
                              <th>Name</th>
                              <th>Email</th>
                              <th>IMT ID</th>
                              <th>Position</th>
                              <th>Password</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bulkUploadResults.new_users_details.map((user, index) => (
                              <tr key={index}>
                                <td>{user.row}</td>
                                <td>{user.name}</td>
                                <td>{user.email}</td>
                                <td>{user.hms_id}</td>
                                <td>{user.position}</td>
                                <td className="bulk-upload-password-cell">{user.password}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  
                  {/* Updated Users Details */}
                  {bulkUploadResults.updated_users_details && bulkUploadResults.updated_users_details.length > 0 && (
                    <div className="bulk-upload-details-section bulk-upload-update-section">
                      <h3 className="bulk-upload-section-header bulk-upload-update-header">
                        <FontAwesomeIcon icon={faUserEdit} /> Users Updated ({bulkUploadResults.updated_users_details.length})
                      </h3>
                      <div className="bulk-upload-table-wrapper">
                        <table className="bulk-upload-details-table">
                          <thead>
                            <tr>
                              <th>Row</th>
                              <th>Name</th>
                              <th>Email</th>
                              <th>IMT ID</th>
                              <th>Position</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bulkUploadResults.updated_users_details.map((user, index) => (
                              <tr key={index}>
                                <td>{user.row}</td>
                                <td>{user.name}</td>
                                <td>{user.email}</td>
                                <td>{user.hms_id}</td>
                                <td>{user.position}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  
                  {/* Errors Details */}
                  {bulkUploadResults.errors && bulkUploadResults.errors.length > 0 && (
                    <div className="bulk-upload-details-section bulk-upload-error-section">
                      <h3 className="bulk-upload-section-header bulk-upload-error-header">
                        <FontAwesomeIcon icon={faExclamationTriangle} /> Errors ({bulkUploadResults.errors.length})
                      </h3>
                      <div className="bulk-upload-table-wrapper">
                        <table className="bulk-upload-details-table">
                          <thead>
                            <tr>
                              <th>Row</th>
                              <th>Email</th>
                              <th>Error Message</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bulkUploadResults.errors.map((error, index) => (
                              <tr key={index}>
                                <td>{error.row}</td>
                                <td>{error.email}</td>
                                <td className="bulk-upload-error-message">{error.error}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <p className="bulk-upload-help-text">
                        <FontAwesomeIcon icon={faInfoCircle} /> Fix the errors in your CSV file and upload again to process these rows.
                      </p>
                    </div>
                  )}
                  
                  {(!bulkUploadResults.errors || bulkUploadResults.errors.length === 0) && 
                   (!bulkUploadResults.new_users_details || bulkUploadResults.new_users_details.length === 0) &&
                   (!bulkUploadResults.updated_users_details || bulkUploadResults.updated_users_details.length === 0) && (
                    <div className="bulk-upload-empty-message">
                      <FontAwesomeIcon icon={faInfoCircle} />
                      <span>No users were processed. Please check your CSV file.</span>
                    </div>
                  )}
                </div>
                <div className="modal-actions">
                  <button 
                    type="button" 
                    className="btn btn-primary"
                    onClick={() => {
                      setShowBulkUploadResults(false);
                      setBulkUploadResults(null);
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Audit Logs Modal */}
          {showAuditLogs && (
            <div className="admin-modal-overlay">
              <div className="modal audit-logs-modal">
                <div className="modal-header">
                  <h2>Admin Center</h2>
                  <button 
                    className="close-btn"
                    onClick={() => setShowAuditLogs(false)}
                  >
                    ×
                  </button>
                </div>
                
                {/* Main Tabs: Employee Logs | System Data Logs | Leave Logs | Admin Requests */}
                <div className="audit-main-tabs">
                  <div className="main-tabs-container">
                    <button
                      className={`main-tab-button ${activeRequestsTab === 'audit' ? 'active' : ''}`}
                      onClick={() => setActiveRequestsTab('audit')}
                    >
                      <div className="audit-tab-icon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                          <polyline points="14,2 14,8 20,8"/>
                          <line x1="16" y1="13" x2="8" y2="13"/>
                          <line x1="16" y1="17" x2="8" y2="17"/>
                          <polyline points="10,9 9,9 8,9"/>
                        </svg>
                      </div>
                      <span>Employee Logs</span>
                    </button>
                    <button
                      className={`main-tab-button ${activeRequestsTab === 'system_data' ? 'active' : ''}`}
                      onClick={() => setActiveRequestsTab('system_data')}
                    >
                      <div className="audit-tab-icon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="4" y="4" width="16" height="16" rx="2" ry="2"/>
                          <line x1="8" y1="12" x2="16" y2="12"/>
                          <line x1="8" y1="16" x2="16" y2="16"/>
                          <line x1="8" y1="8" x2="16" y2="8"/>
                        </svg>
                      </div>
                      <span>System Data Logs</span>
                    </button>
                    <button
                      className={`main-tab-button ${activeRequestsTab === 'leave_logs' ? 'active' : ''}`}
                      onClick={() => setActiveRequestsTab('leave_logs')}
                    >
                      <div className="audit-tab-icon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                          <line x1="16" y1="2" x2="16" y2="6"/>
                          <line x1="8" y1="2" x2="8" y2="6"/>
                          <line x1="3" y1="10" x2="21" y2="10"/>
                        </svg>
                      </div>
                      <span>Leave Logs</span>
                    </button>
                    <button
                      className={`main-tab-button ${activeRequestsTab === 'admin_requests' ? 'active' : ''}`}
                      onClick={() => setActiveRequestsTab('admin_requests')}
                    >
                      <div className="audit-tab-icon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                          <circle cx="9" cy="7" r="4"/>
                          <path d="m22 2-5 10-4-3Z"/>
                        </svg>
                      </div>
                      <span>Admin Requests</span>
                      {(pendingRequestsCount + pendingLeaveRequestsCount) > 0 && (
                        <span className="tab-badge">{pendingRequestsCount + pendingLeaveRequestsCount}</span>
                      )}
                    </button>
                  </div>
                </div>

                {/* Sub-tabs for Audit Logs */}
                {activeRequestsTab === 'audit' && (
                  <div className="audit-filter-tabs">
                    <div className="audit-tabs-container">
                      {auditFilterOptions.map((filter) => (
                        <button
                          key={filter.value}
                          className={`audit-filter-button ${activeAuditFilter === filter.value ? 'active' : ''}`}
                          data-filter={filter.value}
                          onClick={() => handleAuditFilterChange(filter.value)}
                        >
                          <div className="audit-filter-icon">
                            {filter.icon}
                          </div>
                          <span>{filter.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sub-tabs for System Data Logs */}
                {activeRequestsTab === 'system_data' && (
                  <div className="audit-filter-tabs">
                    <div className="audit-tabs-container">
                      {systemDataFilterOptions.map((filter) => (
                        <button
                          key={filter.value}
                          className={`audit-filter-button ${activeSystemDataFilter === filter.value ? 'active' : ''}`}
                          data-filter={filter.value}
                          onClick={() => handleSystemDataFilterChange(filter.value)}
                        >
                          <div className="audit-filter-icon">
                            {filter.icon}
                          </div>
                          <span>{filter.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sub-tabs for Leave Logs */}
                {activeRequestsTab === 'leave_logs' && (
                  <div className="audit-filter-tabs">
                    <div className="audit-tabs-container">
                      {leaveAuditSubTabs.map((tab) => (
                        <button
                          key={tab.value}
                          className={`audit-filter-button ${activeLeaveAuditFilter === tab.value ? 'active' : ''}`}
                          data-filter={tab.value}
                          onClick={() => handleLeaveAuditFilterChange(tab.value)}
                        >
                          <div className="audit-filter-icon">
                            {tab.icon}
                          </div>
                          <span>{tab.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sub-tabs for Admin Requests */}
                {activeRequestsTab === 'admin_requests' && (
                  <div className="admin-requests-sub-tabs">
                    <div className="sub-tabs-container">
                      <button 
                        className={`sub-tab-button ${activeAdminRequestsSubTab === 'position_requests' ? 'active' : ''}`}
                        onClick={() => setActiveAdminRequestsSubTab('position_requests')}
                      >
                        <div className="sub-tab-icon">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                            <circle cx="9" cy="7" r="4"/>
                            <path d="m22 2-5 10-4-3Z"/>
                          </svg>
                        </div>
                        <span>Position Requests</span>
                        {pendingRequestsCount > 0 && <span className="sub-tab-badge">{pendingRequestsCount}</span>}
                      </button>
                      <button 
                        className={`sub-tab-button ${activeAdminRequestsSubTab === 'leave_requests' ? 'active' : ''}`}
                        onClick={() => setActiveAdminRequestsSubTab('leave_requests')}
                      >
                        <div className="sub-tab-icon">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                            <line x1="16" y1="2" x2="16" y2="6"/>
                            <line x1="8" y1="2" x2="8" y2="6"/>
                            <line x1="3" y1="10" x2="21" y2="10"/>
                          </svg>
                        </div>
                        <span>Leave Requests</span>
                        {pendingLeaveRequestsCount > 0 && <span className="sub-tab-badge">{pendingLeaveRequestsCount}</span>}
                      </button>
                    </div>
                  </div>
                )}

                {/* Row counts. The point of the whole sweep: a list showing
                    fewer rows than exist must SAY so, or it is indistinguishable
                    from a list with fewer rows in it. */}
                <div
                  className="audit-log-count"
                  style={{ margin: '0 0 0.5rem', fontSize: '0.85rem', opacity: 0.85 }}
                >
                  {activeRequestsTab === 'audit'
                    ? (auditLogs.length >= auditTotal
                        ? `${auditLogs.length} entries`
                        : `Showing ${auditLogs.length} of ${auditTotal} entries`)
                    : (leaveAuditLogs.length >= leaveAuditTotal
                        ? `${leaveAuditLogs.length} entries`
                        : `Showing ${leaveAuditLogs.length} of ${leaveAuditTotal} entries`)}
                </div>

                <div className="audit-logs">
                  {activeRequestsTab === 'audit' ? (
                    // Employee Audit Logs Table
                    filteredAuditLogs && filteredAuditLogs.length > 0 ? (
                      <table className="audit-table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Admin User</th>
                            <th>Action</th>
                            <th>Affected User</th>
                            <th>Details</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredAuditLogs.map(log => (
                            <tr key={log.id}>
                              <td>{new Date(log.timestamp).toLocaleString()}</td>
                              <td>
                                <div className="admin-user-info">
                                  <div className="admin-user-name">{log.admin_user?.name || 'Unknown Admin'}</div>
                                  <div className="admin-user-details">{log.admin_user?.email || 'N/A'}</div>
                                </div>
                              </td>
                              <td>
                                <span className={`action-badge ${log.action}`}>
                                  {formatActionLabel(log.action, log.action_display)}
                                </span>
                              </td>
                              <td>
                                <div className="affected-user-info">
                                  <div className="affected-user-name">{log.employee?.name || 'Unknown User'}</div>
                                  <div className="affected-user-details">{log.employee?.email || 'N/A'}</div>
                                </div>
                              </td>
                              <td>{log.details || 'N/A'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                        <p>No employee audit logs found</p>
                      </div>
                    )
                  ) : activeRequestsTab === 'system_data' ? (
                    // System Data Logs Table
                    filteredSystemDataLogs && filteredSystemDataLogs.length > 0 ? (
                      <table className="audit-table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Admin User</th>
                            <th>Action</th>
                            <th>Affected User</th>
                            <th>Details</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredSystemDataLogs.map(log => (
                            <tr key={log.id}>
                              <td>{new Date(log.timestamp).toLocaleString()}</td>
                              <td>
                                <div className="admin-user-info">
                                  <div className="admin-user-name">{log.admin_user?.name || 'Unknown Admin'}</div>
                                  <div className="admin-user-details">{log.admin_user?.email || 'N/A'}</div>
                                </div>
                              </td>
                              <td>
                                <span className={`action-badge ${log.action}`}>
                                  {formatActionLabel(log.action, log.action_display)}
                                </span>
                              </td>
                              <td>
                                <div className="affected-user-info">
                                  <div className="affected-user-name">{log.employee?.name || 'Unknown User'}</div>
                                  <div className="affected-user-details">{log.employee?.email || 'N/A'}</div>
                                </div>
                              </td>
                              <td>{log.details || 'N/A'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                        <p>No system data logs found</p>
                      </div>
                    )
                  ) : activeRequestsTab === 'leave_logs' ? (
                    // Leave Logs Table
                    leaveAuditLogsLoading ? (
                      <div className="loading-state" style={{ textAlign: 'center', padding: '40px' }}>
                        <div className="spinner"></div>
                        <p>Loading leave audit logs...</p>
                      </div>
                    ) : filteredLeaveAuditLogs && filteredLeaveAuditLogs.length > 0 ? (
                      <table className="leave-audit-table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Employee</th>
                            <th>Action</th>
                            <th>Leave Type</th>
                            <th>Days</th>
                            <th>Details</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredLeaveAuditLogs.map(log => (
                            <tr key={log.id} className={`leave-action-${log.action}`}>
                              <td>{new Date(log.timestamp).toLocaleString()}</td>
                              <td>
                                <div className="employee-info">
                                  <div className="employee-name">{log.employee?.name || 'Unknown'}</div>
                                  <div className="employee-details">{log.employee?.email || 'N/A'}</div>
                                </div>
                              </td>
                              <td>
                                <div className="action-with-performer">
                                  <span className={`leave-action-badge action-${log.action}`}>
                                    {log.action_display || formatActionLabel(log.action)}
                                  </span>
                                  {log.performed_by && (
                                    <div className="performed-by-text">
                                      by {log.performed_by.email}
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td>
                                {log.leave_request ? (
                                  <span 
                                    className="leave-type-badge"
                                    style={{ backgroundColor: log.leave_request.leave_type_color }}
                                  >
                                    {log.leave_request.leave_type_name}
                                  </span>
                                ) : (
                                  <span className="no-data">-</span>
                                )}
                              </td>
                              <td>
                                <strong>
                                  {log.leave_request 
                                    ? log.leave_request.total_days 
                                    : (log.details?.total_days || log.details?.adjustment_amount || '-')}
                                </strong>
                              </td>
                              <td>
                                <div className="leave-details">
                                  {formatLeaveActionDetails(log)}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                        <p>No leave audit logs found</p>
                      </div>
                    )
                  ) : activeRequestsTab === 'admin_requests' ? (
                    activeAdminRequestsSubTab === 'position_requests' ? (
                      // Position Verification Requests Table
                    <table className="audit-table position-requests-table">
                      <thead>
                        <tr>
                          <th>Date Requested</th>
                          <th>Employee</th>
                          <th>Status</th>
                          <th>Expires</th>
                          <th>Details</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {positionRequests.length > 0 ? (
                          positionRequests.map((request) => (
                            <tr key={request.id} className={`position-request-row status-${request.status}`}>
                              <td>{new Date(request.requested_at).toLocaleString()}</td>
                              <td>
                                <div className="employee-info">
                                  <div className="employee-name">{request.employee.name}</div>
                                  <div className="employee-details">
                                    {request.employee.email}
                                  </div>
                                </div>
                              </td>
                              <td>
                                <span className={`request-status-badge status-${request.status}`}>
                                  {request.status_display}
                                  {request.is_expired && <span className="expired-label"> (Expired)</span>}
                                </span>
                                {request.reviewed_by && (
                                  <div className="reviewed-info">
                                    <small>by {request.reviewed_by.email}</small>
                                  </div>
                                )}
                              </td>
                              <td>
                                <span className={request.is_expired ? 'expired-date' : ''}>
                                  {new Date(request.expires_at).toLocaleDateString()}
                                </span>
                              </td>
                              <td>
                                {`Position change: ${formatPositionDisplay(request.previous_position)} → ${request.requested_position_display}`}
                                {request.admin_notes && ` | Notes: ${request.admin_notes}`}
                              </td>
                              <td>
                                {request.status === 'pending' && !request.is_expired ? (
                                  <div className="request-actions">
                                    <button 
                                      className="btn btn-approve btn-sm"
                                      onClick={() => handlePositionRequestReview(request.id, 'approve')}
                                      title="Approve Request"
                                    >
                                      Approve
                                    </button>
                                    <button 
                                      className="btn btn-deny btn-sm"
                                      onClick={() => handlePositionRequestReview(request.id, 'deny')}
                                      title="Deny Request"
                                    >
                                      Deny
                                    </button>
                                  </div>
                                ) : (
                                  <span className="no-actions">-</span>
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="6" className="no-requests-row">
                              <div className="no-requests">
                                <div className="no-requests-icon">
                                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                                    <circle cx="9" cy="7" r="4"/>
                                    <path d="m22 2-5 10-4-3Z"/>
                                  </svg>
                                </div>
                                <h4>No Position Requests</h4>
                                <p>There are currently no position verification requests to review.</p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    ) : (
                      // Leave Requests Table
                      <div className="leave-requests-table-container">
                        {leaveRequestsLoading ? (
                          <div className="loading-state">
                            <div className="spinner"></div>
                            <p>Loading leave requests...</p>
                          </div>
                        ) : (
                          <table className="audit-table leave-requests-table">
                            <thead>
                              <tr>
                                <th>Date Requested</th>
                                <th>Employee</th>
                                <th>Leave Type</th>
                                <th>Leave Dates</th>
                                <th>Total Days</th>
                                <th>Status</th>
                                <th>Reason</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {leaveRequests.length > 0 ? (
                                leaveRequests.map(request => (
                                  <tr key={request.id}>
                                    <td>
                                      {new Date(request.created_at).toLocaleString()}
                                    </td>
                                    <td>
                                      <div className="employee-info">
                                        <div className="employee-name">{request.employee_details?.name || 'Unknown'}</div>
                                        <div className="employee-details">{request.employee_details?.email || 'N/A'}</div>
                                      </div>
                                    </td>
                                    <td>
                                      <span 
                                        className="leave-type-badge" 
                                        style={{backgroundColor: request.leave_type_details?.color || '#6b7280'}}
                                      >
                                        {request.leave_type_details?.name || 'Unknown'}
                                      </span>
                                    </td>
                                    <td>
                                      <div className="dates-cell">
                                        <div>{new Date(request.start_date).toLocaleDateString()} - {new Date(request.end_date).toLocaleDateString()}</div>
                                        {request.start_day_type !== 'full' && (
                                          <small>Start: {request.start_day_type}</small>
                                        )}
                                        {request.end_day_type !== 'full' && (
                                          <small>End: {request.end_day_type}</small>
                                        )}
                                      </div>
                                    </td>
                                    <td>
                                      <strong>{Number(request.total_days) % 1 === 0 ? Math.floor(request.total_days) : request.total_days}</strong>
                                    </td>
                                    <td>
                                      <span className={`status-badge status-${request.status}`}>
                                        {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                                      </span>
                                      {request.status === 'approved' && 
                                       !request.has_work_assignment && 
                                       !['HO', 'TL'].includes(request.leave_type_details?.code) && (
                                        <div className="no-replacement-warning" title="No replacement assigned">
                                          <FontAwesomeIcon icon={faExclamationTriangle} />
                                          <small>No replacement</small>
                                        </div>
                                      )}
                                      {request.reviewed_by_details && (
                                        <div className="reviewed-info">
                                          <small>by {request.reviewed_by_details.email}</small>
                                        </div>
                                      )}
                                    </td>
                                    <td>
                                      {request.reason || '-'}
                                    </td>
                                    <td>
                                      <button
                                        onClick={() => handleViewLeaveRequest(request)}
                                        className="action-btn action-btn-view"
                                        title="View Details"
                                      >
                                        <FontAwesomeIcon icon={faEye} />
                                      </button>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="8" className="no-requests-row">
                                    <div className="no-requests">
                                      <div className="no-requests-icon">
                                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                                          <line x1="16" y1="2" x2="16" y2="6"/>
                                          <line x1="8" y1="2" x2="8" y2="6"/>
                                          <line x1="3" y1="10" x2="21" y2="10"/>
                                        </svg>
                                      </div>
                                      <h4>No Leave Requests</h4>
                                      <p>There are currently no leave requests to review.</p>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        )}
                      </div>
                    )
                  ) : null}
                </div>
              </div>
            </div>
          )}

          {/* Employee Details Modal */}
          {showEmployeeDetails && selectedEmployee && (
            <div className="admin-modal-overlay">
              <div className="modal employee-details-modal">
                <div className="modal-header">
                  <h2>Employee Details</h2>
                  <div className="modal-header-actions">
                    <button 
                      className="edit-employee-btn" 
                      onClick={() => {
                        setShowEmployeeDetails(false);
                        history.push(`/admin/employees/${selectedEmployee.id}/edit`);
                      }}
                      title="Edit Employee"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                    </button>
                    <button className="close-btn" onClick={() => setShowEmployeeDetails(false)}>×</button>
                  </div>
                </div>
                <div className="modal-body">
                  <div className="employee-details-content">
                    {/* Employee Header */}
                    <div className="employee-header">
                    <div className="employee-avatar">
                      {selectedEmployee.avatar_photo ? (
                        <img src={selectedEmployee.avatar_photo} alt="Employee Avatar" />
                      ) : (
                        <div className="avatar-placeholder">
                          {selectedEmployee.first_name?.[0] || selectedEmployee.email?.[0] || '?'}{selectedEmployee.last_name?.[0] || ''}
                        </div>
                      )}
                    </div>
                      <div className="employee-basic-info">
                        <h3>{selectedEmployee.display_name}</h3>
                        <p className="employee-title">{selectedEmployee.position?.name || 'Position not set'}</p>
                        <div className="employee-status">
                          <span className={`status-badge ${selectedEmployee.is_active ? 'active' : 'inactive'}`}>
                            {selectedEmployee.is_active ? 'Active' : 'Inactive'}
                          </span>
                          {selectedEmployee.is_superuser && (
                            <span className="status-badge superuser">Superuser</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Employee Details Grid */}
                    <div className="employee-details-grid">
                      {/* Basic Information */}
                      <div className="details-section">
                        <h4>Basic Information</h4>
                        <div className="details-row">
                          <span className="label">IMT Employee ID:</span>
                          <span className="value">{selectedEmployee.hms_employee_id || 'Not assigned'}</span>
                        </div>
                        <div className="details-row">
                          <span className="label">Email:</span>
                          <span className="value">{selectedEmployee.email}</span>
                        </div>
                        <div className="details-row">
                          <span className="label">First Name:</span>
                          <span className="value">{selectedEmployee.first_name || 'Not set'}</span>
                        </div>
                        <div className="details-row">
                          <span className="label">Last Name:</span>
                          <span className="value">{selectedEmployee.last_name || 'Not set'}</span>
                        </div>
                        <div className="details-row">
                          <span className="label">Preferred Name:</span>
                          <span className="value">{selectedEmployee.preferred_name || 'Not set'}</span>
                        </div>
                        <div className="details-row">
                          <span className="label">Short Name:</span>
                          <span className="value">{selectedEmployee.short_name || 'Not set'}</span>
                        </div>
                        <div className="details-row">
                          <span className="label">Birthday:</span>
                          <span className="value">{formatBirthday(selectedEmployee.date_of_birth)}</span>
                        </div>
                        <div className="details-row">
                          <span className="label">Joined IMT:</span>
                          <span className="value">
                            {selectedEmployee.date_joined_hms 
                              ? `${new Date(selectedEmployee.date_joined_hms).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}` 
                              : 'Not set'}
                          </span>
                        </div>
                      </div>

                      {/* Business Context */}
                      <div className="details-section">
                        <h4>Business Context</h4>
                        <div className="details-row">
                          <span className="label">Solid Fuels:</span>
                          <span className="value">{selectedEmployee.business_solid_fuels ? 'Yes' : 'No'}</span>
                        </div>
                        <div className="details-row">
                          <span className="label">Liquid Fuels:</span>
                          <span className="value">{selectedEmployee.business_liquid_fuels ? 'Yes' : 'No'}</span>
                        </div>
                      </div>

                      {/* Organization */}
                      <div className="details-section">
                        <h4>Organization</h4>
                        <div className="details-row">
                          <span className="label">Office/Legal Entity:</span>
                          <span className="value">{selectedEmployee.office?.name || 'Not set'}</span>
                        </div>
                        <div className="details-row">
                          <span className="label">Department:</span>
                          <span className="value">{selectedEmployee.department?.name || 'Not set'}</span>
                        </div>
                        <div className="details-row">
                          <span className="label">Position:</span>
                          <span className="value">{selectedEmployee.position?.name || 'Not set'}</span>
                        </div>
                        <div className="details-row">
                          <span className="label">Location:</span>
                          <span className="value">{selectedEmployee.location || 'Not set'}</span>
                        </div>
                        <div className="details-row">
                          <span className="label">Supervisors:</span>
                          <span className="value">
                            {selectedEmployee.supervisors && selectedEmployee.supervisors.length > 0 ? (
                              selectedEmployee.supervisors.map((sup, idx) => (
                                <span key={sup.id}>
                                  {sup.name}
                                  {idx < selectedEmployee.supervisors.length - 1 ? ', ' : ''}
                                </span>
                              ))
                            ) : 'Not set'}
                          </span>
                        </div>
                        <div className="details-row">
                          <span className="label">Heads of Department:</span>
                          <span className="value">
                            {selectedEmployee.heads_of_department && selectedEmployee.heads_of_department.length > 0 ? (
                              selectedEmployee.heads_of_department.map((hod, idx) => (
                                <span key={hod.id}>
                                  {hod.name}
                                  {idx < selectedEmployee.heads_of_department.length - 1 ? ', ' : ''}
                                </span>
                              ))
                            ) : 'Not set'}
                          </span>
                        </div>
                      </div>

                      {/* Contact Information */}
                      <div className="details-section">
                        <h4>Contact Information</h4>
                        <div className="details-row">
                          <span className="label">Work Phone:</span>
                          <span className="value">
                            {selectedEmployee.work_phone ? (
                              selectedEmployee.work_phone.startsWith('+') 
                                ? selectedEmployee.work_phone 
                                : `+27${selectedEmployee.work_phone.startsWith('0') ? selectedEmployee.work_phone.substring(1) : selectedEmployee.work_phone}`
                            ) : 'Not set'}
                          </span>
                        </div>
                        <div className="details-row">
                          <span className="label">Work Email:</span>
                          <span className="value">{selectedEmployee.work_email || selectedEmployee.email || 'Not set'}</span>
                        </div>
                        <div className="details-row">
                          <span className="label">Personal Phone:</span>
                          <span className="value">
                            {selectedEmployee.personal_phone ? (
                              selectedEmployee.personal_phone.startsWith('+') 
                                ? selectedEmployee.personal_phone 
                                : `+27${selectedEmployee.personal_phone.startsWith('0') ? selectedEmployee.personal_phone.substring(1) : selectedEmployee.personal_phone}`
                            ) : 'Not set'}
                          </span>
                        </div>
                      </div>

                      {/* Languages */}
                      <div className="details-section">
                        <h4>Languages Spoken</h4>
                        <div className="details-row">
                          <span className="label">Languages:</span>
                          <span className="value">
                            {formatLanguages(selectedEmployee.languages_spoken)}
                          </span>
                        </div>
                      </div>

                      {/* Working With Me Guide */}
                      <div className="details-section">
                        <h4>Working With Me Guide</h4>
                        <div className="details-row">
                          <span className="label">Characteristics:</span>
                          <span className="value">
                            {formatCharacteristics(selectedEmployee.working_characteristics)}
                          </span>
                        </div>
                        {selectedEmployee.working_guide_custom && (
                          <div className="details-row">
                            <span className="label">Custom Guide:</span>
                            <span className="value working-guide-custom">{selectedEmployee.working_guide_custom}</span>
                          </div>
                        )}
                      </div>

                      {/* CRM Partner (Go-live 1 November): linked partner, or create one from this record */}
                      <div className="details-section">
                        <h4>CRM Partner</h4>
                        <EmployeeCrmPartnerBlock
                          employeeId={selectedEmployee.id}
                          initialProfile={selectedEmployee.crm_partner_profile}
                        />
                      </div>

                      {/* Power of Attorney: the active ones this person holds and gives */}
                      <div className="details-section">
                        <h4>Power of Attorney</h4>
                        <EmployeePowersOfAttorney employeeId={selectedEmployee.id} />
                      </div>

                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Admin Employee Leave Modal */}
          {showViewLeaveModal && selectedEmployee && (
            <AdminEmployeeLeaveModal 
              employee={selectedEmployee}
              onClose={() => setShowViewLeaveModal(false)}
            />
          )}

          {/* Leave Request View Modal */}
          {showLeaveRequestViewModal && selectedLeaveRequestId && (
            <LeaveRequestViewModal
              requestId={selectedLeaveRequestId}
              onClose={() => {
                setShowLeaveRequestViewModal(false);
                setSelectedLeaveRequestId(null);
              }}
              isAdmin={true}
            />
          )}

          {/* Reset Password Modal */}
          {showPasswordReset && passwordResetEmployee && (
            <div className="admin-modal-overlay">
              <div className="modal password-reset-modal">
                <div className="modal-header">
                  <h2>Reset Password - {passwordResetEmployee.display_name}</h2>
                  <button className="close-btn" onClick={() => setShowPasswordReset(false)}>×</button>
                </div>
                <form onSubmit={handlePasswordResetSubmit} className="modal-form">
                  <div className="form-group">
                    <label>New Password *</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showResetPassword ? "text" : "password"}
                        required
                        value={resetPasswordData.password}
                        onChange={(e) => setResetPasswordData({...resetPasswordData, password: e.target.value})}
                        className="form-input"
                        placeholder="Enter new password"
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => setShowResetPassword(!showResetPassword)}
                        aria-label={showResetPassword ? "Hide password" : "Show password"}
                      >
                        <FontAwesomeIcon 
                          icon={showResetPassword ? faEyeSlash : faEye} 
                          className="eye-icon"
                        />
                      </button>
                    </div>
                    {resetPasswordData.password && (
                      <div className="password-requirements">
                        <h4>Password must have:</h4>
                        <ul>
                          <li className={resetPasswordStrength.length ? "requirement-met" : "requirement-missing"}>
                            At least 8 characters
                          </li>
                          <li className={resetPasswordStrength.hasUpper ? "requirement-met" : "requirement-missing"}>
                            At least one uppercase letter (A-Z)
                          </li>
                          <li className={resetPasswordStrength.hasLower ? "requirement-met" : "requirement-missing"}>
                            At least one lowercase letter (a-z)
                          </li>
                          <li className={resetPasswordStrength.hasNumber ? "requirement-met" : "requirement-missing"}>
                            At least one number (0-9)
                          </li>
                          <li className={resetPasswordStrength.hasSpecial ? "requirement-met" : "requirement-missing"}>
                            At least one special character (!@#$%^&*(),.?":{}|&lt;&gt;)
                          </li>
                        </ul>
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Confirm New Password *</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showResetConfirmPassword ? "text" : "password"}
                        required
                        value={resetPasswordData.confirmPassword}
                        onChange={(e) => setResetPasswordData({...resetPasswordData, confirmPassword: e.target.value})}
                        className="form-input"
                        placeholder="Re-enter new password"
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => setShowResetConfirmPassword(!showResetConfirmPassword)}
                        aria-label={showResetConfirmPassword ? "Hide password" : "Show password"}
                      >
                        <FontAwesomeIcon 
                          icon={showResetConfirmPassword ? faEyeSlash : faEye} 
                          className="eye-icon"
                        />
                      </button>
                    </div>
                    {resetPasswordData.confirmPassword && resetPasswordData.password !== resetPasswordData.confirmPassword && (
                      <div className="password-error">
                        Passwords do not match
                      </div>
                    )}
                  </div>
                  <div className="modal-actions">
                    <button 
                      type="submit" 
                      className="btn btn-primary"
                      disabled={!resetPasswordStrength.valid || resetPasswordData.password !== resetPasswordData.confirmPassword}
                    >
                      Reset Password
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-secondary"
                      onClick={() => setShowPasswordReset(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
                <div className="create-info">
                  <p><strong>Note:</strong> The employee will need to log in with the new password. Consider notifying them of the password change.</p>
                </div>
              </div>
            </div>
          )}

          {/* Role Assignment Modal */}
          {showRoleAssignment && roleAssignmentEmployee && (
            <div className="admin-modal-overlay">
              <div className="modal role-assignment-modal">
                <div className="modal-header">
                  <h2>Assign Role - {roleAssignmentEmployee.display_name}</h2>
                  <button className="close-btn" onClick={() => setShowRoleAssignment(false)}>×</button>
                </div>
                <form onSubmit={handleRoleAssignmentSubmit} className="modal-form">
                  <div className="form-group">
                    <label>Current Role</label>
                    <div className="current-role-display">
                      <span className={`role-badge ${roleAssignmentEmployee.is_superuser ? 'admin' : 'user'}`}>
                        {roleAssignmentEmployee.is_superuser ? 'Administrator' : 'Regular User'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label>New Role *</label>
                    <div className="role-selection">
                      <div className="radio-group">
                        <label className="radio-label">
                          <input
                            type="radio"
                            name="newRole"
                            value="user"
                            checked={selectedRole === 'user'}
                            onChange={(e) => setSelectedRole(e.target.value)}
                          />
                          <div className="role-info">
                            <div className="role-title">Regular User</div>
                            <div className="role-description">
                              Standard employee access with personal profile management and system usage capabilities.
                            </div>
                          </div>
                        </label>
                        
                        <label className="radio-label">
                          <input
                            type="radio"
                            name="newRole"
                            value="administrator"
                            checked={selectedRole === 'administrator'}
                            onChange={(e) => setSelectedRole(e.target.value)}
                          />
                          <div className="role-info">
                            <div className="role-title">Administrator</div>
                            <div className="role-description">
                              Full system access including employee management, role assignment, and administrative functions.
                            </div>
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  {/* NEW: Additional Designations */}
                  <div className="form-group">
                    <label>Additional Designations (Optional)</label>
                    <div className="designation-selection">
                      <div className="toggle-group">
                        <h4 className="toggle-section-title">Supervision &amp; HR</h4>
                        <label className={`toggle-label ${roleAssignmentData.is_supervisor ? 'active' : ''}`}>
                          <span className="toggle-icon"><FontAwesomeIcon icon={faUserTie} /></span>
                          <div className="toggle-checkbox-container">
                            <input
                              type="checkbox"
                              className="toggle-checkbox"
                              checked={roleAssignmentData.is_supervisor}
                              onChange={(e) => setRoleAssignmentData({...roleAssignmentData, is_supervisor: e.target.checked})}
                            />
                            <span className="toggle-checkbox-custom"></span>
                          </div>
                          <div className="toggle-content">
                            <strong>Supervisor</strong>
                            <small>Designated as a supervisor (admin-only designation)</small>
                          </div>
                        </label>
                        <label className={`toggle-label ${roleAssignmentData.is_head_of_department ? 'active' : ''}`}>
                          <span className="toggle-icon"><FontAwesomeIcon icon={faSitemap} /></span>
                          <div className="toggle-checkbox-container">
                            <input
                              type="checkbox"
                              className="toggle-checkbox"
                              checked={roleAssignmentData.is_head_of_department}
                              onChange={(e) => setRoleAssignmentData({
                                ...roleAssignmentData, 
                                is_head_of_department: e.target.checked,
                                supervised_department_id: e.target.checked ? roleAssignmentData.supervised_department_id : null
                              })}
                            />
                            <span className="toggle-checkbox-custom"></span>
                          </div>
                          <div className="toggle-content">
                            <strong>Head of Department</strong>
                            <small>Designated as HOD - requires department assignment (admin-only)</small>
                          </div>
                        </label>
                        {roleAssignmentData.is_head_of_department && (
                          <div className="indented-field" style={{ marginBottom: '8px' }}>
                            <select
                              required
                              value={roleAssignmentData.supervised_department_id || ''}
                              onChange={(e) => setRoleAssignmentData({
                                ...roleAssignmentData, 
                                supervised_department_id: e.target.value ? parseInt(e.target.value) : null
                              })}
                              className="form-input"
                            >
                              <option value="">-- Select Department --</option>
                              {getAvailableDepartments(roleAssignmentEmployee?.id).map(dept => (
                                <option key={dept.id} value={dept.id}>
                                  {dept.name}
                                </option>
                              ))}
                            </select>
                            {getAvailableDepartments(roleAssignmentEmployee?.id).length === 0 && (
                              <small style={{ color: '#dc3545', marginTop: '4px', display: 'block' }}>
                                No available departments. All departments already have a Head of Department assigned.
                              </small>
                            )}
                          </div>
                        )}
                        <label className={`toggle-label ${roleAssignmentData.is_leave_supervisor ? 'active' : ''}`}>
                          <span className="toggle-icon"><FontAwesomeIcon icon={faCalendarCheck} /></span>
                          <div className="toggle-checkbox-container">
                            <input
                              type="checkbox"
                              className="toggle-checkbox"
                              checked={roleAssignmentData.is_leave_supervisor}
                              onChange={(e) => setRoleAssignmentData({
                                ...roleAssignmentData,
                                is_leave_supervisor: e.target.checked,
                                leave_supervisor_view_only: e.target.checked ? roleAssignmentData.leave_supervisor_view_only : true
                              })}
                            />
                            <span className="toggle-checkbox-custom"></span>
                          </div>
                          <div className="toggle-content">
                            <strong>Leave Supervisor</strong>
                            <small>{roleAssignmentData.is_leave_supervisor ? (roleAssignmentData.leave_supervisor_view_only ? 'View Only - team leave calendar only' : 'Full Access - approvals, notifications, work assignment') : 'Grants team leave calendar access'}</small>
                          </div>
                        </label>
                        {roleAssignmentData.is_leave_supervisor && (
                          <div className="indented-field" style={{ marginBottom: '8px' }}>
                            <label className={`toggle-label ${roleAssignmentData.leave_supervisor_view_only ? 'active' : ''}`}>
                              <div className="toggle-checkbox-container">
                                <input
                                  type="checkbox"
                                  className="toggle-checkbox"
                                  checked={roleAssignmentData.leave_supervisor_view_only}
                                  onChange={(e) => setRoleAssignmentData({...roleAssignmentData, leave_supervisor_view_only: e.target.checked})}
                                />
                                <span className="toggle-checkbox-custom"></span>
                              </div>
                              <div className="toggle-content">
                                <strong>View Only</strong>
                                <small>When enabled, leave supervisor can only view the team leave calendar. No approvals, notifications, or work assignment.</small>
                              </div>
                            </label>
                          </div>
                        )}
                        <h4 className="toggle-section-title">Approvals &amp; Reviews</h4>
                        <label className={`toggle-label ${roleAssignmentData.is_crm_approver ? 'active' : ''}`}>
                          <span className="toggle-icon"><FontAwesomeIcon icon={faUsers} /></span>
                          <div className="toggle-checkbox-container">
                            <input
                              type="checkbox"
                              className="toggle-checkbox"
                              checked={roleAssignmentData.is_crm_approver}
                              onChange={(e) => setRoleAssignmentData({...roleAssignmentData, is_crm_approver: e.target.checked})}
                            />
                            <span className="toggle-checkbox-custom"></span>
                          </div>
                          <div className="toggle-content">
                            <strong>CRM Approver</strong>
                            <small>Designated as CRM Approver - auto-grants CRM access (admin-only)</small>
                          </div>
                        </label>
                        <label className={`toggle-label ${roleAssignmentData.is_credit_approver ? 'active' : ''}`}>
                          <span className="toggle-icon"><FontAwesomeIcon icon={faHandHoldingDollar} /></span>
                          <div className="toggle-checkbox-container">
                            <input
                              type="checkbox"
                              className="toggle-checkbox"
                              checked={roleAssignmentData.is_credit_approver}
                              onChange={(e) => setRoleAssignmentData({...roleAssignmentData, is_credit_approver: e.target.checked})}
                            />
                            <span className="toggle-checkbox-custom"></span>
                          </div>
                          <div className="toggle-content">
                            <strong>Credit Approver</strong>
                            <small>Approves CRM partner credit limit increase requests (admin-only)</small>
                          </div>
                        </label>
                        <label className={`toggle-label ${roleAssignmentData.is_kyc_approver ? 'active' : ''}`}>
                          <span className="toggle-icon"><FontAwesomeIcon icon={faUserCheck} /></span>
                          <div className="toggle-checkbox-container">
                            <input
                              type="checkbox"
                              className="toggle-checkbox"
                              checked={roleAssignmentData.is_kyc_approver}
                              onChange={(e) => setRoleAssignmentData({...roleAssignmentData, is_kyc_approver: e.target.checked})}
                            />
                            <span className="toggle-checkbox-custom"></span>
                          </div>
                          <div className="toggle-content">
                            <strong>KYC Approver</strong>
                            <small>Approves/rejects KYC portal cases which drive partner KYC &amp; approval status - auto-grants CRM access (admin-only)</small>
                          </div>
                        </label>
                        <label className={`toggle-label ${roleAssignmentData.is_financial_approver ? 'active' : ''}`}>
                          <span className="toggle-icon"><FontAwesomeIcon icon={faMoneyBillWave} /></span>
                          <div className="toggle-checkbox-container">
                            <input
                              type="checkbox"
                              className="toggle-checkbox"
                              checked={roleAssignmentData.is_financial_approver}
                              onChange={(e) => setRoleAssignmentData({...roleAssignmentData, is_financial_approver: e.target.checked})}
                            />
                            <span className="toggle-checkbox-custom"></span>
                          </div>
                          <div className="toggle-content">
                            <strong>Financial Approver</strong>
                            <small>Designated as Financial Approver (admin-only designation, company-wide)</small>
                          </div>
                        </label>
                        <label className={`toggle-label ${roleAssignmentData.can_approve_templates ? 'active' : ''}`}>
                          <span className="toggle-icon"><FontAwesomeIcon icon={faFileUpload} /></span>
                          <div className="toggle-checkbox-container">
                            <input
                              type="checkbox"
                              className="toggle-checkbox"
                              checked={roleAssignmentData.can_approve_templates}
                              onChange={(e) => setRoleAssignmentData({...roleAssignmentData, can_approve_templates: e.target.checked})}
                            />
                            <span className="toggle-checkbox-custom"></span>
                          </div>
                          <div className="toggle-content">
                            <strong>Template Approver</strong>
                            <small>Approves or rejects Template Headquarters content changes, including contract wording, and can open Template Headquarters (admin-only designation, company-wide)</small>
                          </div>
                        </label>
                        {/* IMT only (divergence 07): approves the cost centre each new delivery creates. */}
                        <label className={`toggle-label ${roleAssignmentData.can_approve_cost_centres ? 'active' : ''}`}>
                          <span className="toggle-icon"><FontAwesomeIcon icon={faFileInvoiceDollar} /></span>
                          <div className="toggle-checkbox-container">
                            <input
                              type="checkbox"
                              className="toggle-checkbox"
                              checked={roleAssignmentData.can_approve_cost_centres}
                              onChange={(e) => setRoleAssignmentData({...roleAssignmentData, can_approve_cost_centres: e.target.checked})}
                            />
                            <span className="toggle-checkbox-custom"></span>
                          </div>
                          <div className="toggle-content">
                            <strong>Cost Centre Approver</strong>
                            <small>Gets a notification when a new delivery creates a cost centre, and approves or rejects it on Invoicing &rarr; Master data &rarr; Cost Centres. Also needs Financials access (admin-only designation, company-wide)</small>
                          </div>
                        </label>
                        <label className={`toggle-label ${roleAssignmentData.is_delivery_reviewer ? 'active' : ''}`}>
                          <span className="toggle-icon"><FontAwesomeIcon icon={faTruck} /></span>
                          <div className="toggle-checkbox-container">
                            <input
                              type="checkbox"
                              className="toggle-checkbox"
                              checked={roleAssignmentData.is_delivery_reviewer}
                              onChange={(e) => setRoleAssignmentData({...roleAssignmentData, is_delivery_reviewer: e.target.checked})}
                            />
                            <span className="toggle-checkbox-custom"></span>
                          </div>
                          <div className="toggle-content">
                            <strong>Delivery Reviewer</strong>
                            <small>Designated as Delivery Reviewer (admin-only designation, company-wide)</small>
                          </div>
                        </label>
                        <label className={`toggle-label ${roleAssignmentData.is_bunker_ops_approver ? 'active' : ''}`}>
                          <span className="toggle-icon"><FontAwesomeIcon icon={faAnchor} /></span>
                          <div className="toggle-checkbox-container">
                            <input
                              type="checkbox"
                              className="toggle-checkbox"
                              checked={roleAssignmentData.is_bunker_ops_approver}
                              onChange={(e) => setRoleAssignmentData({...roleAssignmentData, is_bunker_ops_approver: e.target.checked})}
                            />
                            <span className="toggle-checkbox-custom"></span>
                          </div>
                          <div className="toggle-content">
                            <strong>Bunker Operations Approver</strong>
                            <small>Checks the invoices raised on bunker deliveries on the Bunker Invoice Approvals screen and is the only group that sends them to the financial approvers. Everyone else saves them as drafts. Sees every bunker deal, read-only (admin-only designation, company-wide)</small>
                          </div>
                        </label>

                        <label className={`toggle-label ${roleAssignmentData.sees_all_bunker_deals ? 'active' : ''}`}>
                          <span className="toggle-icon"><FontAwesomeIcon icon={faEye} /></span>
                          <div className="toggle-checkbox-container">
                            <input
                              type="checkbox"
                              className="toggle-checkbox"
                              checked={roleAssignmentData.sees_all_bunker_deals}
                              onChange={(e) => setRoleAssignmentData({...roleAssignmentData, sees_all_bunker_deals: e.target.checked})}
                            />
                            <span className="toggle-checkbox-custom"></span>
                          </div>
                          <div className="toggle-content">
                            <strong>Sees all bunker deals</strong>
                            <small>Can open every bunker enquiry, recap and delivery, and their documents, without being an approver, branch manager or on the Ops desk. Also needs Trade Ops Bunkers or Full Access. A customer trader stays limited to their own deals.</small>
                          </div>
                        </label>
                        <label className={`toggle-label ${roleAssignmentData.is_legal_approver ? 'active' : ''}`}>
                          <span className="toggle-icon"><FontAwesomeIcon icon={faGavel} /></span>
                          <div className="toggle-checkbox-container">
                            <input
                              type="checkbox"
                              className="toggle-checkbox"
                              checked={roleAssignmentData.is_legal_approver}
                              onChange={(e) => setRoleAssignmentData({...roleAssignmentData, is_legal_approver: e.target.checked})}
                            />
                            <span className="toggle-checkbox-custom"></span>
                          </div>
                          <div className="toggle-content">
                            <strong>Legal Approver</strong>
                            <small>Receives in-app notifications when bunker contracts are created (no approval gate)</small>
                          </div>
                        </label>
                        <h4 className="toggle-section-title">Module access</h4>
                        <label className={`toggle-label ${roleAssignmentData.has_full_access ? 'active' : ''}`}>
                          <span className="toggle-icon"><FontAwesomeIcon icon={faUserShield} /></span>
                          <div className="toggle-checkbox-container">
                            <input
                              type="checkbox"
                              className="toggle-checkbox"
                              checked={roleAssignmentData.has_full_access}
                              onChange={(e) => setRoleAssignmentData({...roleAssignmentData, has_full_access: e.target.checked})}
                            />
                            <span className="toggle-checkbox-custom"></span>
                          </div>
                          <div className="toggle-content">
                            <strong>Full System Access</strong>
                            <small>Unlocks every module — Trade Ops Cargo &amp; Bunkers, Product, CRM, Financials and Legal.</small>
                          </div>
                        </label>
                        <label className={`toggle-label ${roleAssignmentData.has_cargo_access ? 'active' : ''}`}>
                          <span className="toggle-icon"><FontAwesomeIcon icon={faMountain} /></span>
                          <div className="toggle-checkbox-container">
                            <input
                              type="checkbox"
                              className="toggle-checkbox"
                              checked={roleAssignmentData.has_cargo_access}
                              onChange={(e) => setRoleAssignmentData({...roleAssignmentData, has_cargo_access: e.target.checked})}
                            />
                            <span className="toggle-checkbox-custom"></span>
                          </div>
                          <div className="toggle-content">
                            <strong>Trade Ops Cargo access</strong>
                            <small>Grants ONLY the Trade Ops Cargo (solids) dashboard — without unlocking Bunkers, Stock, Legal, or the other full-access surfaces. Redundant if "Full System Access" is on.</small>
                          </div>
                        </label>
                        <label className={`toggle-label ${roleAssignmentData.has_bunkers_access ? 'active' : ''}`}>
                          <span className="toggle-icon"><FontAwesomeIcon icon={faTint} /></span>
                          <div className="toggle-checkbox-container">
                            <input
                              type="checkbox"
                              className="toggle-checkbox"
                              checked={roleAssignmentData.has_bunkers_access}
                              onChange={(e) => setRoleAssignmentData({...roleAssignmentData, has_bunkers_access: e.target.checked})}
                            />
                            <span className="toggle-checkbox-custom"></span>
                          </div>
                          <div className="toggle-content">
                            <strong>Trade Ops Bunkers access</strong>
                            <small>Grants ONLY the Trade Ops Bunkers (fuels) dashboard and Fuel Enquiries — without unlocking Cargo, Stock, Legal, or the other full-access surfaces. Redundant if "Full System Access" is on.</small>
                          </div>
                        </label>
                        <label className={`toggle-label ${roleAssignmentData.has_financial_access ? 'active' : ''}`}>
                          <span className="toggle-icon"><FontAwesomeIcon icon={faFileInvoiceDollar} /></span>
                          <div className="toggle-checkbox-container">
                            <input
                              type="checkbox"
                              className="toggle-checkbox"
                              checked={roleAssignmentData.has_financial_access}
                              onChange={(e) => setRoleAssignmentData({...roleAssignmentData, has_financial_access: e.target.checked})}
                            />
                            <span className="toggle-checkbox-custom"></span>
                          </div>
                          <div className="toggle-content">
                            <strong>Financials access</strong>
                            <small>Grants the whole Financials tab &mdash; invoices, pro-formas, purchase orders, credit &amp; debit notes, Finance Master Data and Bank Reconciliation &mdash; without unlocking Trade Ops, Product or Legal. Scoped to the entity on the user's office. Redundant if "Full System Access" is on.</small>
                          </div>
                        </label>
                        <label className={`toggle-label ${roleAssignmentData.can_access_all_entities ? 'active' : ''}`}>
                          <span className="toggle-icon"><FontAwesomeIcon icon={faGlobe} /></span>
                          <div className="toggle-checkbox-container">
                            <input
                              type="checkbox"
                              className="toggle-checkbox"
                              checked={roleAssignmentData.can_access_all_entities}
                              onChange={(e) => setRoleAssignmentData({...roleAssignmentData, can_access_all_entities: e.target.checked})}
                            />
                            <span className="toggle-checkbox-custom"></span>
                          </div>
                          <div className="toggle-content">
                            <strong>Financials: all entities</strong>
                            <small>Lifts the entity scope so this user reads every entity's books, not just their own office's. For group finance and consolidation. Leave off for everyone else &mdash; an accountant should see the entity they are linked to.</small>
                          </div>
                        </label>
                        <label className={`toggle-label ${roleAssignmentData.is_customer_trader ? 'active' : ''}`}>
                          <span className="toggle-icon"><FontAwesomeIcon icon={faUserTie} /></span>
                          <div className="toggle-checkbox-container">
                            <input
                              type="checkbox"
                              className="toggle-checkbox"
                              checked={roleAssignmentData.is_customer_trader}
                              onChange={(e) => setRoleAssignmentData({...roleAssignmentData, is_customer_trader: e.target.checked})}
                            />
                            <span className="toggle-checkbox-custom"></span>
                          </div>
                          <div className="toggle-content">
                            <strong>Customer Trader (restricted visibility)</strong>
                            <small>Bunkers visibility restriction: sees ONLY fuel enquiries where they are the Customer Trader, plus those enquiries' Sales Recaps. Purchase recaps and other traders' deals stay hidden. Leave off for Supply Traders — they see everything.</small>
                          </div>
                        </label>
                        <label className={`toggle-label ${roleAssignmentData.is_hedging_trader ? 'active' : ''}`}>
                          <span className="toggle-icon"><FontAwesomeIcon icon={faHandHoldingDollar} /></span>
                          <div className="toggle-checkbox-container">
                            <input
                              type="checkbox"
                              className="toggle-checkbox"
                              checked={roleAssignmentData.is_hedging_trader}
                              onChange={(e) => setRoleAssignmentData({...roleAssignmentData, is_hedging_trader: e.target.checked})}
                            />
                            <span className="toggle-checkbox-custom"></span>
                          </div>
                          <div className="toggle-content">
                            <strong>Hedging Trader</strong>
                            <small>Emails this user whenever a fuel enquiry's sales and purchase pricing disagree — one fixed, one floating — and lets them see those enquiries. Purely additive: it never hides anything they can already see. Leave off for everyone who does not cover price risk.</small>
                            {/* Said here, at the moment of ticking, rather than
                                discovered later by the person who cannot open
                                the link they were emailed. */}
                            {hedgingTraderWarnings(roleAssignmentData).includes('needs-a-module') && (
                              <span className="toggle-warning" role="status">
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                {' '}Also tick <strong>Trade Ops Bunkers</strong> or <strong>Full System Access</strong> above. Without one of them the risk email still arrives, but its button opens a page this user is refused.
                              </span>
                            )}
                            {hedgingTraderWarnings(roleAssignmentData).includes('customer-trader-conflict') && (
                              <span className="toggle-warning" role="status">
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                {' '}Customer Trader is also ticked. A Customer Trader is never shown purchase pricing anywhere in IMT, so this user will keep seeing only their own enquiries and the hedging list will stay empty for them.
                              </span>
                            )}
                          </div>
                        </label>
                        <label className={`toggle-label ${roleAssignmentData.can_manage_bunker_ports ? 'active' : ''}`}>
                          <span className="toggle-icon"><FontAwesomeIcon icon={faAnchor} /></span>
                          <div className="toggle-checkbox-container">
                            <input
                              type="checkbox"
                              className="toggle-checkbox"
                              checked={roleAssignmentData.can_manage_bunker_ports}
                              onChange={(e) => setRoleAssignmentData({...roleAssignmentData, can_manage_bunker_ports: e.target.checked})}
                            />
                            <span className="toggle-checkbox-custom"></span>
                          </div>
                          <div className="toggle-content">
                            <strong>Can manage bunker ports</strong>
                            <small>Adds the Bunker Ports management tab to the Fuels dashboard. Needs a trade-dashboard grant above (Full System Access or Trade Ops Bunkers).</small>
                          </div>
                        </label>
                        <label className={`toggle-label ${roleAssignmentData.can_manage_cargo_ports ? 'active' : ''}`}>
                          <span className="toggle-icon"><FontAwesomeIcon icon={faAnchor} /></span>
                          <div className="toggle-checkbox-container">
                            <input
                              type="checkbox"
                              className="toggle-checkbox"
                              checked={roleAssignmentData.can_manage_cargo_ports}
                              onChange={(e) => setRoleAssignmentData({...roleAssignmentData, can_manage_cargo_ports: e.target.checked})}
                            />
                            <span className="toggle-checkbox-custom"></span>
                          </div>
                          <div className="toggle-content">
                            <strong>Can manage cargo ports</strong>
                            <small>Adds the Cargo Ports management tab to the Cargo dashboard. Needs a trade-dashboard grant above (Full System Access or Trade Ops Cargo).</small>
                          </div>
                        </label>

                        <label className={`toggle-label ${roleAssignmentData.has_offline_access ? 'active' : ''}`}>
                          <span className="toggle-icon"><FontAwesomeIcon icon={faPlaneUp} /></span>
                          <div className="toggle-checkbox-container">
                            <input
                              type="checkbox"
                              className="toggle-checkbox"
                              checked={roleAssignmentData.has_offline_access}
                              onChange={(e) => setRoleAssignmentData({...roleAssignmentData, has_offline_access: e.target.checked})}
                            />
                            <span className="toggle-checkbox-custom"></span>
                          </div>
                          <div className="toggle-content">
                            <strong>Offline access</strong>
                            <small>Lets this user download a read-only snapshot of IMT to their device for offline use. Off for everyone unless ticked here — it is NOT included in Full System Access. They only ever get data they can already see online.</small>
                          </div>
                        </label>

                        <label className={`toggle-label ${roleAssignmentData.has_reporting_access ? 'active' : ''}`}>
                          <span className="toggle-icon"><FontAwesomeIcon icon={faChartColumn} /></span>
                          <div className="toggle-checkbox-container">
                            <input
                              type="checkbox"
                              className="toggle-checkbox"
                              checked={roleAssignmentData.has_reporting_access}
                              onChange={(e) => setRoleAssignmentData({...roleAssignmentData, has_reporting_access: e.target.checked})}
                            />
                            <span className="toggle-checkbox-custom"></span>
                          </div>
                          <div className="toggle-content">
                            <strong>Reporting Central</strong>
                            <small>Can open Reporting Central and run the standard reports. Reports only ever show what this person can already see elsewhere.</small>
                          </div>
                        </label>

                        <label className={`toggle-label ${roleAssignmentData.can_build_reports ? 'active' : ''}`}>
                          <span className="toggle-icon"><FontAwesomeIcon icon={faUserPen} /></span>
                          <div className="toggle-checkbox-container">
                            <input
                              type="checkbox"
                              className="toggle-checkbox"
                              checked={roleAssignmentData.can_build_reports}
                              onChange={(e) => setRoleAssignmentData({...roleAssignmentData, can_build_reports: e.target.checked})}
                            />
                            <span className="toggle-checkbox-custom"></span>
                          </div>
                          <div className="toggle-content">
                            <strong>Report builder</strong>
                            <small>Can build, save and share their own reports. Needs Reporting Central as well.</small>
                          </div>
                        </label>

                        <label className={`toggle-label ${roleAssignmentData.can_manage_crm_partners ? 'active' : ''}`}>
                          <span className="toggle-icon"><FontAwesomeIcon icon={faAddressBook} /></span>
                          <div className="toggle-checkbox-container">
                            <input
                              type="checkbox"
                              className="toggle-checkbox"
                              checked={roleAssignmentData.can_manage_crm_partners}
                              onChange={(e) => setRoleAssignmentData({...roleAssignmentData, can_manage_crm_partners: e.target.checked})}
                            />
                            <span className="toggle-checkbox-custom"></span>
                          </div>
                          <div className="toggle-content">
                            <strong>Manage CRM partners</strong>
                            <small>Lets this user CREATE and EDIT partner records. Looking partners up is unaffected — anyone with CRM, Financials or Full System Access can still search and read them, which invoicing and trade ops depend on. Untick to make someone read-only in CRM.</small>
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="security-warning">
                    <div className="warning-icon"></div>
                    <div className="warning-content">
                      <h4>Security Notice</h4>
                      <p>Role changes take effect immediately and will be logged in the audit trail. 
                      {selectedRole === 'administrator' 
                        ? ' Administrator access grants full system privileges including the ability to manage other users.'
                        : ' Removing administrator access will revoke all administrative privileges.'
                      }</p>
                    </div>
                  </div>

                  <div className="modal-actions">
                    <button 
                      type="submit" 
                      className="btn btn-primary"
                      disabled={!selectedRole}
                    >
                      Assign Role
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-secondary"
                      onClick={() => setShowRoleAssignment(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Unassignment Warning Modal */}
          {showUnassignWarning && unassignWarningData && (
            <div className="admin-modal-overlay">
              <div className="modal unassign-warning-modal">
                <div className="modal-header warning-header">
                  <h2>Confirm Role Unassignment</h2>
                  <button className="close-btn" onClick={handleCancelUnassignment}>×</button>
                </div>
                <div className="modal-body">
                  <div className="warning-content-box">
                    <div className="warning-icon-large">
                      <i className="fas fa-exclamation-triangle"></i>
                    </div>
                    <h3>This action will affect assigned users</h3>
                    <p className="warning-message">
                      You are about to remove designation(s) from <strong>{unassignWarningData.employee?.display_name}</strong>:
                    </p>
                    
                    <div className="affected-users-info">
                      {unassignWarningData.isUncheckingSupervisor && unassignWarningData.supervisorCount > 0 && (
                        <div className="affected-role">
                          <i className="fas fa-user-tie"></i>
                          <div>
                            <strong>Supervisor Role</strong>
                            <p>{unassignWarningData.supervisorCount} user(s) currently report to this supervisor</p>
                          </div>
                        </div>
                      )}
                      
                      {unassignWarningData.isUncheckingHOD && unassignWarningData.hodCount > 0 && (
                        <div className="affected-role">
                          <i className="fas fa-user-shield"></i>
                          <div>
                            <strong>Head of Department Role</strong>
                            <p>{unassignWarningData.hodCount} user(s) currently report to this HOD</p>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="warning-explanation">
                      <p><strong>What will happen:</strong></p>
                      <ul>
                        <li>All reportee assignments will be removed</li>
                        <li>Affected users will no longer have this person as their supervisor/HOD</li>
                        <li>Leave approval workflows may be affected</li>
                        <li>This action will be logged in the audit trail</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="modal-actions">
                    <button 
                      type="button" 
                      className="btn btn-danger"
                      onClick={handleConfirmUnassignment}
                    >
                      Confirm Unassignment
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-secondary"
                      onClick={handleCancelUnassignment}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Assign Users to Supervisor/HOD/Leave Supervisor Modal */}
          {showAssignUsers && (assignSupervisor || assignHOD || assignLeaveSupervisor) && (
            <div className="admin-modal-overlay">
              <div className="modal assign-users-modal">
                <div className="modal-header">
                  <h2>Assign Users to {assignType === 'supervisor' ? assignSupervisor?.display_name : assignType === 'hod' ? assignHOD?.display_name : assignLeaveSupervisor?.display_name}</h2>
                  <button 
                    className="close-btn" 
                    onClick={() => setShowAssignUsers(false)}
                  >
                    ×
                  </button>
                </div>
                
                <div className="modal-body">
                  <div className="assign-users-instructions">
                    <p>Select users to assign to this supervisor. Users already assigned to another supervisor will show their current supervisor.</p>
                  </div>

                  <div className="assign-users-search">
                    <input
                      type="text"
                      placeholder="Search users by name or email..."
                      value={assignUsersSearch}
                      onChange={(e) => setAssignUsersSearch(e.target.value)}
                      className="form-input"
                    />
                  </div>

                  <div className="bulk-actions">
                    <button 
                      type="button" 
                      className="btn btn-small"
                      onClick={selectAllUsers}
                    >
                      Select All
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-small"
                      onClick={deselectAllUsers}
                    >
                      Deselect All
                    </button>
                    <div className="assign-department-wrapper">
                      <select
                        className="assign-department-select"
                        value={assignFilterDept}
                        onChange={(e) => setAssignFilterDept(e.target.value)}
                      >
                        <option value="">All Departments</option>
                        {departments.map(dept => (
                          <option key={dept.id} value={dept.id}>{dept.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="assign-department-wrapper">
                      <select
                        className="assign-department-select"
                        value={assignFilterOffice}
                        onChange={(e) => setAssignFilterOffice(e.target.value)}
                      >
                        <option value="">All Offices</option>
                        {offices.map(office => (
                          <option key={office.id} value={office.id}>{office.name}</option>
                        ))}
                      </select>
                    </div>
                    <span className="selection-count">
                      {selectedUserIds.length} user(s) selected
                    </span>
                  </div>

                  <div className="users-grid-container">
                    <div className="users-grid">
                      {getFilteredAssignUsers()
                        .map(user => (
                        <div 
                          key={user.id} 
                          className={`user-grid-item ${selectedUserIds.includes(user.id) ? 'selected' : ''}`}
                          onClick={() => toggleUserSelection(user.id)}
                        >
                          <div className="user-grid-checkbox-container">
                            <input
                              type="checkbox"
                              className="user-grid-checkbox"
                              checked={selectedUserIds.includes(user.id)}
                              onChange={() => {}}
                            />
                            <span className="user-grid-checkbox-custom"></span>
                          </div>
                          <div className="user-grid-avatar">
                            {user.avatar_photo ? (
                              <img src={user.avatar_photo} alt={user.display_name} />
                            ) : (
                              <div className="user-grid-initials">
                                {user.first_name?.[0]}{user.last_name?.[0]}
                              </div>
                            )}
                          </div>
                          <div className="user-grid-info">
                            <div className="user-grid-name">{user.display_name}</div>
                            <div className="user-grid-email">{user.email}</div>
                            {assignType === 'supervisor' && user.supervisor && assignSupervisor && user.supervisor.id !== assignSupervisor.id && (
                              <div className="user-grid-supervisor">
                                Current Supervisor: {user.supervisor.name}
                              </div>
                            )}
                            {assignType === 'hod' && user.head_of_department && assignHOD && user.head_of_department.id !== assignHOD.id && (
                              <div className="user-grid-supervisor">
                                Current HOD: {user.head_of_department.name}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="modal-actions">
                  <button 
                    type="button" 
                    className="btn btn-primary"
                    onClick={() => handleAssignUsersSubmit(false)}
                    disabled={selectedUserIds.length === 0}
                  >
                    Assign Selected Users
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-danger-outline"
                    onClick={() => handleAssignUsersSubmit(true)}
                    disabled={selectedUserIds.length === 0}
                  >
                    Unassign Selected Users
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={() => setShowAssignUsers(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Deactivate Confirmation Modal */}
          {showDeactivateConfirm && deactivateEmployee && (
            <div className="admin-modal-overlay">
              <div className="modal deactivate-confirm-modal">
                <div className="modal-body">
                  <div className="confirmation-content">
                    <div className="deactivate-warning-icon">
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                    </div>
                    <h3 className="deactivate-modal-title">Deactivate Employee</h3>
                    <p className="deactivate-modal-message">
                      Are you sure you want to deactivate <strong>{deactivateEmployee.name}</strong>?
                    </p>
                    <div className="warning-details">
                      <p>This action will:</p>
                      <ul>
                        <li>Disable the employee's account access</li>
                        <li>Remove them from active employee lists</li>
                        <li>Log this action in the audit trail</li>
                        <li>Preserve all employee data for future reference</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className="modal-actions">
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={cancelDeactivateEmployee}
                  >
                    Cancel
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-danger"
                    onClick={confirmDeactivateEmployee}
                  >
                    Deactivate Employee
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* System Data Management Content - Only show in System Data mode */}
          {adminMode === 'system-data' && (
            <div className="system-data-management-section">
              {systemDataTab === 'power_of_attorney_roles' ? (
                <PowerOfAttorneyRolesPanel />
              ) : (
                <SystemDataManagement activeTab={systemDataTab} />
              )}
            </div>
          )}

          {/* Power of Attorney (Go-live 1 November): who empowers whom, for which roles, until when */}
          {adminMode === 'power-of-attorney' && (
            <div className="system-data-management-section">
              <PowerOfAttorneyManagement employees={[...superusers, ...regularEmployees]} />
            </div>
          )}
        </div>
        
        {/* Leave Request Notification Modal */}
        <HMSNotificationModal
          isOpen={isLeaveModalOpen}
          notification={selectedLeaveForModal}
          onClose={handleCloseLeaveModal}
          onActionComplete={handleLeaveModalActionComplete}
        />
        
        {/* Hamster Chat Integration */}
        <HamsterFloatingButton />
        
        {/* Back to Top Button */}
        <BackToTopButton />
        
        {/* Footer */}
        <Footer />
      </div>
    </Layout>
  );
}

export default withAuth(HMSAdminDashboard);
